import { useState, useEffect, useCallback, useRef, useMemo, useLayoutEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import {
  ArrowLeft, Save, Loader2, Briefcase, FileText, AlertTriangle,
  Paperclip, ChevronDown, ChevronUp, Plus, Trash2, Download,
  Clock, User, Edit3, X, Check, Calendar, ChevronLeft, ChevronRight,
  Eye, EyeOff, History, UserX, Shield,
  Car, MapPin, RotateCcw, Pencil, KeyRound,
} from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { DashboardHeader } from "@/components/DashboardHeader";
import ModalPortal from "@/components/ModalPortal";
import AccessDenied from "@/components/AccessDenied";
import RettighedsEditor from "@/components/RettighedsEditor";


const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_OPTIONS = ["Aktiv", "Fratrådt", "Opsagt", "Bortvist", "Barsel/Orlov"];
const STATUS_STYLES = {
  "Aktiv":        { ring: "#22c55e", bg: "bg-green-500/10",   text: "text-green-500" },
  "Fratrådt":     { ring: "#ef4444", bg: "bg-red-500/10",     text: "text-red-500" },
  "Opsagt":       { ring: "#f97316", bg: "bg-orange-500/10",  text: "text-orange-500" },
  "Bortvist":     { ring: "#991b1b", bg: "bg-red-900/10",     text: "text-red-800 dark:text-red-300" },
  "Barsel/Orlov": { ring: "#a855f7", bg: "bg-purple-500/10",  text: "text-purple-500" },
};
const ANSAETTELSESTYPER = ["Timer", "Funktionær", "Vikar"];
// Officiel ansættelsesform — passer med backend-valideringen (manedsloen / timeloen / vikar).
// Vikar = ingen faste timer, ingen fast løn — bookes ad-hoc fra vagtplan.
const ANSAETTELSESFORM_OPTIONS = [
  { value: "manedsloen", label: "Månedsløn" },
  { value: "timeloen",   label: "Timeløn" },
  { value: "vikar",      label: "Vikar" },
];
const ANSAETTELSESTYPE_LABEL = ANSAETTELSESFORM_OPTIONS.reduce(
  (acc, o) => { acc[o.value] = o.label; return acc; },
  {}
);
const DISPLAY_NAMES = { "Koebenhavn": "København" };
const displayCity = (c) => DISPLAY_NAMES[c] || c || "—";

// Udled fødselsdato fra dansk CPR-nummer (DDMMYY-XXXX). Returnerer
// "DD.MM.YYYY" eller "—" hvis input er ugyldigt eller mangler.
function fmtFoedselsdagFraCpr(cpr) {
  if (!cpr) return "—";
  const ren = String(cpr).replace(/[^\d]/g, "");
  if (ren.length < 7) return "—";
  const dd = parseInt(ren.slice(0, 2), 10);
  const mm = parseInt(ren.slice(2, 4), 10);
  const yy = parseInt(ren.slice(4, 6), 10);
  const c7 = parseInt(ren[6], 10);
  if (!Number.isFinite(dd) || !Number.isFinite(mm) || !Number.isFinite(yy) || !Number.isFinite(c7)) return "—";
  if (dd < 1 || dd > 31 || mm < 1 || mm > 12) return "—";
  let aar;
  if (c7 <= 3) aar = 1900 + yy;
  else if (c7 === 4 || c7 === 9) aar = (yy <= 36) ? 2000 + yy : 1900 + yy;
  else aar = (yy <= 57) ? 2000 + yy : 1800 + yy;
  return `${String(dd).padStart(2, "0")}.${String(mm).padStart(2, "0")}.${aar}`;
}

// Formatér en ISO-dato (YYYY-MM-DD) som "DD.MM.YYYY" — bruges når backend
// allerede har sendt en udledt fødselsdag (admin/self ser ikke CPR direkte).
function fmtFoedselsdagFraIso(iso) {
  if (!iso) return "—";
  const s = String(iso).trim();
  const m = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!m) return "—";
  return `${m[3]}.${m[2]}.${m[1]}`;
}

function CprFelt({ cpr }) {
  const [vis, setVis] = useState(false);
  if (!cpr) return <p className="text-sm font-medium">—</p>;
  const clean = cpr.replace(/\D/g, "");
  const masked = clean.length >= 6 ? clean.slice(0, 6) + "-xxxx" : cpr;
  const full = clean.length >= 10 ? clean.slice(0, 6) + "-" + clean.slice(6) : cpr;
  return (
    <div className="flex items-center gap-2">
      <p className="text-sm font-medium font-mono">{vis ? full : masked}</p>
      <button onClick={() => setVis(v => !v)} className="text-muted-foreground hover:text-foreground transition-colors">
        {vis ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
      </button>
    </div>
  );
}

function isArbeejdsmail(email) {
  const em = (email || "").toLowerCase();
  return em.endsWith("@tnbc.dk") || em.endsWith("@beautyandstyle.dk");
}

function fmtDate(d) {
  if (!d) return "—";
  try {
    const [y, m, day] = d.split("-");
    const months = ["jan", "feb", "mar", "apr", "maj", "jun", "jul", "aug", "sep", "okt", "nov", "dec"];
    return `${parseInt(day, 10)}. ${months[parseInt(m, 10) - 1]} ${y}`;
  } catch { return d; }
}

function fmtTs(ts) {
  if (!ts) return "—";
  try {
    const d = new Date(ts);
    return d.toLocaleDateString("da-DK", { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
  } catch { return ts; }
}

function fmtBytes(n) {
  if (!n) return "";
  if (n < 1024) return n + " B";
  if (n < 1024 * 1024) return (n / 1024).toFixed(0) + " KB";
  return (n / (1024 * 1024)).toFixed(1) + " MB";
}

function fileIcon(ct = "") {
  if (ct.includes("pdf")) return "📄";
  if (ct.includes("word") || ct.includes("doc")) return "📝";
  if (ct.includes("sheet") || ct.includes("excel") || ct.includes("csv")) return "📊";
  if (ct.includes("image")) return "🖼";
  return "📎";
}

// ─── Inline-redigerbar felt ────────────────────────────────────────────────
function EditableField({ label, value, onChange, type = "text", multiline = false, readOnly = false }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value || "");
  const inputRef = useRef(null);

  useEffect(() => { setDraft(value || ""); }, [value]);
  useEffect(() => { if (editing && inputRef.current) inputRef.current.focus(); }, [editing]);

  const commit = () => { onChange(draft); setEditing(false); };
  const cancel = () => { setDraft(value || ""); setEditing(false); };

  if (readOnly) {
    return (
      <div>
        <p className="text-[13px] text-muted-foreground mb-0.5">{label}</p>
        <p className="text-sm font-medium">{value || "—"}</p>
      </div>
    );
  }

  return (
    <div className="group">
      <p className="text-[13px] text-muted-foreground mb-0.5">{label}</p>
      {editing ? (
        <div className="flex items-start gap-1">
          {multiline ? (
            <textarea
              ref={inputRef}
              value={draft}
              onChange={e => setDraft(e.target.value)}
              rows={3}
              className="flex-1 bg-secondary border border-[#FF2A6D] rounded-lg px-3 py-2 text-sm text-foreground focus:outline-none resize-y"
            />
          ) : (
            <input
              ref={inputRef}
              type={type}
              value={draft}
              onChange={e => setDraft(e.target.value)}
              className="flex-1 bg-secondary border border-[#FF2A6D] rounded-lg px-3 h-9 text-sm text-foreground focus:outline-none"
            />
          )}
          <button onClick={commit} className="p-1.5 text-green-500 hover:bg-green-500/10 rounded-lg"><Check className="w-4 h-4" /></button>
          <button onClick={cancel} className="p-1.5 text-muted-foreground hover:bg-secondary rounded-lg"><X className="w-4 h-4" /></button>
        </div>
      ) : (
        <div
          onClick={() => setEditing(true)}
          className="flex items-center gap-1.5 cursor-pointer min-h-[28px] rounded-lg px-1 -mx-1 hover:bg-secondary/50 transition-colors"
        >
          <span className="text-sm font-medium flex-1">{value || <span className="text-muted-foreground italic">Klik for at redigere</span>}</span>
          <Edit3 className="w-3 h-3 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity shrink-0" />
        </div>
      )}
    </div>
  );
}

// ─── CV — dato-parsing + sortering (#43) ─────────────────────────────────────
// Robust parser: oversætter "Jan 2020" / "Dec 2022" / "nu" → sorterbar nøgle.
// Måned-navne på dansk + engelsk → tal (1-12); ukendt → 0.
const MAANED_MAP = {
  jan: 1, januar: 1, january: 1,
  feb: 2, februar: 2, february: 2,
  mar: 3, marts: 3, march: 3,
  apr: 4, april: 4,
  maj: 5, may: 5,
  jun: 6, juni: 6, june: 6,
  jul: 7, juli: 7, july: 7,
  aug: 8, august: 8,
  sep: 9, sept: 9, september: 9,
  okt: 10, oktober: 10, oct: 10, october: 10,
  nov: 11, november: 11,
  dec: 12, december: 12,
};

// Returnerer { now, year, month } for en "fra/til"-streng. now=true sorteres øverst.
function parseCvDato(s) {
  const raw = (s || "").trim().toLowerCase();
  if (raw === "nu" || raw === "now" || raw === "nuværende" || raw === "current") {
    return { now: true, year: 0, month: 0 };
  }
  let year = 0, month = 0;
  const yearMatch = raw.match(/\b(19|20)\d{2}\b/g);
  if (yearMatch) year = parseInt(yearMatch[yearMatch.length - 1], 10);
  for (const word of raw.split(/[^a-zæøå]+/)) {
    if (word && MAANED_MAP[word]) { month = MAANED_MAP[word]; break; }
  }
  return { now: false, year, month };
}

// Sorterer en liste (erhvervserfaring/uddannelse) faldende efter "Til"-dato.
// "nu"/tom-men-har-fra øverst. Ikke-destruktivt (returnerer ny array m. orig-index).
function sorterEfterTil(liste) {
  return liste
    .map((post, _origIndex) => ({ post, _origIndex }))
    .sort((a, b) => {
      const ta = parseCvDato(a.post.til), tb = parseCvDato(b.post.til);
      // "nu" eller (tom til men har fra) → behandles som pågående → øverst.
      const aNow = ta.now || (!(a.post.til || "").trim() && !!(a.post.fra || "").trim());
      const bNow = tb.now || (!(b.post.til || "").trim() && !!(b.post.fra || "").trim());
      if (aNow !== bNow) return aNow ? -1 : 1;
      if (tb.year !== ta.year) return tb.year - ta.year;
      if (tb.month !== ta.month) return tb.month - ta.month;
      return a._origIndex - b._origIndex;
    });
}

// Sorterer kurser faldende efter "aar" (sidste 4-cifrede årstal i strengen).
function sorterEfterAar(liste) {
  return liste
    .map((post, _origIndex) => ({ post, _origIndex }))
    .sort((a, b) => {
      const ya = parseCvDato(a.post.aar).year, yb = parseCvDato(b.post.aar).year;
      if (yb !== ya) return yb - ya;
      return a._origIndex - b._origIndex;
    });
}

// Fælles input-felt-klasse for CV-formularer.
const CV_INPUT = "w-full bg-secondary border border-border rounded-lg px-3 h-8 text-sm text-foreground focus:outline-none focus:ring-1 focus:ring-[#FF2A6D]";
const CV_TEXTAREA = "w-full bg-secondary border border-border rounded-lg px-3 py-2 text-sm text-foreground focus:outline-none focus:ring-1 focus:ring-[#FF2A6D] resize-y";

// Fælles Gem/Annullér-knaprække for CV-input-kort.
function CvEditActions({ onSave, onCancel, saving }) {
  return (
    <div className="flex items-center gap-2 mt-3">
      <button type="button" onClick={onSave} disabled={saving}
        className="flex items-center gap-1.5 px-4 py-1.5 rounded-lg bg-[#FF2A6D] text-white text-sm font-semibold hover:bg-[#e02560] transition-colors disabled:opacity-60">
        {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />} Gem
      </button>
      <button type="button" onClick={onCancel} disabled={saving}
        className="px-4 py-1.5 rounded-lg border border-border text-sm text-muted-foreground hover:bg-secondary transition-colors disabled:opacity-60">
        Annullér
      </button>
    </div>
  );
}

// Fælles Rediger/Slet-knaprække for read-only CV-kort.
// #51-konsistens: Slet kræver bekræftelse (samme adfærd som Karakter-fanen).
function CvReadActions({ onEdit, onDelete }) {
  return (
    <div className="flex items-center gap-3 mt-2">
      <button type="button" onClick={onEdit}
        className="flex items-center gap-1 text-[13px] text-muted-foreground hover:text-[#FF2A6D] transition-colors">
        <Edit3 className="w-3.5 h-3.5" /> Rediger
      </button>
      <button type="button"
        onClick={() => { if (window.confirm("Slet denne post?")) onDelete(); }}
        className="flex items-center gap-1 text-[13px] text-muted-foreground hover:text-red-500 transition-colors">
        <Trash2 className="w-3.5 h-3.5" /> Slet
      </button>
    </div>
  );
}

// ─── CV-post (erhvervserfaring) — gem-pr-post + read/edit (#43) ──────────────
function CvPost({ post, mode, onSave, onCancel, onEdit, onDelete, saving }) {
  const [draft, setDraft] = useState(post);
  useEffect(() => { if (mode === "edit") setDraft(post); }, [mode, post]);

  if (mode === "read") {
    const periode = [post.fra, post.til].filter(s => (s || "").trim()).join(" – ");
    return (
      <div className="rounded-xl border border-border p-4 bg-secondary/20">
        <p className="text-sm font-semibold text-foreground">
          {[post.stilling, post.firma].filter(s => (s || "").trim()).join(" · ") || "(uden titel)"}
        </p>
        {periode && <p className="text-[13px] text-muted-foreground mt-0.5">{periode}</p>}
        {(post.beskrivelse || "").trim() && (
          <p className="text-sm text-foreground/90 mt-2 whitespace-pre-wrap">
            <span className="text-muted-foreground">Ansvarsområder: </span>{post.beskrivelse}
          </p>
        )}
        <CvReadActions onEdit={onEdit} onDelete={onDelete} />
      </div>
    );
  }

  return (
    <div className="rounded-xl border border-[#FF2A6D]/40 p-4 bg-secondary/20">
      <div className="grid grid-cols-2 gap-3 mb-3">
        <div>
          <label className="text-[13px] text-muted-foreground mb-1 block">Fra (mdr/år)</label>
          <input type="text" placeholder="Jan 2020" value={draft.fra || ""}
            onChange={e => setDraft({ ...draft, fra: e.target.value })} className={CV_INPUT} />
        </div>
        <div>
          <label className="text-[13px] text-muted-foreground mb-1 block">Til (mdr/år)</label>
          <input type="text" placeholder="Dec 2022 / nu" value={draft.til || ""}
            onChange={e => setDraft({ ...draft, til: e.target.value })} className={CV_INPUT} />
        </div>
        <div>
          <label className="text-[13px] text-muted-foreground mb-1 block">Stilling</label>
          <input type="text" placeholder="Frisør" value={draft.stilling || ""}
            onChange={e => setDraft({ ...draft, stilling: e.target.value })} className={CV_INPUT} />
        </div>
        <div>
          <label className="text-[13px] text-muted-foreground mb-1 block">Firma / skole</label>
          <input type="text" placeholder="Salon XYZ" value={draft.firma || ""}
            onChange={e => setDraft({ ...draft, firma: e.target.value })} className={CV_INPUT} />
        </div>
      </div>
      <div>
        <label className="text-[13px] text-muted-foreground mb-1 block">Kort beskrivelse</label>
        <textarea placeholder="Beskriv ansvarsområder..." value={draft.beskrivelse || ""}
          onChange={e => setDraft({ ...draft, beskrivelse: e.target.value })} rows={2} className={CV_TEXTAREA} />
      </div>
      <CvEditActions onSave={() => onSave(draft)} onCancel={onCancel} saving={saving} />
    </div>
  );
}

// ─── Uddannelse-post — gem-pr-post + read/edit (#43) ─────────────────────────
function UddannelsePost({ post, mode, onSave, onCancel, onEdit, onDelete, saving }) {
  const [draft, setDraft] = useState(post);
  useEffect(() => { if (mode === "edit") setDraft(post); }, [mode, post]);

  if (mode === "read") {
    const periode = [post.fra, post.til].filter(s => (s || "").trim()).join(" – ");
    return (
      <div className="rounded-xl border border-border p-4 bg-secondary/20">
        <p className="text-sm font-semibold text-foreground">
          {[post.titel, post.skole].filter(s => (s || "").trim()).join(" · ") || "(uden titel)"}
        </p>
        {periode && <p className="text-[13px] text-muted-foreground mt-0.5">{periode}</p>}
        {(post.beskrivelse || "").trim() && (
          <p className="text-sm text-foreground/90 mt-2 whitespace-pre-wrap">{post.beskrivelse}</p>
        )}
        <CvReadActions onEdit={onEdit} onDelete={onDelete} />
      </div>
    );
  }

  return (
    <div className="rounded-xl border border-[#FF2A6D]/40 p-4 bg-secondary/20">
      <div className="grid grid-cols-2 gap-3 mb-3">
        <div>
          <label className="text-[13px] text-muted-foreground mb-1 block">Fra (mdr/år)</label>
          <input type="text" placeholder="Aug 2018" value={draft.fra || ""}
            onChange={e => setDraft({ ...draft, fra: e.target.value })} className={CV_INPUT} />
        </div>
        <div>
          <label className="text-[13px] text-muted-foreground mb-1 block">Til (mdr/år)</label>
          <input type="text" placeholder="Jun 2021 / nu" value={draft.til || ""}
            onChange={e => setDraft({ ...draft, til: e.target.value })} className={CV_INPUT} />
        </div>
        <div>
          <label className="text-[13px] text-muted-foreground mb-1 block">Uddannelse / titel</label>
          <input type="text" placeholder="Kosmetolog" value={draft.titel || ""}
            onChange={e => setDraft({ ...draft, titel: e.target.value })} className={CV_INPUT} />
        </div>
        <div>
          <label className="text-[13px] text-muted-foreground mb-1 block">Skole / institution</label>
          <input type="text" placeholder="Beauty Academy" value={draft.skole || ""}
            onChange={e => setDraft({ ...draft, skole: e.target.value })} className={CV_INPUT} />
        </div>
      </div>
      <div>
        <label className="text-[13px] text-muted-foreground mb-1 block">Beskrivelse</label>
        <textarea placeholder="Hovedfag, speciale, særlige projekter…" value={draft.beskrivelse || ""}
          onChange={e => setDraft({ ...draft, beskrivelse: e.target.value })} rows={2} className={CV_TEXTAREA} />
      </div>
      <CvEditActions onSave={() => onSave(draft)} onCancel={onCancel} saving={saving} />
    </div>
  );
}

// ─── Kursus-post — gem-pr-post + read/edit (#43) ─────────────────────────────
function KursusPost({ post, mode, onSave, onCancel, onEdit, onDelete, saving }) {
  const [draft, setDraft] = useState(post);
  useEffect(() => { if (mode === "edit") setDraft(post); }, [mode, post]);

  if (mode === "read") {
    return (
      <div className="rounded-xl border border-border p-4 bg-secondary/20">
        <p className="text-sm font-semibold text-foreground">{(post.navn || "").trim() || "(uden navn)"}</p>
        <p className="text-[13px] text-muted-foreground mt-0.5">
          {[post.skole, post.aar].filter(s => (s || "").trim()).join(" · ")}
        </p>
        {(post.beskrivelse || "").trim() && (
          <p className="text-sm text-foreground/90 mt-2 whitespace-pre-wrap">{post.beskrivelse}</p>
        )}
        <CvReadActions onEdit={onEdit} onDelete={onDelete} />
      </div>
    );
  }

  return (
    <div className="rounded-xl border border-[#FF2A6D]/40 p-4 bg-secondary/20">
      <div className="grid grid-cols-2 gap-3 mb-3">
        <div>
          <label className="text-[13px] text-muted-foreground mb-1 block">År (el. fra/til)</label>
          <input type="text" placeholder="2023" value={draft.aar || ""}
            onChange={e => setDraft({ ...draft, aar: e.target.value })} className={CV_INPUT} />
        </div>
        <div>
          <label className="text-[13px] text-muted-foreground mb-1 block">Udbyder</label>
          <input type="text" placeholder="Fx L'Oréal Academy" value={draft.skole || ""}
            onChange={e => setDraft({ ...draft, skole: e.target.value })} className={CV_INPUT} />
        </div>
        <div className="col-span-2">
          <label className="text-[13px] text-muted-foreground mb-1 block">Kursusnavn</label>
          <input type="text" placeholder="Avanceret farveteknik" value={draft.navn || ""}
            onChange={e => setDraft({ ...draft, navn: e.target.value })} className={CV_INPUT} />
        </div>
      </div>
      <div>
        <label className="text-[13px] text-muted-foreground mb-1 block">Beskrivelse</label>
        <textarea placeholder="Fokusområde, certificering, varighed…" value={draft.beskrivelse || ""}
          onChange={e => setDraft({ ...draft, beskrivelse: e.target.value })} rows={2} className={CV_TEXTAREA} />
      </div>
      <CvEditActions onSave={() => onSave(draft)} onCancel={onCancel} saving={saving} />
    </div>
  );
}

// ─── "Lidt om mig"-editor (#43) — fritekst med Gem/Annullér ──────────────────
function OmkringMigEditor({ initial, onSave, onCancel, saving }) {
  const [tekst, setTekst] = useState(initial || "");
  return (
    <div className="rounded-xl border border-[#FF2A6D]/40 p-4 bg-secondary/20">
      <textarea
        value={tekst}
        onChange={e => setTekst(e.target.value)}
        rows={4}
        placeholder="Hvis du har lyst, kan du skrive lidt om dig selv: faglige interesser, hvad du brænder for, særlige styrker eller noget personligt."
        className={CV_TEXTAREA} />
      <CvEditActions onSave={() => onSave(tekst)} onCancel={onCancel} saving={saving} />
    </div>
  );
}

// ─── Advarsel-post ───────────────────────────────────────────────────────────
function AdvarselsPost({ post, onChange, onDelete }) {
  return (
    <div className="rounded-xl border border-border p-4 bg-red-500/5 relative group">
      <button onClick={onDelete}
        className="absolute top-3 right-3 p-1 rounded-full text-muted-foreground hover:text-red-500 hover:bg-red-500/10 opacity-0 group-hover:opacity-100 transition-all">
        <Trash2 className="w-3.5 h-3.5" />
      </button>
      <div className="grid grid-cols-2 gap-3 mb-3">
        <div>
          <label className="text-[13px] text-muted-foreground mb-1 block">Dato</label>
          <input type="date" value={post.dato || ""} onChange={e => onChange({ ...post, dato: e.target.value })}
            className="w-full bg-secondary border border-border rounded-lg px-3 h-8 text-sm text-foreground focus:outline-none focus:ring-1 focus:ring-[#FF2A6D]" />
        </div>
        <div>
          <label className="text-[13px] text-muted-foreground mb-1 block">Type</label>
          <select value={post.type || "Mundtlig advarsel"} onChange={e => onChange({ ...post, type: e.target.value })}
            className="w-full bg-secondary border border-border rounded-lg px-3 h-8 text-sm text-foreground focus:outline-none">
            <option>Mundtlig advarsel</option>
            <option>Skriftlig advarsel</option>
            <option>Note</option>
          </select>
        </div>
      </div>
      <textarea placeholder="Beskriv hændelsen..." value={post.tekst || ""}
        onChange={e => onChange({ ...post, tekst: e.target.value })} rows={3}
        className="w-full bg-secondary border border-border rounded-lg px-3 py-2 text-sm text-foreground focus:outline-none focus:ring-1 focus:ring-[#FF2A6D] resize-y" />
    </div>
  );
}

// ─── CV undersektion ────────────────────────────────────────────────────────
function CvUndersektion({ titel, badge, color, children }) {
  const [open, setOpen] = useState(true);
  return (
    <div>
      <button type="button" onClick={() => setOpen(o => !o)} className="flex items-center gap-2 w-full text-left mb-2">
        <span className="text-sm font-semibold" style={{ color }}>{titel}</span>
        {badge > 0 && <span className="text-[13px] font-bold px-1.5 py-0.5 rounded-full bg-secondary text-muted-foreground">{badge}</span>}
        <ChevronDown className={`w-3.5 h-3.5 ml-auto text-muted-foreground transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && children}
    </div>
  );
}

// ─── Vagtplan konstanter ────────────────────────────────────────────────────
const VAGTTYPER = ["", "Behandler timer", "Syg", "Ferie", "Fri", "Lægeeksamination", "Lægeundervisning", "Barnets første sygedag"];
const VAGTTYPE_COLORS = {
  "Behandler timer": "text-blue-500 bg-blue-500/10",
  "Syg": "text-red-500 bg-red-500/10",
  "Ferie": "text-emerald-500 bg-emerald-500/10",
  "Fri": "text-purple-500 bg-purple-500/10",
  "Lægeeksamination": "text-amber-500 bg-amber-500/10",
  "Lægeundervisning": "text-amber-500 bg-amber-500/10",
  "Barnets første sygedag": "text-orange-500 bg-orange-500/10",
};
const ALLE_BYER = ["København", "Aarhus", "Odense", "Aalborg", "Kolding", "Administration", "TNBC"];
const DK_HELLIGDAGE = new Set([
  "2025-01-01","2025-04-17","2025-04-18","2025-04-21","2025-05-16","2025-05-29","2025-06-05","2025-06-08","2025-06-09","2025-12-25","2025-12-26",
  "2026-01-01","2026-04-02","2026-04-03","2026-04-06","2026-05-01","2026-05-14","2026-05-24","2026-05-25","2026-06-05","2026-12-25","2026-12-26",
  "2027-01-01","2027-03-25","2027-03-26","2027-03-29","2027-04-23","2027-06-03","2027-06-05","2027-06-13","2027-06-14","2027-12-25","2027-12-26",
]);
const UGEDAGE_SHORT = ["Man", "Tir", "Ons", "Tor", "Fre", "Lør", "Søn"];

function calcTimer(fra, til) {
  if (!fra || !til) return 0;
  const [fh, fm] = fra.split(":").map(Number);
  const [th, tm] = til.split(":").map(Number);
  const diff = (th * 60 + tm) - (fh * 60 + fm);
  return diff > 0 ? +(diff / 60).toFixed(2) : 0;
}

function fmtTimer(h) {
  if (!h && h !== 0) return "0t";
  const hh = Math.floor(h);
  const mm = Math.round((h - hh) * 60);
  return mm > 0 ? `${hh}t ${mm}m` : `${hh}t`;
}

// ─── Timer / vagtplan fane ──────────────────────────────────────────────────
function TimerFane({ token, medarbejderNavn }) {
  const [vagter, setVagter] = useState([]);
  const [weekOffset, setWeekOffset] = useState(0);
  const [oversigtOffset, setOversigtOffset] = useState(0);
  const [loading, setLoading] = useState(false);
  const [editVagt, setEditVagt] = useState(null);
  const [draft, setDraft] = useState({ dato: "", fra: "", til: "", vagttype: "", city: "", note: "" });
  const [saving, setSaving] = useState(false);

  const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;
  const hdrs = useMemo(() => ({ headers: { Authorization: `Bearer ${token}` } }), [token]);

  const weekDates = useMemo(() => {
    const today = new Date();
    const dow = today.getDay();
    const mon = new Date(today);
    mon.setDate(today.getDate() - (dow === 0 ? 6 : dow - 1) + weekOffset * 7);
    return Array.from({ length: 7 }, (_, i) => {
      const d = new Date(mon);
      d.setDate(mon.getDate() + i);
      return d.toISOString().slice(0, 10);
    });
  }, [weekOffset]);

  const weekLabel = useMemo(() => {
    if (!weekDates.length) return "";
    const fmt = (s) => { const [, m, d] = s.split("-"); return `${parseInt(d, 10)}/${parseInt(m, 10)}`; };
    return `${fmt(weekDates[0])} – ${fmt(weekDates[6])}`;
  }, [weekDates]);

  const oversigtPeriod = useMemo(() => {
    const now = new Date();
    let year = now.getFullYear();
    let month = now.getMonth() + 1;
    month -= oversigtOffset;
    while (month <= 0) { month += 12; year--; }
    while (month > 12) { month -= 12; year++; }
    const prevMonth = month === 1 ? 12 : month - 1;
    const prevYear = month === 1 ? year - 1 : year;
    const pad = (n) => String(n).padStart(2, "0");
    const fraDato = `${prevYear}-${pad(prevMonth)}-20`;
    const tilDato = `${year}-${pad(month)}-19`;
    const monthNames = ["jan","feb","mar","apr","maj","jun","jul","aug","sep","okt","nov","dec"];
    const label = `20. ${monthNames[prevMonth - 1]} – 19. ${monthNames[month - 1]} ${year}`;
    return { label, fraDato, tilDato };
  }, [oversigtOffset]);

  const fetchVagter = useCallback(() => {
    if (!medarbejderNavn) return;
    setLoading(true);
    axios.get(`${API_URL}/hub/vagtplan`, { ...hdrs, params: { medarbejder: medarbejderNavn } })
      .then(r => setVagter(r.data.vagter || r.data || []))
      .catch(() => {}).finally(() => setLoading(false));
  }, [medarbejderNavn, hdrs, API_URL]);

  useEffect(() => { fetchVagter(); }, [fetchVagter]);

  const vagterByDato = useMemo(() => {
    const map = {};
    vagter.forEach(v => { if (!map[v.dato]) map[v.dato] = []; map[v.dato].push(v); });
    return map;
  }, [vagter]);

  const oversigtData = useMemo(() => {
    const { fraDato, tilDato } = oversigtPeriod;
    const periodVagter = vagter.filter(v => v.dato >= fraDato && v.dato <= tilDato);
    const kategorier = {};
    let total = 0;
    periodVagter.forEach(v => {
      const vt = v.vagttype || "Normal";
      kategorier[vt] = (kategorier[vt] || 0) + parseFloat(v.timer || 0);
      total += parseFloat(v.timer || 0);
    });
    return { kategorier, total };
  }, [vagter, oversigtPeriod]);

  const openNew = (dato) => {
    if (DK_HELLIGDAGE.has(dato)) return;
    if ((vagterByDato[dato] || []).length >= 2) return;
    setEditVagt(null);
    setDraft({ dato, fra: "09:00", til: "17:00", vagttype: "", city: ALLE_BYER[0], note: "" });
  };
  const openEdit = (v) => {
    setEditVagt(v);
    setDraft({ dato: v.dato, fra: v.fra, til: v.til, vagttype: v.vagttype || "", city: v.city || ALLE_BYER[0], note: v.note || "" });
  };
  const closeForm = () => { setEditVagt(null); setDraft({ dato: "", fra: "", til: "", vagttype: "", city: "", note: "" }); };

  const handleSave = async () => {
    if (!draft.dato) return;
    setSaving(true);
    try {
      if (editVagt) {
        await axios.put(`${API_URL}/hub/vagtplan/${editVagt.vagt_id}`, { ...draft, medarbejder: medarbejderNavn }, hdrs);
      } else {
        await axios.post(`${API_URL}/hub/vagtplan`, { ...draft, medarbejder: medarbejderNavn }, hdrs);
      }
      fetchVagter(); closeForm();
    } catch (e) { alert(e?.response?.data?.detail || "Fejl ved gemning"); }
    setSaving(false);
  };

  const handleDelete = async (vagt_id) => {
    if (!window.confirm("Slet denne vagt?")) return;
    await axios.delete(`${API_URL}/hub/vagtplan/${vagt_id}`, hdrs);
    fetchVagter();
  };

  const isFormOpen = draft.dato !== "";

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <button onClick={() => setWeekOffset(w => w - 1)} className="p-1.5 rounded-lg hover:bg-secondary transition-colors">
            <ChevronLeft className="w-4 h-4" />
          </button>
          <span className="text-sm font-medium min-w-[120px] text-center">{weekLabel}</span>
          <button onClick={() => setWeekOffset(w => w + 1)} className="p-1.5 rounded-lg hover:bg-secondary transition-colors">
            <ChevronRight className="w-4 h-4" />
          </button>
          {weekOffset !== 0 && (
            <button onClick={() => setWeekOffset(0)} className="text-[13px] text-muted-foreground hover:text-foreground px-2 py-1 rounded-lg hover:bg-secondary transition-colors">
              I dag
            </button>
          )}
        </div>
        <span className="text-[13px] text-muted-foreground">{loading ? "Henter..." : ""}</span>
      </div>

      <div className="grid grid-cols-7 gap-1">
        {weekDates.map((dato, idx) => {
          const isHelligdag = DK_HELLIGDAGE.has(dato);
          const dayVagter = vagterByDato[dato] || [];
          const dayNum = parseInt(dato.slice(8), 10);
          const isToday = dato === new Date().toISOString().slice(0, 10);
          const canAddMore = !isHelligdag && dayVagter.length < 2;
          return (
            <div key={dato}
              className={`rounded-lg border p-2 flex flex-col gap-1 min-h-[80px] transition-colors
                ${isHelligdag ? "border-amber-500/30 bg-amber-500/5" : "border-border"}
                ${isToday ? "ring-1 ring-[#FF2A6D]/40" : ""}
                ${canAddMore ? "cursor-pointer hover:bg-secondary/30" : ""}`}
              onClick={() => canAddMore && openNew(dato)}>
              <div className="flex items-center justify-between">
                <span className={`text-[13px] font-semibold ${isToday ? "text-[#FF2A6D]" : "text-muted-foreground"}`}>{UGEDAGE_SHORT[idx]}</span>
                <span className={`text-[13px] font-bold ${isToday ? "text-[#FF2A6D]" : ""}`}>{dayNum}</span>
              </div>
              {isHelligdag ? (
                <span className="text-[12px] text-amber-500 font-semibold uppercase tracking-wide">Helligdag</span>
              ) : (
                dayVagter.map(v => (
                  <div key={v.vagt_id} className={`rounded px-1 py-0.5 group relative border ${v.vagttype ? "bg-[#FF2A6D]/10 border-[#FF2A6D]/30" : "bg-transparent border-border"}`}
                    onClick={(e) => { e.stopPropagation(); openEdit(v); }}>
                    {v.vagttype && (
                      <span className={`text-[12px] font-semibold block truncate ${VAGTTYPE_COLORS[v.vagttype]?.split(" ")[0] || "text-muted-foreground"}`}>{v.vagttype}</span>
                    )}
                    <span className="text-[13px] text-foreground block">{v.fra}–{v.til}</span>
                    {v.city && <span className="text-[12px] text-muted-foreground block truncate">{v.city}</span>}
                    <button className="absolute top-0 right-0 p-0.5 opacity-0 group-hover:opacity-100 transition-opacity text-red-500"
                      onClick={(e) => { e.stopPropagation(); handleDelete(v.vagt_id); }}>
                      <X className="w-3 h-3" />
                    </button>
                  </div>
                ))
              )}
              {!isHelligdag && dayVagter.length === 1 && (
                <span className="text-[12px] text-muted-foreground/60 text-center">+ vagt</span>
              )}
            </div>
          );
        })}
      </div>

      {isFormOpen && (
        <div className="rounded-xl border border-[#FF2A6D]/30 bg-[#FF2A6D]/5 p-4 flex flex-col gap-3">
          <div className="flex items-center justify-between">
            <span className="text-sm font-semibold">{editVagt ? "Rediger vagt" : `Ny vagt — ${draft.dato}`}</span>
            <button onClick={closeForm} className="p-1 rounded text-muted-foreground hover:text-foreground"><X className="w-4 h-4" /></button>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-[13px] text-muted-foreground mb-1 block">Fra</label>
              <input type="time" value={draft.fra} onChange={e => setDraft(d => ({ ...d, fra: e.target.value }))}
                className="w-full bg-secondary border border-border rounded-lg px-3 h-9 text-sm text-foreground focus:outline-none focus:ring-1 focus:ring-[#FF2A6D]" />
            </div>
            <div>
              <label className="text-[13px] text-muted-foreground mb-1 block">Til</label>
              <input type="time" value={draft.til} onChange={e => setDraft(d => ({ ...d, til: e.target.value }))}
                className="w-full bg-secondary border border-border rounded-lg px-3 h-9 text-sm text-foreground focus:outline-none focus:ring-1 focus:ring-[#FF2A6D]" />
            </div>
            <div>
              <label className="text-[13px] text-muted-foreground mb-1 block">By</label>
              <select value={draft.city} onChange={e => setDraft(d => ({ ...d, city: e.target.value }))}
                className="w-full bg-secondary border border-border rounded-lg px-3 h-9 text-sm text-foreground focus:outline-none">
                {ALLE_BYER.map(b => <option key={b} value={b}>{b}</option>)}
              </select>
            </div>
            <div>
              <label className="text-[13px] text-muted-foreground mb-1 block">Vagttype</label>
              <select value={draft.vagttype} onChange={e => setDraft(d => ({ ...d, vagttype: e.target.value }))}
                className="w-full bg-secondary border border-border rounded-lg px-3 h-9 text-sm text-foreground focus:outline-none">
                {VAGTTYPER.map(vt => <option key={vt} value={vt}>{vt || "Normal"}</option>)}
              </select>
            </div>
            <div className="col-span-2">
              <label className="text-[13px] text-muted-foreground mb-1 block">Note (valgfri)</label>
              <input type="text" value={draft.note} onChange={e => setDraft(d => ({ ...d, note: e.target.value }))} placeholder="Eventuel note..."
                className="w-full bg-secondary border border-border rounded-lg px-3 h-9 text-sm text-foreground focus:outline-none focus:ring-1 focus:ring-[#FF2A6D]" />
            </div>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-[13px] text-muted-foreground">{fmtTimer(calcTimer(draft.fra, draft.til))}</span>
            <button onClick={handleSave} disabled={saving}
              className="flex items-center gap-1.5 px-4 py-2 rounded-lg bg-[#FF2A6D] text-white text-sm font-semibold hover:bg-[#e02560] transition-colors disabled:opacity-60">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
              {editVagt ? "Gem ændringer" : "Tilføj vagt"}
            </button>
          </div>
        </div>
      )}

      <div className="border-t border-border pt-3">
        <div className="flex items-center justify-between mb-3">
          <span className="text-sm font-semibold">Timeoversigt</span>
          <div className="flex items-center gap-1">
            <button onClick={() => setOversigtOffset(o => o + 1)} className="p-1 rounded hover:bg-secondary transition-colors"><ChevronLeft className="w-4 h-4" /></button>
            <span className="text-[13px] font-medium min-w-[160px] text-center">{oversigtPeriod.label}</span>
            <button onClick={() => setOversigtOffset(o => Math.max(0, o - 1))} disabled={oversigtOffset === 0} className="p-1 rounded hover:bg-secondary transition-colors disabled:opacity-30"><ChevronRight className="w-4 h-4" /></button>
          </div>
        </div>
        <div className="rounded-lg border border-border divide-y divide-border">
          {Object.entries(oversigtData.kategorier).length === 0 ? (
            <p className="text-sm text-muted-foreground italic p-3">Ingen vagter i denne periode.</p>
          ) : (
            Object.entries(oversigtData.kategorier).map(([kat, timer]) => (
              <div key={kat} className="flex items-center justify-between px-3 py-2">
                <span className={`text-[13px] font-medium px-2 py-0.5 rounded-full ${VAGTTYPE_COLORS[kat] || "text-foreground bg-secondary"}`}>{kat}</span>
                <span className="text-sm font-semibold tabular-nums">{fmtTimer(timer)}</span>
              </div>
            ))
          )}
          <div className="flex items-center justify-between px-3 py-2 bg-secondary/30">
            <span className="text-[13px] font-bold text-muted-foreground uppercase tracking-wide">Total</span>
            <span className="text-sm font-bold">{fmtTimer(oversigtData.total)}</span>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Ansættelseshistorik ─────────────────────────────────────────────────────
// Hjælper: backward-compat-mapping fra ældre `loenform`-felt til konsolideret `ansaettelsestype`.
function ansTypeFraPost(post) {
  if (!post) return "manedsloen";
  if (post.ansaettelsestype) return post.ansaettelsestype;
  // Gamle records: "timeloen" -> "timeloen", alt andet -> "manedsloen".
  const lf = (post.loenform || "").toString().toLowerCase();
  return lf === "timeloen" ? "timeloen" : "manedsloen";
}

function AnsaettelsesHistorikSektion({ itemId, token, historik, onUpdated, dokumenter, onOpenDoc, ansaettelsestype }) {
  const aktuelType = ansaettelsestype === "timeloen" ? "timeloen" : "manedsloen";
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({
    periode_fra: "", periode_til: "",
    loen: "",
    timer: "", timer_pr_uge: "", stillingsbetegnelse: "",
    pension_arbejdsgiver: "", pension_medarbejder: "",
    afdeling: "", titel: "", note: "",
  });
  const [saving, setSaving] = useState(false);

  const setF = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.value }));

  const handleAdd = async () => {
    if (!form.periode_fra || !form.loen) return;
    setSaving(true);
    try {
      // Send både `ansaettelsestype` (det konsoliderede felt) og `loenform`
      // for backward-compat med eksisterende læsestier i backend.
      const payload = {
        ...form,
        ansaettelsestype: aktuelType,
        loenform: aktuelType === "timeloen" ? "timeloen" : "fastloen",
      };
      await axios.post(`${API}/admin/medarbejdere/${itemId}/ansaettelseshistorik`, payload, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setShowForm(false);
      setForm({
        periode_fra: "", periode_til: "", loen: "",
        timer: "", timer_pr_uge: "", stillingsbetegnelse: "",
        pension_arbejdsgiver: "", pension_medarbejder: "",
        afdeling: "", titel: "", note: "",
      });
      onUpdated();
    } catch { alert("Kunne ikke gemme"); }
    setSaving(false);
  };

  const handleDelete = async (postId) => {
    if (!window.confirm("Slet denne post?")) return;
    await axios.delete(`${API}/admin/medarbejdere/${itemId}/ansaettelseshistorik/${postId}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    onUpdated();
  };

  const sorted = [...(historik || [])].sort((a, b) => (b.periode_fra || "").localeCompare(a.periode_fra || ""));

  return (
    <div className="flex flex-col gap-3">
      {sorted.length === 0 && !showForm && (
        <p className="text-sm text-muted-foreground italic">Ingen ansættelseshistorik endnu.</p>
      )}
      {sorted.map(post => {
        const periodeDok = (dokumenter || []).filter(d => d.doc_type === "kontrakt" && d.periode_id === post.id);
        return (
          <div key={post.id} className="rounded-xl border border-border p-3 bg-secondary/20 group">
            <div className="flex items-start justify-between">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-0.5 flex-wrap">
                  {(() => {
                    const postType = ansTypeFraPost(post);
                    const erTime = postType === "timeloen";
                    return (
                      <span className={`text-[13px] font-semibold px-2 py-0.5 rounded-full ${erTime ? "bg-blue-500/10 text-blue-500" : "bg-emerald-500/10 text-emerald-500"}`}>
                        {erTime ? "Timeløn" : "Månedsløn"}
                      </span>
                    );
                  })()}
                  {(post.titel || post.stillingsbetegnelse) && (
                    <span className="text-[13px] text-muted-foreground font-medium">{post.titel || post.stillingsbetegnelse}</span>
                  )}
                  {post.afdeling && (
                    <span className="text-[13px] text-muted-foreground">{post.afdeling}</span>
                  )}
                  <span className="text-sm font-bold">
                    {ansTypeFraPost(post) === "timeloen"
                      ? `${post.loen} kr./t${post.timer ? ` · ${post.timer}t/uge` : ""}`
                      : `${Number(post.loen).toLocaleString("da-DK")} kr./md`}
                  </span>
                </div>
                <div className="flex flex-wrap gap-x-3 gap-y-0.5">
                  <p className="text-[13px] text-muted-foreground">
                    {fmtDate(post.periode_fra)}{post.periode_til ? ` – ${fmtDate(post.periode_til)}` : " – nu"}
                    {post.note ? ` · ${post.note}` : ""}
                  </p>
                  {(post.pension_arbejdsgiver != null || post.pension_medarbejder != null) && (
                    <p className="text-[13px] text-muted-foreground">
                      Pension: arb. {post.pension_arbejdsgiver ?? "—"}% / md. {post.pension_medarbejder ?? "—"}%
                    </p>
                  )}
                  {post.timer_pr_uge != null && (
                    <p className="text-[13px] text-muted-foreground">{post.timer_pr_uge}t/uge</p>
                  )}
                </div>
                <p className="text-[13px] text-muted-foreground/60 mt-0.5">Registreret af {post.registreret_af} · {fmtTs(post.registreret_at)}</p>
              </div>
              <button onClick={() => handleDelete(post.id)}
                className="p-1 rounded text-muted-foreground hover:text-red-500 hover:bg-red-500/10 opacity-0 group-hover:opacity-100 transition-all ml-2 shrink-0">
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            </div>
            {periodeDok.length > 0 && (
              <div className="mt-2 pt-2 border-t border-border flex flex-col gap-1">
                {periodeDok.map(d => (
                  <button key={d.doc_id} onClick={() => onOpenDoc && onOpenDoc(d)} className="flex items-center gap-1.5 text-[13px] text-[#FF2A6D] hover:text-[#e02560] transition-colors w-fit">
                    <Eye className="w-3 h-3 shrink-0" />
                    <span className="truncate">{d.filename}</span>
                  </button>
                ))}
              </div>
            )}
          </div>
        );
      })}

      {showForm ? (
        <div className="rounded-xl border border-[#FF2A6D]/30 bg-[#FF2A6D]/5 p-4 flex flex-col gap-3">
          {/* Ansættelsestype (read-only, fra stamdata). Konsolideret felt — redigeres på Stamdata-fanen. */}
          <div className="flex items-center gap-2 text-[13px] text-muted-foreground">
            <span>Ansættelsestype:</span>
            <span className={`font-semibold px-2 py-0.5 rounded-full ${aktuelType === "timeloen" ? "bg-blue-500/10 text-blue-500" : "bg-emerald-500/10 text-emerald-500"}`}>
              {aktuelType === "timeloen" ? "Timeløn" : "Månedsløn"}
            </span>
            <span className="italic">(redigeres på Stamdata-fanen)</span>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-[13px] text-muted-foreground mb-1 block">
                {aktuelType === "timeloen" ? "Timeløn (kr./t)" : "Månedsløn (kr./md)"}
              </label>
              <input type="number" value={form.loen} onChange={setF("loen")} placeholder="0"
                className="w-full bg-secondary border border-border rounded-lg px-3 h-9 text-sm focus:outline-none focus:ring-1 focus:ring-[#FF2A6D]" />
            </div>
            <div>
              <label className="text-[13px] text-muted-foreground mb-1 block">Timer pr. uge</label>
              <input type="number" value={form.timer_pr_uge} onChange={setF("timer_pr_uge")} placeholder="37"
                className="w-full bg-secondary border border-border rounded-lg px-3 h-9 text-sm focus:outline-none focus:ring-1 focus:ring-[#FF2A6D]" />
            </div>
            <div>
              <label className="text-[13px] text-muted-foreground mb-1 block">Titel/stilling</label>
              <input type="text" value={form.titel} onChange={setF("titel")} placeholder="Frisør"
                className="w-full bg-secondary border border-border rounded-lg px-3 h-9 text-sm focus:outline-none focus:ring-1 focus:ring-[#FF2A6D]" />
            </div>
            <div>
              <label className="text-[13px] text-muted-foreground mb-1 block">Pension arb.giver %</label>
              <input type="number" step="0.5" value={form.pension_arbejdsgiver} onChange={setF("pension_arbejdsgiver")} placeholder="0"
                className="w-full bg-secondary border border-border rounded-lg px-3 h-9 text-sm focus:outline-none focus:ring-1 focus:ring-[#FF2A6D]" />
            </div>
            <div>
              <label className="text-[13px] text-muted-foreground mb-1 block">Pension medarbejder %</label>
              <input type="number" step="0.5" value={form.pension_medarbejder} onChange={setF("pension_medarbejder")} placeholder="0"
                className="w-full bg-secondary border border-border rounded-lg px-3 h-9 text-sm focus:outline-none focus:ring-1 focus:ring-[#FF2A6D]" />
            </div>
            <div>
              <label className="text-[13px] text-muted-foreground mb-1 block">Afdeling</label>
              <select value={form.afdeling} onChange={setF("afdeling")} className="w-full bg-secondary border border-border rounded-lg px-3 h-9 text-sm focus:outline-none">
                <option value="">— vælg —</option>
                {ALLE_BYER.map(b => <option key={b} value={b}>{b}</option>)}
              </select>
            </div>
            <div>
              <label className="text-[13px] text-muted-foreground mb-1 block">Periode fra</label>
              <input type="date" value={form.periode_fra} onChange={setF("periode_fra")}
                className="w-full bg-secondary border border-border rounded-lg px-3 h-9 text-sm focus:outline-none focus:ring-1 focus:ring-[#FF2A6D]" />
            </div>
            <div>
              <label className="text-[13px] text-muted-foreground mb-1 block">Periode til (tom = nu)</label>
              <input type="date" value={form.periode_til} onChange={setF("periode_til")}
                className="w-full bg-secondary border border-border rounded-lg px-3 h-9 text-sm focus:outline-none focus:ring-1 focus:ring-[#FF2A6D]" />
            </div>
            <div className="col-span-2">
              <label className="text-[13px] text-muted-foreground mb-1 block">Note (valgfri)</label>
              <input type="text" value={form.note} onChange={setF("note")} placeholder="fx. Lønforhandling"
                className="w-full bg-secondary border border-border rounded-lg px-3 h-9 text-sm focus:outline-none focus:ring-1 focus:ring-[#FF2A6D]" />
            </div>
          </div>
          <div className="flex justify-end gap-2">
            <button onClick={() => setShowForm(false)} className="px-3 py-1.5 rounded-lg text-sm border border-border hover:bg-secondary transition-colors">Annuller</button>
            <button onClick={handleAdd} disabled={saving || !form.loen || !form.periode_fra}
              className="flex items-center gap-1.5 px-4 py-1.5 rounded-lg bg-[#FF2A6D] text-white text-sm font-semibold hover:bg-[#e02560] transition-colors disabled:opacity-60">
              {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />}
              Gem
            </button>
          </div>
        </div>
      ) : (
        <button onClick={() => setShowForm(true)}
          className="flex items-center gap-1.5 text-sm text-[#FF2A6D] hover:text-[#e02560] transition-colors mt-1">
          <Plus className="w-4 h-4" /> Tilføj post
        </button>
      )}
    </div>
  );
}

// ─── Lønberegner ─────────────────────────────────────────────────────────────
// Backward-compat helper: læs ansaettelsestype, fald evt. tilbage til ældre loenform.
function _resolveAnsType(rec) {
  if (!rec) return "manedsloen";
  if (rec.ansaettelsestype) return rec.ansaettelsestype;
  const lf = (rec.loenform || "").toString().toLowerCase();
  return lf === "timeloen" ? "timeloen" : "manedsloen";
}

function LoenBeregner({ stamdata, profil, itemId, token, onUpdated, onLoenChanged }) {
  const valFn = (key) => stamdata[key] !== undefined && stamdata[key] !== null ? stamdata[key] : (profil || {})[key];

  // Konsolideret ansættelsestype — læs direkte fra medarbejder-objektet (stamdata + profil).
  // Backward-compat: hvis kun loenform findes, mapper vi til ny værdi.
  const ansType = _resolveAnsType({
    ansaettelsestype: valFn("ansaettelsestype"),
    loenform: valFn("loenform"),
  });
  const erTime = ansType === "timeloen";
  const erVikar = ansType === "vikar";

  // For månedsløn: `loen` = kr/md. For timeløn: `loen` = kr/t.
  const nuLoen = parseFloat(valFn("loen") || valFn("manedligt_vederlag") || 0);
  const nuTimer = parseFloat(valFn("timer_pr_uge") || 37) || 37;
  const pensionPct = parseFloat(valFn("pension_arbejdsgiver") || 0);

  const [nyLoen, setNyLoen] = useState("");
  const [nyTimer, setNyTimer] = useState("");
  const [implementing, setImplementing] = useState(false);

  // Vikarer har ingen fast løn — vis informativ besked og skjul beregner-felterne.
  // (Early return EFTER hooks så Rules of Hooks overholdes.)
  if (erVikar) {
    return (
      <div className="rounded-xl border border-amber-500/30 bg-amber-500/5 p-6">
        <h3 className="text-sm font-bold mb-2">Vikar</h3>
        <p className="text-sm text-muted-foreground">
          Vikarer har ingen fast løn — løn beregnes pr. opgave. Lønberegneren er derfor deaktiveret.
        </p>
        <p className="text-[13px] text-muted-foreground mt-2">
          Vikar-vagter tildeles ad-hoc fra vagtplanen uden faste timer.
        </p>
      </div>
    );
  }

  const beregn = () => {
    const loenInput = parseFloat(nyLoen) || nuLoen;
    const timer = parseFloat(nyTimer) || nuTimer;
    const timerMd = +(timer * 4.33).toFixed(1);

    // Bestem månedslønnen (faktisk eller afledt) — ALT tillægs-beregning bygger på den.
    let maanedsloen = 0;
    let timeloenAfledt = 0;
    let maanedsloenAfledt = 0;

    if (erTime) {
      // Timeløn: input er kr/t. Afledt månedsløn = timeløn × timer/uge × 4.33.
      maanedsloen = loenInput > 0 && timer > 0 ? +(loenInput * timer * 4.33).toFixed(2) : 0;
      maanedsloenAfledt = maanedsloen;
    } else {
      // Månedsløn: input er kr/md. Afledt timeløn = månedsløn / (timer/uge × 4.33).
      maanedsloen = loenInput;
      timeloenAfledt = loenInput > 0 && timerMd > 0 ? +(loenInput / timerMd).toFixed(2) : 0;
    }

    const pension = +(maanedsloen * pensionPct / 100).toFixed(0);
    const ferie = +(maanedsloen * 0.125).toFixed(0);
    const atp = 300;
    const total = maanedsloen + pension + ferie + atp;
    const totalAar = total * 12;

    // Diff sammenlignes i samme enhed som input (kr/t for timeløn, kr/md for månedsløn).
    const diff = loenInput - nuLoen;
    const diffPct = nuLoen > 0 ? +((diff / nuLoen) * 100).toFixed(1) : 0;

    return {
      loenInput, timer, timerMd, maanedsloen,
      timeloenAfledt, maanedsloenAfledt,
      pension, ferie, atp, total, totalAar, diff, diffPct,
    };
  };

  const r = beregn();
  const harNyLoen = nyLoen !== "" || nyTimer !== "";

  const handleImplement = async () => {
    if (!nyLoen && !nyTimer) return;
    setImplementing(true);
    try {
      const datoIDag = new Date().toISOString().slice(0, 10);
      if (nuLoen > 0) {
        // Gem en lønhistorik-post med den GAMLE løn — type-konsolideret.
        await axios.post(`${API}/admin/medarbejdere/${itemId}/ansaettelseshistorik`, {
          periode_fra: valFn("anciennitetsdato") || valFn("ansaettelsesdato") || datoIDag,
          periode_til: datoIDag,
          loen: nuLoen,
          ansaettelsestype: ansType,
          loenform: erTime ? "timeloen" : "fastloen", // backward-compat
          timer_pr_uge: nuTimer,
          pension_arbejdsgiver: pensionPct,
          note: "Tidligere løn (fra lønberegner)",
        }, { headers: { Authorization: `Bearer ${token}` } });
      }
      const opdateret = {};
      if (nyLoen) opdateret.loen = parseFloat(nyLoen);
      if (nyTimer) opdateret.timer_pr_uge = parseFloat(nyTimer);
      // Bevar ansættelsestypen (kan ikke ændres her — gøres på Stamdata-fanen).
      opdateret.ansaettelsestype = ansType;
      await axios.put(`${API}/admin/medarbejdere/${itemId}/stamdata`, opdateret, {
        headers: { Authorization: `Bearer ${token}` },
      });
      // Trigger Fortryd-toast på hovedsiden — kun hvis selve lønnen blev ændret.
      if (nyLoen && onLoenChanged) {
        onLoenChanged(nuLoen, parseFloat(nyLoen));
      }
      setNyLoen("");
      setNyTimer("");
      onUpdated();
    } catch { alert("Kunne ikke implementere løn"); }
    setImplementing(false);
  };

  const fmtKr = (n) => Number(n).toLocaleString("da-DK") + " kr";
  const ancDato = valFn("anciennitetsdato") || valFn("ansaettelsesdato");
  const typeLabel = erTime ? "Timeløn" : "Månedsløn";
  const loenLabel = erTime ? "Timeløn (kr/t)" : "Månedsløn (kr/md)";
  const enhed = erTime ? "/t" : "/md";

  return (
    <div className="flex flex-col gap-5">
      <div className="rounded-xl border border-border p-4 bg-secondary/20">
        <p className="text-[13px] font-bold text-muted-foreground uppercase tracking-wider mb-3">Nuværende</p>
        <div className="grid grid-cols-3 gap-3">
          <div>
            <p className="text-[13px] text-muted-foreground mb-1">Ansættelsestype</p>
            <p className="text-sm font-semibold">{typeLabel}</p>
          </div>
          <div>
            <p className="text-[13px] text-muted-foreground mb-1">Løn</p>
            <p className="text-sm font-semibold">{nuLoen > 0 ? fmtKr(nuLoen) + enhed : "—"}</p>
          </div>
          <div>
            <p className="text-[13px] text-muted-foreground mb-1">Timer</p>
            <p className="text-sm font-semibold">{nuTimer > 0 ? nuTimer + " t/uge" : "—"}</p>
          </div>
        </div>
      </div>

      <div className="rounded-xl border border-border p-4">
        <p className="text-[13px] font-bold text-muted-foreground uppercase tracking-wider mb-3">Beregn ny løn</p>
        <div className="grid grid-cols-2 gap-3 mb-4">
          <div>
            <label className="text-[13px] text-muted-foreground mb-1 block">{loenLabel}</label>
            <input type="number" value={nyLoen} onChange={e => setNyLoen(e.target.value)} placeholder={String(nuLoen || "")}
              className="w-full bg-secondary border border-border rounded-lg px-3 h-9 text-sm focus:outline-none focus:ring-1 focus:ring-[#FF2A6D]" />
            {/* Afledt visning under input */}
            {erTime ? (
              r.maanedsloenAfledt > 0 && (
                <p className="text-[13px] text-muted-foreground mt-1">
                  Månedsløn svarer til: {fmtKr(r.maanedsloenAfledt)}/md (ved {r.timer} t/uge)
                </p>
              )
            ) : (
              r.timeloenAfledt > 0 && (
                <p className="text-[13px] text-muted-foreground mt-1">
                  Timeløn svarer til: {fmtKr(r.timeloenAfledt)}/t
                </p>
              )
            )}
          </div>
          <div>
            <label className="text-[13px] text-muted-foreground mb-1 block">Timer/uge</label>
            <input type="number" value={nyTimer} onChange={e => setNyTimer(e.target.value)} placeholder={String(nuTimer || "")}
              className="w-full bg-secondary border border-border rounded-lg px-3 h-9 text-sm focus:outline-none focus:ring-1 focus:ring-[#FF2A6D]" />
            <p className="text-[13px] text-muted-foreground mt-1">{r.timer} × 4.33 = {r.timerMd} t/md</p>
          </div>
        </div>

        <div className="rounded-lg border border-border overflow-hidden mb-4">
          <div className="px-3 py-2 bg-secondary/50">
            <p className="text-[13px] font-bold text-muted-foreground uppercase tracking-wider">Fuld medarbejderudgift</p>
          </div>
          <div className="divide-y divide-border">
            <div className="flex justify-between px-3 py-2">
              <span className="text-sm text-muted-foreground">Grundløn (md)</span>
              <span className="text-sm font-medium tabular-nums">{fmtKr(r.maanedsloen)}</span>
            </div>
            {pensionPct > 0 && (
              <div className="flex justify-between px-3 py-2">
                <span className="text-sm text-muted-foreground">Pension arb.giver ({pensionPct}%)</span>
                <span className="text-sm font-medium tabular-nums">{fmtKr(r.pension)}</span>
              </div>
            )}
            <div className="flex justify-between px-3 py-2">
              <span className="text-sm text-muted-foreground">Feriepenge (12.5%)</span>
              <span className="text-sm font-medium tabular-nums">{fmtKr(r.ferie)}</span>
            </div>
            <div className="flex justify-between px-3 py-2">
              <span className="text-sm text-muted-foreground">ATP (est.)</span>
              <span className="text-sm font-medium tabular-nums">{fmtKr(r.atp)}</span>
            </div>
            <div className="flex justify-between px-3 py-2 bg-secondary/30">
              <span className="text-sm font-bold">Total pr. md</span>
              <span className="text-sm font-bold tabular-nums">{fmtKr(r.total)}</span>
            </div>
            <div className="flex justify-between px-3 py-2">
              <span className="text-sm text-muted-foreground">Total pr. år</span>
              <span className="text-sm font-semibold tabular-nums">{fmtKr(r.totalAar)}</span>
            </div>
          </div>
        </div>

        {harNyLoen && nuLoen > 0 && (
          <div className="rounded-lg border border-border p-3 mb-4 bg-secondary/20">
            <p className="text-[13px] font-bold text-muted-foreground uppercase tracking-wider mb-2">Ændring fra nuværende</p>
            <p className={`text-sm font-bold ${r.diff >= 0 ? "text-green-500" : "text-red-500"}`}>
              {r.diff >= 0 ? "+" : ""}{fmtKr(r.diff)}{enhed} ({r.diff >= 0 ? "+" : ""}{r.diffPct}%)
            </p>
            {ancDato && (
              <p className="text-[13px] text-muted-foreground mt-0.5">
                Siden ansættelse ({fmtDate(ancDato)}): {r.diff >= 0 ? "+" : ""}{fmtKr(r.diff)}{enhed}
              </p>
            )}
          </div>
        )}

        {harNyLoen && (
          <div className="flex justify-end">
            <button onClick={handleImplement} disabled={implementing}
              className="flex items-center gap-1.5 px-4 py-2 rounded-lg bg-[#FF2A6D] text-white text-sm font-semibold hover:bg-[#e02560] transition-colors disabled:opacity-60">
              {implementing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
              Implementér som nuværende løn
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Kontrakter sektion ──────────────────────────────────────────────────────
function KontrakterSektion({ kontrakter, token, onSynced, onStatusChanged, showAktive = true, hideSyncButton = false }) {
  const [syncing, setSyncing] = useState(false);
  const [syncMsg, setSyncMsg] = useState(null);
  const [movingId, setMovingId] = useState(null);
  const [fetchingFilerId, setFetchingFilerId] = useState(null);
  const aktive = kontrakter.filter(k => k.er_aktiv);
  const tidligere = kontrakter.filter(k => !k.er_aktiv);

  const downloadFil = async (fileId, navn) => {
    try {
      const r = await fetch(`${API}/podio/file/${fileId}`, { headers: { Authorization: `Bearer ${token}` } });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = navn; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { alert("Download fejlede: " + e.message); }
  };

  const hentFiler = async (kontrakt_id) => {
    setFetchingFilerId(kontrakt_id);
    try {
      await axios.post(`${API}/admin/kontrakter/${kontrakt_id}/sync-filer`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (onSynced) onSynced();
    } catch (e) { alert("Fejl ved hentning af filer: " + (e.response?.data?.detail || e.message)); }
    setFetchingFilerId(null);
  };

  const flytTilHistorik = async (kontrakt_id) => {
    setMovingId(kontrakt_id);
    try {
      await axios.patch(`${API}/admin/medarbejdere/0/kontrakter/${kontrakt_id}/status`,
        { er_aktiv: false, status: "Ophørt" },
        { headers: { Authorization: `Bearer ${token}` } });
      if (onStatusChanged) onStatusChanged();
    } catch (e) { alert("Fejl: " + (e.response?.data?.detail || e.message)); }
    setMovingId(null);
  };

  const handleSync = async () => {
    setSyncing(true);
    setSyncMsg("Starter sync...");
    try {
      await axios.post(`${API}/admin/medarbejdere/sync-kontrakter`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setSyncMsg("Sync kører... (kan tage et par minutter)");
      setTimeout(() => {
        setSyncMsg("Sync afsluttet — genindlæser...");
        setSyncing(false);
        onSynced();
      }, 15000);
    } catch (e) {
      setSyncMsg("Fejl: " + (e.response?.data?.detail || e.message));
      setSyncing(false);
    }
  };

  const Kontrakt = ({ k }) => (
    <div className={`rounded-xl border p-3 ${k.er_aktiv ? "border-green-500/30 bg-green-500/5" : "border-border bg-secondary/20"}`}>
      <div className="flex items-start justify-between gap-2">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-sm font-medium truncate">{k.titel || "Kontrakt"}</span>
            <span className={`text-[13px] font-bold px-1.5 py-0.5 rounded-full shrink-0 ${
              k.er_aktiv ? "bg-green-500/20 text-green-600 dark:text-green-400"
              : "bg-secondary text-muted-foreground"
            }`}>
              {k.status || (k.er_aktiv ? "Aktiv" : "Ophørt")}
            </span>
          </div>
          {k.dato && <p className="text-[13px] text-muted-foreground mt-0.5">{fmtDate(k.dato)}</p>}
        </div>
        {k.er_aktiv && (
          <button
            onClick={() => flytTilHistorik(k.kontrakt_id)}
            disabled={movingId === k.kontrakt_id}
            className="shrink-0 flex items-center gap-1 text-[13px] text-muted-foreground hover:text-orange-500 border border-border hover:border-orange-500/40 rounded-lg px-2 py-1 transition-colors disabled:opacity-50">
            {movingId === k.kontrakt_id ? <Loader2 className="w-3 h-3 animate-spin" /> : <History className="w-3 h-3" />}
            Flyt til historik
          </button>
        )}
      </div>
      {k.filer?.length > 0 ? (
        <div className="mt-2 flex flex-col gap-1">
          {k.filer.map(f => (
            <button key={f.file_id} onClick={() => downloadFil(f.file_id, f.navn)}
              className="flex items-center gap-2 text-[13px] text-[#FF2A6D] hover:text-[#e02560] transition-colors">
              <Download className="w-3 h-3 shrink-0" />
              <span className="truncate">{f.navn}</span>
              {f.size && <span className="text-muted-foreground shrink-0">{fmtBytes(f.size)}</span>}
            </button>
          ))}
        </div>
      ) : (
        <button
          onClick={() => hentFiler(k.kontrakt_id)}
          disabled={fetchingFilerId === k.kontrakt_id}
          className="mt-2 flex items-center gap-1.5 text-[13px] text-amber-500 hover:text-amber-400 transition-colors disabled:opacity-50">
          {fetchingFilerId === k.kontrakt_id ? <Loader2 className="w-3 h-3 animate-spin" /> : <Download className="w-3 h-3" />}
          {fetchingFilerId === k.kontrakt_id ? "Henter filer..." : "Hent filer fra Podio"}
        </button>
      )}
    </div>
  );

  return (
    <div className="flex flex-col gap-3">
      {kontrakter.length === 0 && (
        <p className="text-sm text-muted-foreground italic">Ingen kontrakter synkroniseret fra Podio endnu.</p>
      )}
      {!showAktive && kontrakter.length > 0 && tidligere.length === 0 && (
        <p className="text-sm text-muted-foreground italic">Ingen tidligere kontrakter.</p>
      )}
      {showAktive && aktive.length > 0 && (
        <div className="flex flex-col gap-2">
          <p className="text-[13px] font-bold text-muted-foreground uppercase tracking-wider">Aktiv kontrakt</p>
          {aktive.map(k => <Kontrakt key={k.kontrakt_id} k={k} />)}
        </div>
      )}
      {tidligere.length > 0 && (
        <div className="flex flex-col gap-2">
          <p className="text-[13px] font-bold text-muted-foreground uppercase tracking-wider">Tidligere kontrakter</p>
          {tidligere.map(k => <Kontrakt key={k.kontrakt_id} k={k} />)}
        </div>
      )}
      {!hideSyncButton && (
        <div className="flex items-center gap-2 pt-1">
          <button onClick={handleSync} disabled={syncing}
            className="flex items-center gap-1.5 text-[13px] text-amber-500 hover:text-amber-400 border border-amber-500/30 bg-amber-500/10 hover:bg-amber-500/20 px-3 py-1.5 rounded-full font-semibold transition-colors disabled:opacity-60">
            {syncing ? <Loader2 className="w-3 h-3 animate-spin" /> : null}
            Sync kontrakter fra Podio
          </button>
          {syncMsg && <span className="text-[13px] text-muted-foreground">{syncMsg}</span>}
        </div>
      )}
    </div>
  );
}

// ─── Opsigelses-modal ────────────────────────────────────────────────────────
const OPSIG_AARSAGER = ["Opsagt", "Fratrådt", "Bortvist", "Barsel/Orlov"];

function OpsigModal({ onClose, onConfirm, saving }) {
  const [aarsag, setAarsag] = useState("Opsagt");
  const [dato, setDato] = useState("");
  return (
    <ModalPortal onClose={onClose}>
      <div className="bg-background border border-border rounded-2xl shadow-2xl w-full max-w-sm">
        <div className="flex items-center justify-between px-5 py-4 border-b border-border">
          <h2 className="font-semibold text-sm flex items-center gap-2"><UserX className="w-4 h-4 text-red-500" /> Opsig medarbejder</h2>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground"><X className="w-4 h-4" /></button>
        </div>
        <div className="p-5 flex flex-col gap-4">
          <div>
            <label className="text-[13px] text-muted-foreground mb-1 block">Årsag</label>
            <select value={aarsag} onChange={e => setAarsag(e.target.value)}
              className="w-full bg-secondary border border-border rounded-lg px-3 h-9 text-sm focus:outline-none focus:ring-1 focus:ring-red-500">
              {OPSIG_AARSAGER.map(a => <option key={a} value={a}>{a}</option>)}
            </select>
          </div>
          <div>
            <label className="text-[13px] text-muted-foreground mb-1 block">Ophørsdato / Startdato</label>
            <input type="date" value={dato} onChange={e => setDato(e.target.value)}
              className="w-full bg-secondary border border-border rounded-lg px-3 h-9 text-sm focus:outline-none focus:ring-1 focus:ring-red-500" />
          </div>
        </div>
        <div className="flex justify-end gap-2 px-5 pb-5">
          <button onClick={onClose} className="px-4 py-2 rounded-lg text-sm border border-border hover:bg-secondary transition-colors">Annuller</button>
          <button onClick={() => onConfirm(aarsag, dato)} disabled={saving || !dato}
            className="flex items-center gap-1.5 px-5 py-2 rounded-lg bg-red-500 text-white text-sm font-semibold hover:bg-red-600 transition-colors disabled:opacity-60">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <UserX className="w-4 h-4" />}
            Bekræft opsigelse
          </button>
        </div>
      </div>
    </ModalPortal>
  );
}

// ─── Personlige arbejdslokationer ───────────────────────────────────────────
const EXTRA_LOK_VAGTTYPER = ["Normal timer", "Hjemmearbejdsplads", "Behandler timer", "Andet"];
const EXTRA_LOK_EMPTY = { vagttype: "Normal timer", adresse: "", lat: null, lng: null, radius: 100 };

// Leaflet-kort til adressesøgning og pin-placering
function LokationKort({ lat, lng, onPick }) {
  const mapRef = useRef(null);
  const leafletRef = useRef(null);
  const markerRef = useRef(null);
  const containerRef = useRef(null);

  useLayoutEffect(() => {
    if (!containerRef.current || leafletRef.current) return;
    // Dynamisk import af leaflet CSS
    if (!document.getElementById("leaflet-css")) {
      const link = document.createElement("link");
      link.id = "leaflet-css";
      link.rel = "stylesheet";
      link.href = "https://unpkg.com/leaflet@1.9.4/dist/leaflet.css";
      document.head.appendChild(link);
    }
    import("leaflet").then(L => {
      // Fix manglende marker-ikoner
      delete L.Icon.Default.prototype._getIconUrl;
      L.Icon.Default.mergeOptions({
        iconUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png",
        iconRetinaUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png",
        shadowUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png",
      });

      const initLat = lat || 55.6761;
      const initLng = lng || 12.5683;
      const map = L.map(containerRef.current).setView([initLat, initLng], lat ? 16 : 7);
      L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
        attribution: "© OpenStreetMap",
      }).addTo(map);

      if (lat && lng) {
        markerRef.current = L.marker([lat, lng], { draggable: true }).addTo(map);
        markerRef.current.on("dragend", () => {
          const p = markerRef.current.getLatLng();
          onPick(p.lat, p.lng);
        });
      }

      map.on("click", (e) => {
        const { lat: clickLat, lng: clickLng } = e.latlng;
        if (markerRef.current) {
          markerRef.current.setLatLng([clickLat, clickLng]);
        } else {
          markerRef.current = L.marker([clickLat, clickLng], { draggable: true }).addTo(map);
          markerRef.current.on("dragend", () => {
            const p = markerRef.current.getLatLng();
            onPick(p.lat, p.lng);
          });
        }
        onPick(clickLat, clickLng);
      });

      leafletRef.current = { map, L };
      mapRef.current = map;
    });
    return () => {
      if (mapRef.current) { mapRef.current.remove(); mapRef.current = null; leafletRef.current = null; markerRef.current = null; }
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Opdater markør udefra (når geocoding finder en adresse)
  useEffect(() => {
    if (!leafletRef.current || lat == null || lng == null) return;
    const { map, L } = leafletRef.current;
    if (markerRef.current) {
      markerRef.current.setLatLng([lat, lng]);
    } else {
      markerRef.current = L.marker([lat, lng], { draggable: true }).addTo(map);
      markerRef.current.on("dragend", () => {
        const p = markerRef.current.getLatLng();
        onPick(p.lat, p.lng);
      });
    }
    map.setView([lat, lng], 16);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lat, lng]);

  return <div ref={containerRef} style={{ height: 260, borderRadius: 12, overflow: "hidden" }} />;
}

function LokationModal({ onClose, onSave, saving, token }) {
  const [form, setForm] = useState(EXTRA_LOK_EMPTY);
  const [query, setQuery] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [searching, setSearching] = useState(false);
  const [standardLoks, setStandardLoks] = useState([]);
  const searchTimer = useRef(null);

  useEffect(() => {
    axios.get(`${API}/admin/standard-lokationer`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => setStandardLoks(r.data.lokationer || []))
      .catch(() => {});
  }, [token]);

  const pickStandard = (lok) => {
    const adresse = lok.adresse || "";
    setForm(f => ({ ...f, adresse, lat: lok.lat, lng: lok.lng, radius: lok.radius_meter || 100, radius_meter: lok.radius_meter || 100, _fromStandard: lok.navn }));
    setQuery(adresse);
    setSuggestions([]);
  };

  const setF = (k) => (v) => setForm(f => ({ ...f, [k]: v }));

  const geocode = async (q) => {
    if (!q.trim() || q.length < 4) { setSuggestions([]); return; }
    setSearching(true);
    try {
      const r = await fetch(
        `https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(q)}&format=json&addressdetails=1&limit=5&countrycodes=dk`,
        { headers: { "Accept-Language": "da" } }
      );
      const data = await r.json();
      setSuggestions(data);
    } catch {}
    setSearching(false);
  };

  const handleQueryChange = (e) => {
    const v = e.target.value;
    setQuery(v);
    clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => geocode(v), 400);
  };

  const pickSuggestion = (s) => {
    const a = s.address || {};
    const vej = a.road || a.pedestrian || a.footway || "";
    const nr = a.house_number || "";
    const post = a.postcode || "";
    const by = a.city || a.town || a.village || a.municipality || "";
    const adresse = [vej + (nr ? " " + nr : ""), post + (by ? " " + by : "")].filter(Boolean).join(", ");
    setForm(f => ({ ...f, adresse: adresse || s.display_name.split(",").slice(0, 3).join(", "), lat: parseFloat(s.lat), lng: parseFloat(s.lon) }));
    setQuery(adresse || s.display_name.split(",").slice(0, 3).join(", "));
    setSuggestions([]);
  };

  const handleMapPick = async (lat, lng) => {
    setForm(f => ({ ...f, lat, lng }));
    // Reverse geocode
    try {
      const r = await fetch(
        `https://nominatim.openstreetmap.org/reverse?lat=${lat}&lon=${lng}&format=json`,
        { headers: { "Accept-Language": "da" } }
      );
      const data = await r.json();
      if (data.display_name) {
        const adresse = data.display_name.split(",").slice(0, 3).join(", ");
        setForm(f => ({ ...f, lat, lng, adresse }));
        setQuery(adresse);
      }
    } catch {}
  };

  const canSave = (form.adresse.trim() || form._fromStandard) && form.lat != null && form.lng != null;

  return (
    <ModalPortal onClose={onClose}>
      <div className="bg-card border border-border rounded-2xl shadow-2xl w-full max-w-lg flex flex-col" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-4 border-b border-border">
          <h2 className="font-bold text-sm">Tilføj arbejdslokation</h2>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-secondary transition-colors"><X className="w-4 h-4" /></button>
        </div>
        <div className="p-5 flex flex-col gap-4 overflow-y-auto">
          {/* Vagttype */}
          <div>
            <label className="text-[13px] text-muted-foreground mb-1 block">Vagttype</label>
            <select value={form.vagttype} onChange={e => setF("vagttype")(e.target.value)}
              className="w-full bg-secondary border border-border rounded-lg px-3 h-9 text-sm focus:outline-none focus:ring-1 focus:ring-[#FF2A6D]">
              {EXTRA_LOK_VAGTTYPER.map(vt => <option key={vt} value={vt}>{vt}</option>)}
            </select>
          </div>

          {/* Standard-lokationer */}
          {standardLoks.length > 0 && (
            <div>
              <label className="text-[13px] text-muted-foreground mb-1.5 block">Vælg fra standard</label>
              <div className="flex flex-wrap gap-1.5">
                {standardLoks.map(lok => (
                  <button
                    key={lok.lok_id}
                    onClick={() => pickStandard(lok)}
                    className={`px-2.5 py-1 rounded-full text-[13px] font-medium border transition-colors ${
                      form.adresse === (lok.adresse || lok.navn)
                        ? "bg-[#FF2A6D] text-white border-[#FF2A6D]"
                        : "border-border bg-secondary/50 hover:border-[#FF2A6D]/50 hover:bg-[#FF2A6D]/10 hover:text-[#FF2A6D]"
                    }`}
                  >
                    {lok.navn}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Adressesøgning */}
          <div className="relative">
            <label className="text-[13px] text-muted-foreground mb-1 block">Søg adresse</label>
            <div className="relative">
              <input
                value={query}
                onChange={handleQueryChange}
                placeholder="Bredbjergvej 48, 5000 Odense..."
                className="w-full bg-secondary border border-border rounded-lg px-3 pr-8 h-9 text-sm focus:outline-none focus:ring-1 focus:ring-[#FF2A6D]"
              />
              {searching && <Loader2 className="absolute right-2 top-2.5 w-4 h-4 animate-spin text-muted-foreground" />}
            </div>
            {suggestions.length > 0 && (
              <div className="absolute z-10 left-0 right-0 top-full mt-1 bg-background border border-border rounded-xl shadow-xl overflow-hidden">
                {suggestions.map((s, i) => (
                  <button key={i} onClick={() => pickSuggestion(s)}
                    className="w-full text-left px-3 py-2 text-[13px] hover:bg-secondary transition-colors border-b border-border last:border-0">
                    {s.display_name}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Kort */}
          <div>
            <label className="text-[13px] text-muted-foreground mb-1 block">
              Bekræft på kortet — klik for at justere pin
            </label>
            <LokationKort lat={form.lat} lng={form.lng} onPick={handleMapPick} />
          </div>

          {/* Adresse-bekræftelse og radius */}
          {form.lat != null && (
            <div className="flex items-center gap-3 bg-secondary/40 rounded-lg px-3 py-2.5">
              <Check className="w-4 h-4 text-green-500 shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="text-[13px] font-medium truncate">{form.adresse || "Placering valgt"}</p>
                <p className="text-[12px] text-muted-foreground">{form.lat?.toFixed(5)}, {form.lng?.toFixed(5)}</p>
              </div>
              <div className="shrink-0">
                <label className="text-[13px] text-muted-foreground block mb-0.5">Radius (m)</label>
                <input type="number" value={form.radius} min="50" max="2000"
                  onChange={e => setF("radius")(parseInt(e.target.value, 10) || 100)}
                  className="w-20 bg-secondary border border-border rounded-lg px-2 h-7 text-[13px] focus:outline-none focus:ring-1 focus:ring-[#FF2A6D] text-right" />
              </div>
            </div>
          )}
        </div>

        <div className="px-5 pb-4 flex justify-end gap-2 border-t border-border pt-3">
          <button onClick={onClose} className="px-4 py-2 rounded-xl text-sm border border-border hover:bg-secondary transition-colors">Annuller</button>
          <button onClick={() => onSave(form)} disabled={saving || !canSave}
            className="flex items-center gap-1.5 px-5 py-2 rounded-xl bg-[#FF2A6D] text-white text-sm font-semibold hover:bg-[#e02560] transition-colors disabled:opacity-60">
            {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />}
            Gem lokation
          </button>
        </div>
      </div>
    </ModalPortal>
  );
}

function ExtraLokationerSektion({ itemId, token, lokationer, onUpdated }) {
  const [list, setList] = useState(lokationer || []);
  const [showModal, setShowModal] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => { setList(lokationer || []); }, [lokationer]);

  const save = async (nyList) => {
    setSaving(true);
    try {
      await axios.put(`${API}/admin/medarbejdere/${itemId}/extra-lokationer`, { extra_lokationer: nyList }, {
        headers: { Authorization: `Bearer ${token}` },
      });
      onUpdated();
    } catch { alert("Kunne ikke gemme lokationer"); }
    setSaving(false);
  };

  const handleSave = async (form) => {
    const radius = parseInt(form.radius_meter || form.radius, 10) || 100;
    const entry = {
      vagttype: form._fromStandard || form.vagttype,
      adresse: form.adresse.trim(),
      lat: parseFloat(form.lat),
      lng: parseFloat(form.lng),
      radius,
      radius_meter: radius,
      navn: form._fromStandard || form.vagttype,
    };
    await save([...list, entry]);
    setShowModal(false);
  };

  const handleDelete = async (idx) => {
    await save(list.filter((_, i) => i !== idx));
  };

  return (
    <div className="rounded-xl border border-border p-5">
      {showModal && <LokationModal onClose={() => setShowModal(false)} onSave={handleSave} saving={saving} token={token} />}
      <h3 className="text-[13px] font-bold text-muted-foreground uppercase tracking-wider mb-4">Arbejdslokationer</h3>
      <div className="flex flex-col gap-2 mb-3">
        {list.length === 0 && (
          <p className="text-sm text-muted-foreground italic">Ingen personlige lokationer oprettet.</p>
        )}
        {list.map((lok, idx) => (
          <div key={idx} className="flex items-center justify-between rounded-lg border border-border px-3 py-2.5 bg-secondary/20 group">
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold truncate">{lok.vagttype || lok.navn}</p>
              <p className="text-[13px] text-muted-foreground truncate">{lok.adresse || `${lok.lat}, ${lok.lng}`} · {lok.radius}m</p>
            </div>
            <button onClick={() => handleDelete(idx)}
              className="ml-3 p-1 rounded text-muted-foreground hover:text-red-500 hover:bg-red-500/10 opacity-0 group-hover:opacity-100 transition-all shrink-0">
              <Trash2 className="w-3.5 h-3.5" />
            </button>
          </div>
        ))}
      </div>
      <button onClick={() => setShowModal(true)}
        className="flex items-center gap-1.5 text-sm text-[#FF2A6D] hover:opacity-80 transition-opacity">
        <Plus className="w-4 h-4" /> Tilføj lokation
      </button>
    </div>
  );
}

// ─── "Tæl ikke timer"-toggle (Opf. #59, DEL A) ──────────────────────────────
// Placeres øverst i Rettigheder-boksen. Gemmer øjeblikkeligt via stamdata-PUT.
function TaelIkkeTimerToggle({ aktiv, onToggle }) {
  return (
    <div className={`rounded-lg border px-3 py-2.5 mb-3 transition-colors ${aktiv ? "border-[#FF2A6D] bg-[#FF2A6D]/10" : "border-border bg-secondary/20"}`}>
      <label className="flex items-center gap-2 cursor-pointer select-none group">
        <input type="checkbox" checked={aktiv} onChange={e => onToggle(e.target.checked)}
          className="w-4 h-4 rounded border-border bg-secondary accent-[#FF2A6D] cursor-pointer" />
        <span className={`text-sm font-semibold ${aktiv ? "text-[#FF2A6D]" : "text-foreground"}`}>Tæl ikke timer</span>
        {aktiv && (
          <span className="inline-flex items-center gap-1 text-[12px] text-muted-foreground ml-auto group-hover:text-[#FF2A6D] transition-colors"
            title="Slå fra for at tælle timer igen">
            <RotateCcw className="w-3.5 h-3.5" /> Tæl timer igen
          </span>
        )}
      </label>
      <p className="text-[12px] text-muted-foreground mt-1.5">
        Medarbejderen vises ikke i Vagtplan og tæller ikke i timeoversigter, Lønperiode-saldo, +/- timer.
      </p>
    </div>
  );
}

// ─── Rettigheder sektion ─────────────────────────────────────────────────────
// Kun synlig for superadmins. Bruger fælles RettighedsEditor med debounced gem.
function RettighedSektion({ itemId, token, aktiveRettigheder: rettigheder, onUpdated, rolle: rolleProp, brugerNavn, topSlot, primaerAfdeling = "" }) {
  const rolle = rolleProp || "member";
  const [saving, setSaving] = useState(false);
  const debounceRef = useRef(null);

  const handleChange = (dict) => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      setSaving(true);
      try {
        await axios.put(`${API}/admin/medarbejdere/${itemId}/rettigheder`, { rettigheder: dict }, {
          headers: { Authorization: `Bearer ${token}` },
        });
        onUpdated && onUpdated();
      } catch {
        alert("Kunne ikke gemme rettighed");
      } finally {
        setSaving(false);
      }
    }, 400);
  };

  useEffect(() => () => { if (debounceRef.current) clearTimeout(debounceRef.current); }, []);

  return (
    <div className="rounded-xl border border-border p-5">
      <h3 className="text-[13px] font-bold text-muted-foreground uppercase tracking-wider mb-3 flex items-center gap-1.5">
        <Shield className="w-3.5 h-3.5" /> Rettigheder {saving && <Loader2 className="w-3 h-3 animate-spin ml-1" />}
      </h3>
      {topSlot}
      <RettighedsEditor rolle={rolle} vaerdier={rettigheder} onChange={handleChange} token={token} brugerNavn={brugerNavn}
        itemId={itemId} primaerAfdeling={primaerAfdeling} onRolleChange={() => onUpdated && onUpdated()} />
    </div>
  );
}

// ─── Feriebank ───────────────────────────────────────────────────────────────
function FerieBank({ itemId, token, isSuperAdmin }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [visAntal, setVisAntal] = useState(10);
  const [nySaldoInput, setNySaldoInput] = useState("");
  const [beskrivelse, setBeskrivelse] = useState("");
  const [gemmer, setGemmer] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    axios.get(`${API}/medarbejdere/${itemId}/feriebank`, {
      headers: { Authorization: `Bearer ${token}` },
    }).then(r => {
      setData(r.data);
      const s = Number(r.data?.saldo || 0);
      setNySaldoInput(s.toFixed(2).replace(".", ","));
    })
      .catch(e => setError(e.response?.data?.detail || "Kunne ikke hente feriebank"))
      .finally(() => setLoading(false));
  }, [itemId, token]);

  useEffect(() => { load(); }, [load]);

  const fmt = (n) => Number(n || 0).toLocaleString("da-DK", { maximumFractionDigits: 2 });
  const fmtSigned = (n) => {
    const v = Number(n || 0);
    const sign = v > 0 ? "+" : v < 0 ? "−" : "";
    return `${sign}${fmt(Math.abs(v))}`;
  };
  const fmtDato = (s) => {
    if (!s) return "";
    const [y, m, d] = String(s).split("-");
    if (!y || !m || !d) return s;
    return `${d}.${m}.${y}`;
  };

  const gemSaldo = async () => {
    const num = parseFloat(String(nySaldoInput).replace(",", "."));
    if (Number.isNaN(num)) { alert("Indtast et gyldigt tal for saldo"); return; }
    const gammel = Number(data?.saldo || 0);
    const delta = Math.round((num - gammel) * 100) / 100;
    if (delta === 0) { alert("Ny saldo er identisk med nuværende — ingen ændring at gemme"); return; }
    if (Math.abs(delta) > 5) {
      const deltaTxt = `${delta > 0 ? "+" : "−"}${fmt(Math.abs(delta))}`;
      const ok = window.confirm(`Du ændrer saldoen med ${deltaTxt} dage. Er du sikker?`);
      if (!ok) return;
    }
    setGemmer(true);
    try {
      await axios.post(`${API}/medarbejdere/${itemId}/feriebank/set-saldo`,
        { ny_saldo: num, beskrivelse: beskrivelse.trim() },
        { headers: { Authorization: `Bearer ${token}` } });
      setBeskrivelse("");
      load();
    } catch (e) {
      alert(e.response?.data?.detail || "Kunne ikke gemme saldo");
    }
    setGemmer(false);
  };

  if (loading) {
    return (
      <div className="rounded-xl border border-border p-5 flex items-center justify-center">
        <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
      </div>
    );
  }
  if (error) {
    return <div className="rounded-xl border border-border p-5 text-sm text-red-500">{error}</div>;
  }

  const historik = data?.historik || [];
  const synlige = historik.slice(0, visAntal);

  return (
    <div className="flex flex-col gap-5">
      {/* SALDO */}
      <div className="rounded-xl border border-border p-5">
        <h3 className="text-[13px] font-bold text-muted-foreground uppercase tracking-wider mb-4">Saldo</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
          <div>
            <p className="text-3xl font-bold text-foreground">
              {fmt(data?.saldo)} <span className="text-base font-medium text-muted-foreground">feriedage til gode</span>
            </p>
          </div>
          <div className="sm:text-right">
            <p className="text-[13px] text-muted-foreground mb-0.5">Forventet ved udgangen af året</p>
            <p className="text-xl font-semibold text-foreground">{fmt(data?.forventet_aar_slut)} dage</p>
          </div>
        </div>
      </div>

      {/* JUSTERING — kun superadmin. Direkte saldo-indtastning. */}
      {isSuperAdmin && (() => {
        const parsed = parseFloat(String(nySaldoInput).replace(",", "."));
        const gammel = Number(data?.saldo || 0);
        const delta = !Number.isNaN(parsed) ? Math.round((parsed - gammel) * 100) / 100 : 0;
        const stor = Math.abs(delta) > 5;
        return (
          <div className="rounded-xl border border-border p-5">
            <h3 className="text-[13px] font-bold text-muted-foreground uppercase tracking-wider mb-3">Justering</h3>
            <div className="flex flex-col gap-3">
              <div className="grid grid-cols-1 sm:grid-cols-[180px_1fr_auto] gap-3 items-end">
                <div>
                  <p className="text-[13px] text-muted-foreground mb-0.5">Ny saldo (feriedage)</p>
                  <input
                    type="text"
                    inputMode="decimal"
                    value={nySaldoInput}
                    onChange={e => setNySaldoInput(e.target.value)}
                    placeholder="0,00"
                    className="w-full bg-secondary border border-border rounded-lg px-3 h-9 text-sm text-foreground focus:outline-none focus:ring-1 focus:ring-[#FF2A6D]"
                  />
                </div>
                <div>
                  <p className="text-[13px] text-muted-foreground mb-0.5">Beskrivelse (valgfri)</p>
                  <input
                    type="text"
                    value={beskrivelse}
                    onChange={e => setBeskrivelse(e.target.value)}
                    placeholder="Fx. Overført fra 2025, korrektion…"
                    className="w-full bg-secondary border border-border rounded-lg px-3 h-9 text-sm text-foreground focus:outline-none focus:ring-1 focus:ring-[#FF2A6D]"
                  />
                </div>
                <button
                  onClick={gemSaldo}
                  disabled={gemmer || delta === 0 || Number.isNaN(parsed)}
                  className="h-9 flex items-center gap-1.5 px-4 rounded-full text-sm font-semibold bg-[#FF2A6D] text-white hover:bg-[#e02560] transition-colors disabled:opacity-50"
                >
                  {gemmer ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                  Gem ny saldo
                </button>
              </div>
              {!Number.isNaN(parsed) && delta !== 0 && (
                <p className={`text-[13px] ${stor ? "text-amber-500" : "text-muted-foreground"}`}>
                  {stor && <AlertTriangle className="inline w-3.5 h-3.5 mr-1 -mt-0.5" />}
                  Ændring: <span className={`font-mono font-semibold ${delta > 0 ? "text-green-500" : "text-red-500"}`}>{fmtSigned(delta)}</span> dage
                  {stor && " — kræver bekræftelse"}
                </p>
              )}
            </div>
          </div>
        );
      })()}

      {/* HISTORIK */}
      <div className="rounded-xl border border-border p-5">
        <h3 className="text-[13px] font-bold text-muted-foreground uppercase tracking-wider mb-3">Historik</h3>
        {historik.length === 0 && (
          <p className="text-sm text-muted-foreground italic">Ingen poster endnu.</p>
        )}
        {historik.length > 0 && (
          <div className="flex flex-col">
            {synlige.map((h, i) => {
              const v = Number(h.aendring || 0);
              const farve = v > 0 ? "text-green-500" : v < 0 ? "text-red-500" : "text-foreground";
              return (
                <div key={h.id || i}
                  className="flex items-center gap-3 py-2 border-b border-border last:border-b-0 text-sm">
                  <span className="w-24 shrink-0 text-muted-foreground font-mono">{fmtDato(h.dato)}</span>
                  <span className={`w-20 shrink-0 font-mono font-semibold ${farve}`}>{fmtSigned(v)}</span>
                  {h.type === "auto" && (
                    <span className="text-[13px] px-2 py-0.5 rounded-full bg-secondary text-muted-foreground shrink-0">auto</span>
                  )}
                  <span className="flex-1 min-w-0 truncate text-foreground">
                    {h.beskrivelse || ""}
                    {h.oprettet_af && h.oprettet_af !== "system" && (
                      <span className="text-muted-foreground"> · {h.oprettet_af}</span>
                    )}
                  </span>
                </div>
              );
            })}
            {historik.length > visAntal && (
              <button onClick={() => setVisAntal(n => n + 10)}
                className="mt-3 flex items-center gap-1.5 text-sm text-[#FF2A6D] hover:text-[#e02560] transition-colors self-start">
                <ChevronDown className="w-4 h-4" /> Vis flere
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}


// ─── Stamdata-sammenfatning: feriesaldo, skemalagte timer, stempel-saldo ────
// Read-only widgets der vises på Stamdata-fanen. Henter data fra eksisterende
// backend-endpoints (feriebank, vagtplan, clock-records) og beregner saldi.
function StamdataOversigtKort({ itemId, token, medarbejderNavn, kontraktTimerPrUge, isSuperAdmin, kanSeFortrolige }) {
  const [feriedata, setFeriedata] = useState(null);
  const [vagter, setVagter] = useState([]);
  const [stemplinger, setStemplinger] = useState([]);
  const [syg, setSyg] = useState(null);
  const [sygTooltipOpen, setSygTooltipOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [sygReloadKey, setSygReloadKey] = useState(0);
  const [showSygHistorikModal, setShowSygHistorikModal] = useState(false);
  const [sygFra, setSygFra] = useState("");
  const [sygTil, setSygTil] = useState("");
  const [sygType, setSygType] = useState("sygdom");
  const [sygNote, setSygNote] = useState("");
  const [sygSaving, setSygSaving] = useState(false);

  // Periode 20.-19. (samme logik som TimerFane)
  const periode = useMemo(() => {
    const now = new Date();
    const year = now.getFullYear();
    const month = now.getMonth() + 1;
    const day = now.getDate();
    // Hvis i dag <= 19, går perioden fra 20. i forrige måned til 19. denne.
    // Ellers fra 20. denne måned til 19. næste måned.
    const pad = (n) => String(n).padStart(2, "0");
    let fraY, fraM, tilY, tilM;
    if (day <= 19) {
      tilY = year; tilM = month;
      fraM = month === 1 ? 12 : month - 1;
      fraY = month === 1 ? year - 1 : year;
    } else {
      fraY = year; fraM = month;
      tilM = month === 12 ? 1 : month + 1;
      tilY = month === 12 ? year + 1 : year;
    }
    return { fra: `${fraY}-${pad(fraM)}-20`, til: `${tilY}-${pad(tilM)}-19` };
  }, []);

  // Indeværende uge (mandag–søndag)
  const uge = useMemo(() => {
    const today = new Date();
    const dow = today.getDay();
    const mon = new Date(today);
    mon.setDate(today.getDate() - (dow === 0 ? 6 : dow - 1));
    const son = new Date(mon);
    son.setDate(mon.getDate() + 6);
    return {
      fra: mon.toISOString().slice(0, 10),
      til: son.toISOString().slice(0, 10),
    };
  }, []);

  useEffect(() => {
    let aktiv = true;
    setLoading(true);
    const hdrs = { headers: { Authorization: `Bearer ${token}` } };

    const reqs = [
      axios.get(`${API}/medarbejdere/${itemId}/feriebank`, hdrs).catch(() => null),
    ];
    if (medarbejderNavn) {
      reqs.push(
        axios.get(`${API}/hub/vagtplan`, { ...hdrs, params: { medarbejder: medarbejderNavn } }).catch(() => null)
      );
      reqs.push(
        axios.get(`${API}/admin/clock-records`, { ...hdrs, params: { medarbejder: medarbejderNavn, fra: periode.fra, til: periode.til } }).catch(() => null)
      );
    }

    Promise.all(reqs).then(([f, v, c]) => {
      if (!aktiv) return;
      if (f) setFeriedata(f.data);
      if (v) setVagter(v.data?.vagter || v.data || []);
      if (c) setStemplinger(c.data?.records || c.data || []);
      setLoading(false);
    });
    return () => { aktiv = false; };
  }, [itemId, token, medarbejderNavn, periode.fra, periode.til]);

  // Sygefravær 12 mdr. — gated på rettigheden kan_se_fortrolige_medarbejderoplysninger.
  useEffect(() => {
    if (!itemId || !kanSeFortrolige) return;
    let aktiv = true;
    axios.get(`${API}/admin/medarbejdere/${itemId}/sygefravaer-12mdr`, {
      headers: { Authorization: `Bearer ${token}` },
    }).then(r => { if (aktiv) setSyg(r.data); })
      .catch(() => { if (aktiv) setSyg(null); });
    return () => { aktiv = false; };
  }, [itemId, token, kanSeFortrolige, sygReloadKey]);

  // Beregn skemalagte timer denne uge + i lønperioden.
  const ugeTimer = useMemo(() => {
    return vagter
      .filter(v => v.dato >= uge.fra && v.dato <= uge.til)
      .reduce((sum, v) => sum + (parseFloat(v.timer) || 0), 0);
  }, [vagter, uge]);

  const periodeTimer = useMemo(() => {
    return vagter
      .filter(v => v.dato >= periode.fra && v.dato <= periode.til)
      .reduce((sum, v) => sum + (parseFloat(v.timer) || 0), 0);
  }, [vagter, periode]);

  // Stempel-saldo: faktiske stempeltimer i lønperioden minus kontrakttimer for samme periode.
  const stempelSaldo = useMemo(() => {
    const faktiske = stemplinger.reduce((s, r) => s + (parseFloat(r.timer) || parseFloat(r.faktiske_timer) || 0), 0);
    // Kontrakttimer for perioden = timer_pr_uge × (antal dage i perioden / 7).
    if (!kontraktTimerPrUge) return { faktiske, forventet: 0, saldo: 0 };
    const dage = (new Date(periode.til) - new Date(periode.fra)) / 86400000 + 1;
    const forventet = +(kontraktTimerPrUge * (dage / 7)).toFixed(1);
    return { faktiske: +faktiske.toFixed(1), forventet, saldo: +(faktiske - forventet).toFixed(1) };
  }, [stemplinger, kontraktTimerPrUge, periode]);

  if (loading) {
    return (
      <div className="rounded-xl border border-border p-5 flex items-center gap-2 text-sm text-muted-foreground">
        <Loader2 className="w-4 h-4 animate-spin" /> Henter sammenfatning...
      </div>
    );
  }

  const fmtT = (h) => {
    if (!h && h !== 0) return "—";
    return h.toFixed(1).replace(".", ",") + "t";
  };

  // Dansk datoformat (DD.MM.YYYY) — bruges i sygefravær-tooltip.
  const fmtDK = (iso) => {
    if (!iso || typeof iso !== "string") return "";
    const [y, m, d] = iso.split("-");
    return `${d}.${m}.${y}`;
  };
  const fmtEnhed = (n, enhed) => {
    const num = Number(n || 0);
    const str = (enhed === "timer" ? num.toFixed(1) : num.toFixed(num % 1 === 0 ? 0 : 1)).replace(".", ",");
    return `${str} ${enhed}`;
  };
  const visSyg = kanSeFortrolige && syg;
  const sygGridCls = visSyg
    ? "grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4"
    : "grid grid-cols-2 sm:grid-cols-4 gap-4";

  const aabnSygHistorikModal = () => {
    const i_dag = new Date().toISOString().slice(0, 10);
    setSygFra(i_dag);
    setSygTil(i_dag);
    setSygType("sygdom");
    setSygNote("");
    setShowSygHistorikModal(true);
  };

  const haandterFraDato = (ny) => {
    setSygTil((cur) => (!cur || cur === sygFra ? ny : cur));
    setSygFra(ny);
  };

  const gemSygHistorik = async () => {
    if (!sygFra) return;
    setSygSaving(true);
    try {
      await axios.post(
        `${API}/admin/medarbejdere/${itemId}/syg-historik`,
        { fra_dato: sygFra, til_dato: sygTil || sygFra, type: sygType, note: sygNote },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setShowSygHistorikModal(false);
      setSygReloadKey((k) => k + 1);
    } catch (e) {
      alert(e?.response?.data?.detail || "Kunne ikke gemme");
    }
    setSygSaving(false);
  };

  return (
    <div className="rounded-xl border border-border p-6">
      <h3 className="text-[13px] font-bold text-muted-foreground uppercase tracking-wider mb-4">Sammenfatning</h3>
      <div className={sygGridCls}>
        <div>
          <p className="text-[13px] text-muted-foreground mb-0.5">Feriedage til gode</p>
          <p className="text-lg font-bold">{feriedata != null ? Number(feriedata.saldo || 0).toFixed(2).replace(".", ",") : "—"}</p>
          {feriedata?.forventet_aar_slut != null && (
            <p className="text-[12px] text-muted-foreground mt-0.5">Forventet årsslut: {Number(feriedata.forventet_aar_slut).toFixed(1).replace(".", ",")}</p>
          )}
        </div>
        <div>
          <p className="text-[13px] text-muted-foreground mb-0.5">Skemalagt denne uge</p>
          <p className="text-lg font-bold">{fmtT(ugeTimer)}</p>
          {kontraktTimerPrUge > 0 && (
            <p className="text-[12px] text-muted-foreground mt-0.5">Kontrakt: {kontraktTimerPrUge}t/uge</p>
          )}
        </div>
        <div>
          <p className="text-[13px] text-muted-foreground mb-0.5">Skemalagt lønperiode</p>
          <p className="text-lg font-bold">{fmtT(periodeTimer)}</p>
          <p className="text-[12px] text-muted-foreground mt-0.5">20.{periode.fra.slice(5, 7)}–19.{periode.til.slice(5, 7)}</p>
        </div>
        <div>
          <p className="text-[13px] text-muted-foreground mb-0.5">Stempel-saldo</p>
          <p className={`text-lg font-bold ${stempelSaldo.saldo >= 0 ? "text-green-500" : "text-red-500"}`}>
            {stempelSaldo.saldo >= 0 ? "+" : ""}{fmtT(stempelSaldo.saldo)}
          </p>
          {kontraktTimerPrUge > 0 && (
            <p className="text-[12px] text-muted-foreground mt-0.5">
              {fmtT(stempelSaldo.faktiske)} / {fmtT(stempelSaldo.forventet)}
            </p>
          )}
        </div>
        {visSyg && (
          <div
            className="relative"
            onMouseEnter={() => setSygTooltipOpen(true)}
            onMouseLeave={() => setSygTooltipOpen(false)}
          >
            <p className="text-[13px] text-muted-foreground mb-0.5">Sygefravær 12 mdr.</p>
            <p className="text-lg font-bold cursor-help">
              {fmtEnhed(syg.total, syg.enhed)}
            </p>
            <p className="text-[12px] text-muted-foreground mt-0.5">
              {Number(syg.total) === 0
                ? "Ingen sygedage"
                : `${syg.antal_perioder} ${syg.antal_perioder === 1 ? "periode" : "perioder"}`}
            </p>
            {kanSeFortrolige && (
              <button
                type="button"
                onClick={aabnSygHistorikModal}
                className="mt-1 text-[13px] text-[#FF2A6D] font-semibold hover:underline"
              >
                + Tilføj historisk sygedag
              </button>
            )}
            {sygTooltipOpen && syg.perioder?.length > 0 && (
              <div className="absolute z-20 top-full left-0 mt-2 w-72 max-h-80 overflow-y-auto rounded-xl border border-border bg-background shadow-lg p-3">
                <p className="text-[13px] font-bold text-muted-foreground uppercase tracking-wider mb-2">
                  Perioder (seneste 12 mdr.)
                </p>
                <ul className="flex flex-col gap-1.5">
                  {syg.perioder.map((p, i) => {
                    const labelDato = p.fra === p.til
                      ? fmtDK(p.fra)
                      : `${fmtDK(p.fra)} – ${fmtDK(p.til)}`;
                    const labelVarighed = syg.enhed === "timer"
                      ? `${Number(p.timer).toFixed(1).replace(".", ",")} timer`
                      : `${p.dage} ${p.dage === 1 ? "dag" : "dage"}`;
                    return (
                      <li key={i} className="flex justify-between gap-3 text-[13px]">
                        <span className="text-foreground">
                          {labelDato}
                          {p.manuel && (
                            <span className="ml-1 text-muted-foreground">(manuelt)</span>
                          )}
                        </span>
                        <span className="text-muted-foreground shrink-0">{labelVarighed}</span>
                      </li>
                    );
                  })}
                </ul>
              </div>
            )}
          </div>
        )}
      </div>
      {showSygHistorikModal && (
        <ModalPortal onClose={() => setShowSygHistorikModal(false)}>
          <div
            className="bg-background border border-border rounded-2xl shadow-2xl w-full max-w-md p-5"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="font-bold text-base mb-4">Tilføj historisk sygedag</h3>

            <div className="grid grid-cols-2 gap-3 mb-4">
              <div>
                <label className="text-[13px] text-muted-foreground mb-1 block">Fra dato</label>
                <input
                  type="date"
                  value={sygFra}
                  onChange={(e) => haandterFraDato(e.target.value)}
                  className="w-full bg-secondary border border-border rounded-lg px-3 h-9 text-sm"
                />
              </div>
              <div>
                <label className="text-[13px] text-muted-foreground mb-1 block">Til dato</label>
                <input
                  type="date"
                  value={sygTil}
                  min={sygFra || undefined}
                  onChange={(e) => setSygTil(e.target.value)}
                  className="w-full bg-secondary border border-border rounded-lg px-3 h-9 text-sm"
                />
              </div>
            </div>

            <div className="mb-4">
              <label className="text-[13px] text-muted-foreground mb-2 block">Type</label>
              <div className="flex gap-4">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="syg-type"
                    checked={sygType === "sygdom"}
                    onChange={() => setSygType("sygdom")}
                  />
                  <span className="text-sm">Sygdom</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="syg-type"
                    checked={sygType === "barnets_foerste_sygedag"}
                    onChange={() => setSygType("barnets_foerste_sygedag")}
                  />
                  <span className="text-sm">Barnets første sygedag</span>
                </label>
              </div>
            </div>

            <div className="mb-5">
              <label className="text-[13px] text-muted-foreground mb-1 block">Note (valgfri)</label>
              <textarea
                value={sygNote}
                onChange={(e) => setSygNote(e.target.value)}
                className="w-full bg-secondary border border-border rounded-lg px-3 py-2 text-sm min-h-[60px]"
                placeholder="Fx hvor data kommer fra"
              />
            </div>

            <div className="flex justify-end gap-2">
              <button
                onClick={() => setShowSygHistorikModal(false)}
                className="px-4 py-2 rounded-lg text-sm border border-border hover:bg-secondary"
              >
                Annullér
              </button>
              <button
                onClick={gemSygHistorik}
                disabled={sygSaving || !sygFra}
                className="px-4 py-2 rounded-lg text-sm font-semibold bg-[#FF2A6D] text-white hover:opacity-90 disabled:opacity-50"
              >
                {sygSaving ? "Gemmer..." : "Gem"}
              </button>
            </div>
          </div>
        </ModalPortal>
      )}
    </div>
  );
}


// ─── Fane-navigation (som Hub) ───────────────────────────────────────────────
// Topfanen "Timer" er fjernet — data findes i vagtplan-modulet, og ugeskema/saldo
// vises nu direkte på Stamdata. Dokumenter er flyttet ind under "Ansættelse".
// ─── Arbejdsplan-fane (Opfølgning #37) ──────────────────────────────────────
// Medarbejderens egen arbejdsplan: denne uge, kommende vagter, lønperiode-saldo,
// ferie (med "Anmod om ferie" flyttet hertil) og sygefravær. Vises for
// medarbejderen selv, for admins, og for vagtplan-rettighedshavere.
const UGEDAGE_KORT = ["man", "tir", "ons", "tor", "fre", "lør", "søn"];
function isoDag(d) { return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,"0")}-${String(d.getDate()).padStart(2,"0")}`; }
function ugeNr(iso) {
  const d = new Date(iso + "T00:00:00");
  const thu = new Date(d); thu.setDate(d.getDate() + 3 - ((d.getDay()+6)%7));
  const jan1 = new Date(thu.getFullYear(), 0, 1);
  return Math.ceil(((thu - jan1) / 86400000 + 1) / 7);
}
// #44.2: betalt fravær (Syg/Ferie/Fri/Barnets/Læge*). #60: disse TÆLLER nu som
// almindelige timer i ALLE summer (for månedsløn behandles de som Normal timer).
// Settet beholdes KUN til visuel markering (amber-chip), ikke til at ekskludere.
const FRAVAERS_TYPER_MP = new Set(["Syg", "Ferie", "Fri", "Lægeeksamination", "Lægeundervisning", "Barnets første sygedag"]);
// Opf. #36-regel genbrugt: stempling overtager planlagt vagt. #60: fravær-timer
// medregnes (ingen ekskludering).
function dagTimerMP(vagter = [], stempler = []) {
  if (stempler.length > 0) return stempler.reduce((s, r) => s + (parseFloat(r.timer_actual) || parseFloat(r.timer) || 0), 0);
  return vagter.reduce((s, v) => s + (parseFloat(v.timer) || 0), 0);
}

// ─── Kørselsregistrering ─────────────────────────────────────────────────────
// Lønperiode 20.–19.: en kørsels dato hører til perioden hvis dato>=20 → perioden
// starter den måned; ellers forrige måned. periode_key = slut-månedens "YYYY-MM".
// (Skal matche backendens _koersel_periode.)
function koerselPeriode(d) {
  const dt = d instanceof Date ? d : new Date((d || "") + "T00:00:00");
  const pad = (n) => String(n).padStart(2, "0");
  let year = dt.getFullYear(), month = dt.getMonth() + 1; // 1-12
  const day = dt.getDate();
  let fraY, fraM, tilY, tilM;
  if (day >= 20) {
    fraY = year; fraM = month;
    tilM = month === 12 ? 1 : month + 1; tilY = month === 12 ? year + 1 : year;
  } else {
    tilY = year; tilM = month;
    fraM = month === 1 ? 12 : month - 1; fraY = month === 1 ? year - 1 : year;
  }
  const fra = `${fraY}-${pad(fraM)}-20`;
  const til = `${tilY}-${pad(tilM)}-19`;
  const key = `${tilY}-${pad(tilM)}`; // slut-måned
  return { fra, til, key };
}

// Tilføj/redigér/"kør tilbage"-dialog. `initial` præudfylder felterne.
function KoerselModal({ token, itemId, medarbejderNavn, initial, onClose, onSaved }) {
  const idag = isoDag(new Date());
  const erRediger = !!initial?.id;
  const [dato, setDato] = useState(initial?.dato || idag);
  const [fraAdresse, setFraAdresse] = useState(initial?.fra_adresse || "");
  const [tilAdresse, setTilAdresse] = useState(initial?.til_adresse || "");
  const [km, setKm] = useState(initial?.km != null ? String(initial.km) : "");
  const [note, setNote] = useState(initial?.note || "");
  const [formaal, setFormaal] = useState(initial?.formaal || "");
  const [saving, setSaving] = useState(false);
  const [fejl, setFejl] = useState("");

  const gem = async () => {
    setFejl("");
    if (!dato) { setFejl("Vælg en dato."); return; }
    if (!fraAdresse.trim() || !tilAdresse.trim()) { setFejl("Udfyld fra- og til-adresse."); return; }
    if (!formaal.trim()) { setFejl("Formål er påkrævet."); return; }
    const kmNum = parseFloat(String(km).replace(",", "."));
    if (!(kmNum > 0)) { setFejl("Angiv antal km (større end 0)."); return; }
    setSaving(true);
    try {
      const hdrs = { headers: { Authorization: `Bearer ${token}` } };
      if (erRediger) {
        await axios.put(`${API}/admin/koersel/${initial.id}`, {
          dato, fra_adresse: fraAdresse.trim(), til_adresse: tilAdresse.trim(), km: kmNum, note: note.trim(), formaal: formaal.trim(),
        }, hdrs);
      } else {
        await axios.post(`${API}/admin/koersel`, {
          medarbejder_item_id: itemId, dato,
          fra_adresse: fraAdresse.trim(), til_adresse: tilAdresse.trim(), km: kmNum, note: note.trim(), formaal: formaal.trim(),
        }, hdrs);
      }
      onSaved();
    } catch (e) {
      setFejl(e?.response?.data?.detail || "Kunne ikke gemme kørsel.");
      setSaving(false);
    }
  };

  return (
    <ModalPortal onClose={onClose}>
      <div className="bg-background border border-border rounded-2xl shadow-2xl w-full max-w-md">
        <div className="flex items-center justify-between px-5 py-4 border-b border-border">
          <h2 className="font-semibold text-sm flex items-center gap-1.5">
            <Car className="w-4 h-4 text-[#FF2A6D]" /> {erRediger ? "Redigér kørsel" : "Tilføj kørsel"}
          </h2>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground"><X className="w-4 h-4" /></button>
        </div>
        <div className="p-5 flex flex-col gap-4">
          <div>
            <label className="text-[13px] text-muted-foreground mb-1 block">Dato</label>
            <input type="date" value={dato} onChange={e => setDato(e.target.value)}
              className="w-full bg-secondary border border-border rounded-lg px-3 h-9 text-sm focus:outline-none focus:ring-1 focus:ring-[#FF2A6D]" />
          </div>
          <div>
            <label className="text-[13px] text-muted-foreground mb-1 flex items-center gap-1.5"><MapPin className="w-3.5 h-3.5" /> Fra (adresse)</label>
            <input type="text" value={fraAdresse} onChange={e => setFraAdresse(e.target.value)} placeholder="Startadresse"
              className="w-full bg-secondary border border-border rounded-lg px-3 h-9 text-sm focus:outline-none focus:ring-1 focus:ring-[#FF2A6D]" />
          </div>
          <div>
            <label className="text-[13px] text-muted-foreground mb-1 flex items-center gap-1.5"><MapPin className="w-3.5 h-3.5" /> Til (adresse)</label>
            <input type="text" value={tilAdresse} onChange={e => setTilAdresse(e.target.value)} placeholder="Slutadresse"
              className="w-full bg-secondary border border-border rounded-lg px-3 h-9 text-sm focus:outline-none focus:ring-1 focus:ring-[#FF2A6D]" />
          </div>
          <div>
            <label className="text-[13px] text-muted-foreground mb-1 block">KM</label>
            <input type="number" step="0.1" min="0" value={km} onChange={e => setKm(e.target.value)} placeholder="0"
              className="w-full bg-secondary border border-border rounded-lg px-3 h-9 text-sm focus:outline-none focus:ring-1 focus:ring-[#FF2A6D]" />
            <p className="text-[12px] text-muted-foreground mt-1">(beregnes evt. automatisk)</p>
          </div>
          <div>
            <label className="text-[13px] text-muted-foreground mb-1 block">Formål <span className="text-[#FF2A6D]">*</span></label>
            <input type="text" value={formaal} onChange={e => setFormaal(e.target.value)}
              placeholder="Fx Kundebesøg, Undervisning, Forretningsmøde"
              className="w-full bg-secondary border border-border rounded-lg px-3 h-9 text-sm focus:outline-none focus:ring-1 focus:ring-[#FF2A6D]" />
          </div>
          <div>
            <label className="text-[13px] text-muted-foreground mb-1 block">Note (valgfri)</label>
            <textarea value={note} onChange={e => setNote(e.target.value)} rows={2}
              className="w-full bg-secondary border border-border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-[#FF2A6D] resize-y" />
          </div>
          {fejl && <p className="text-[13px] text-red-500">{fejl}</p>}
        </div>
        <div className="flex items-center justify-end gap-2 px-5 py-4 border-t border-border">
          <button onClick={onClose} disabled={saving}
            className="px-4 py-2 rounded-lg border border-border text-sm text-muted-foreground hover:bg-secondary transition-colors disabled:opacity-60">
            Annullér
          </button>
          <button onClick={gem} disabled={saving || !formaal.trim()}
            className="flex items-center gap-1.5 px-4 py-2 rounded-lg bg-[#FF2A6D] text-white text-sm font-semibold hover:bg-[#e02560] transition-colors disabled:opacity-60">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />} Gem
          </button>
        </div>
      </div>
    </ModalPortal>
  );
}

// Status-chip for en kørselslinje.
function KoerselStatusChip({ status, aarsag }) {
  if (status === "godkendt") {
    return <span className="inline-flex items-center gap-1 text-[12px] font-semibold px-2 py-0.5 rounded-full bg-green-500/10 text-green-600 dark:text-green-400"><Check className="w-3 h-3" /> Godkendt</span>;
  }
  if (status === "afvist") {
    return (
      <span className="inline-flex items-center gap-1 text-[12px] font-semibold px-2 py-0.5 rounded-full bg-red-500/10 text-red-600 dark:text-red-400"
        title={aarsag || "Afvist"}>
        <AlertTriangle className="w-3 h-3" /> Afvist{aarsag ? ` · ${aarsag}` : ""}
      </span>
    );
  }
  return <span className="inline-flex items-center gap-1 text-[12px] font-semibold px-2 py-0.5 rounded-full bg-amber-500/10 text-amber-600 dark:text-amber-400">Afventer</span>;
}

// KØRSEL-boks på Arbejdsplan-fanen. Vises kun når koersel_aktiveret.
function KoerselBoks({ itemId, token, medarbejderNavn }) {
  const periode = useMemo(() => koerselPeriode(new Date()), []);
  const [koersler, setKoersler] = useState([]);
  const [totalKm, setTotalKm] = useState(0);
  const [loading, setLoading] = useState(true);
  const [visHistorik, setVisHistorik] = useState(false);
  const [historik, setHistorik] = useState([]);
  const [histPeriode, setHistPeriode] = useState(""); // "" = alle
  const [modal, setModal] = useState(null); // {initial} | null

  const fmtDKdato = (iso) => { if (!iso) return ""; const [y, m, d] = String(iso).split("-"); return `${d}.${m}.${y}`; };

  const hentPeriode = useCallback(() => {
    if (!itemId) return;
    setLoading(true);
    axios.get(`${API}/admin/koersel`, {
      headers: { Authorization: `Bearer ${token}` },
      params: { medarbejder_item_id: itemId, periode: periode.key },
    }).then(r => {
      setKoersler(r.data?.koersler || []);
      setTotalKm(r.data?.total_km ?? 0);
    }).catch(() => { setKoersler([]); setTotalKm(0); })
      .finally(() => setLoading(false));
  }, [itemId, token, periode.key]);

  const hentHistorik = useCallback(() => {
    if (!itemId) return;
    axios.get(`${API}/admin/koersel`, {
      headers: { Authorization: `Bearer ${token}` },
      params: { medarbejder_item_id: itemId },
    }).then(r => setHistorik(r.data?.koersler || []))
      .catch(() => setHistorik([]));
  }, [itemId, token]);

  useEffect(() => { hentPeriode(); }, [hentPeriode]);
  useEffect(() => { if (visHistorik) hentHistorik(); }, [visHistorik, hentHistorik]);

  const refetch = () => { hentPeriode(); if (visHistorik) hentHistorik(); };

  const handleSlet = async (row) => {
    const id = row.id || row._id;
    if (!id) return;
    if (!window.confirm("Arkivér denne kørsel?")) return;
    try {
      await axios.delete(`${API}/admin/koersel/${id}`, { headers: { Authorization: `Bearer ${token}` } });
      refetch();
    } catch (e) { alert(e?.response?.data?.detail || "Kunne ikke arkivere kørsel."); }
  };

  const aabnTilfoej = () => setModal({ initial: null });
  const aabnRediger = (row) => setModal({ initial: { ...row, id: row.id || row._id } });
  const aabnKoerTilbage = (row) => setModal({ initial: {
    fra_adresse: row.til_adresse, til_adresse: row.fra_adresse, km: row.km, note: row.note, dato: isoDag(new Date()),
  } });

  // Distinkte lønperioder til historik-filteret.
  const histPerioder = useMemo(() => {
    const s = new Set();
    historik.forEach(r => { if (r.periode_key) s.add(r.periode_key); });
    return Array.from(s).sort().reverse();
  }, [historik]);

  const histVist = useMemo(() => {
    const liste = histPeriode ? historik.filter(r => r.periode_key === histPeriode) : historik;
    return [...liste].sort((a, b) => (b.dato || "").localeCompare(a.dato || ""));
  }, [historik, histPeriode]);

  const renderRaekke = (row, { laast }) => {
    const id = row.id || row._id;
    const kanRedigere = !laast && row.status !== "godkendt";
    return (
      <tr key={id || `${row.dato}-${row.fra_adresse}-${row.til_adresse}`} className="border-t border-border">
        <td className="py-2 px-3 tabular-nums whitespace-nowrap">{fmtDKdato(row.dato)}</td>
        <td className="py-2 px-3 max-w-[160px] truncate" title={row.fra_adresse}>{row.fra_adresse}</td>
        <td className="py-2 px-3 max-w-[160px] truncate" title={row.til_adresse}>{row.til_adresse}</td>
        <td className="py-2 px-3 max-w-[140px] truncate text-muted-foreground" title={row.formaal}>{row.formaal || <span className="italic opacity-50">—</span>}</td>
        <td className="py-2 px-3 tabular-nums whitespace-nowrap">{Number(row.km || 0).toLocaleString("da-DK")} km</td>
        <td className="py-2 px-3"><KoerselStatusChip status={row.status} aarsag={row.afvisning_aarsag} /></td>
        <td className="py-2 px-3">
          <div className="flex items-center justify-end gap-1">
            <button onClick={() => aabnKoerTilbage(row)} title="Kør tilbage (omvendt rute)"
              className="inline-flex items-center gap-1 text-[12px] px-2 py-1 rounded-lg text-muted-foreground hover:text-[#FF2A6D] hover:bg-secondary transition-colors">
              <RotateCcw className="w-3.5 h-3.5" /> Kør tilbage
            </button>
            <button onClick={() => aabnRediger(row)} disabled={!kanRedigere}
              title={kanRedigere ? "Redigér" : "Låst (godkendt eller låst periode)"}
              className="p-1.5 rounded-lg text-muted-foreground hover:text-foreground hover:bg-secondary transition-colors disabled:opacity-30 disabled:hover:bg-transparent">
              <Pencil className="w-3.5 h-3.5" />
            </button>
            <button onClick={() => handleSlet(row)} disabled={!kanRedigere}
              title={kanRedigere ? "Arkivér" : "Låst (godkendt eller låst periode)"}
              className="p-1.5 rounded-lg text-muted-foreground hover:text-red-500 hover:bg-red-500/10 transition-colors disabled:opacity-30 disabled:hover:bg-transparent">
              <Trash2 className="w-3.5 h-3.5" />
            </button>
          </div>
        </td>
      </tr>
    );
  };

  return (
    <div className="rounded-xl border border-border p-5">
      {modal && (
        <KoerselModal token={token} itemId={itemId} medarbejderNavn={medarbejderNavn}
          initial={modal.initial} onClose={() => setModal(null)}
          onSaved={() => { setModal(null); refetch(); }} />
      )}
      <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
        <h3 className="text-[13px] font-bold text-muted-foreground uppercase tracking-wider flex items-center gap-1.5">
          <Car className="w-3.5 h-3.5 text-[#FF2A6D]" /> Kørsel · Lønperiode {fmtDKdato(periode.fra).slice(0, 5)} - {fmtDKdato(periode.til).slice(0, 5)}
        </h3>
        <div className="flex items-center gap-2">
          <button onClick={aabnTilfoej}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-[#FF2A6D] text-white text-[13px] font-semibold hover:bg-[#e02560] transition-colors">
            <Plus className="w-3.5 h-3.5" /> Tilføj kørsel
          </button>
          <button onClick={() => setVisHistorik(v => !v)}
            className="inline-flex items-center gap-1 px-3 py-1.5 rounded-xl border border-border text-[13px] text-muted-foreground hover:text-foreground hover:bg-secondary transition-colors">
            Vis historik {visHistorik ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
          </button>
        </div>
      </div>
      <p className="text-[13px] text-muted-foreground mb-3">
        Total km i perioden: <span className="font-bold text-foreground tabular-nums">{Number(totalKm || 0).toLocaleString("da-DK")} km</span>
      </p>

      {loading ? (
        <div className="flex items-center gap-2 text-sm text-muted-foreground py-3"><Loader2 className="w-4 h-4 animate-spin" /> Henter kørsel…</div>
      ) : koersler.length === 0 ? (
        <p className="text-sm text-muted-foreground py-2">Ingen kørsel registreret i denne periode.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-secondary/40">
                {["Dato", "Fra", "Til", "Formål", "KM", "Status", ""].map((h, i) => (
                  <th key={i} className={`py-2 px-3 text-[12px] font-semibold text-muted-foreground uppercase tracking-wider ${i === 6 ? "text-right" : "text-left"}`}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {koersler.map(row => renderRaekke(row, { laast: !!row.periode_laast }))}
            </tbody>
          </table>
        </div>
      )}

      {visHistorik && (
        <div className="mt-4 pt-4 border-t border-border">
          <div className="flex items-center gap-2 mb-2 text-[13px]">
            <History className="w-3.5 h-3.5 text-muted-foreground" />
            <span className="font-semibold text-muted-foreground uppercase tracking-wider text-[12px]">Historik</span>
            <select value={histPeriode} onChange={e => setHistPeriode(e.target.value)}
              className="ml-auto bg-secondary border border-border rounded-lg px-2 h-8 text-[13px] focus:outline-none focus:ring-1 focus:ring-[#FF2A6D]">
              <option value="">Alle lønperioder</option>
              {histPerioder.map(p => <option key={p} value={p}>{p}</option>)}
            </select>
          </div>
          {histVist.length === 0 ? (
            <p className="text-sm text-muted-foreground py-2">Ingen kørsel i historikken.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-secondary/40">
                    {["Dato", "Fra", "Til", "KM", "Status", ""].map((h, i) => (
                      <th key={i} className={`py-2 px-3 text-[12px] font-semibold text-muted-foreground uppercase tracking-wider ${i === 5 ? "text-right" : "text-left"}`}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {histVist.map(row => renderRaekke(row, { laast: !!row.periode_laast }))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function ArbejdsplanFane({ itemId, token, medarbejderNavn, kontraktTimerPrUge, erEgenProfil, kanSeFortrolige, isSuperAdmin, ansaettelsestype, ansaettelsesdato, taelIkkeTimer, koerselAktiveret, onToggleTaelIkke }) {
  const [feriedata, setFeriedata] = useState(null);
  const [vagter, setVagter] = useState([]);
  const [stemplinger, setStemplinger] = useState([]);
  const [syg, setSyg] = useState(null);
  const [loading, setLoading] = useState(true);
  const [visUgetabel, setVisUgetabel] = useState(false);
  const [visKommende, setVisKommende] = useState(false); // #39.4: sammenfoldet default
  // #40.4: periode-vælger for "Kommende vagter".
  const [kommendePeriode, setKommendePeriode] = useState("naeste14");
  const [kommendeCustomFra, setKommendeCustomFra] = useState("");
  const [kommendeCustomTil, setKommendeCustomTil] = useState("");
  const [showFerieModal, setShowFerieModal] = useState(false);
  const [showFerieJuster, setShowFerieJuster] = useState(false); // #39.3: blyant-dialog
  const [showSidsteAar, setShowSidsteAar] = useState(false); // #39.5: redigér sidste år
  const [mineFerier, setMineFerier] = useState([]);
  // #39.5: kun månedsløn har +/- timer saldo-tracking.
  const erMaanedsloen = (ansaettelsestype || "manedsloen") === "manedsloen";

  const idag = useMemo(() => isoDag(new Date()), []);
  // Lønperiode 20.–19.
  const periode = useMemo(() => {
    const now = new Date();
    const year = now.getFullYear(), month = now.getMonth() + 1, day = now.getDate();
    const pad = (n) => String(n).padStart(2, "0");
    let fraY, fraM, tilY, tilM;
    if (day <= 19) { tilY = year; tilM = month; fraM = month === 1 ? 12 : month - 1; fraY = month === 1 ? year - 1 : year; }
    else { fraY = year; fraM = month; tilM = month === 12 ? 1 : month + 1; tilY = month === 12 ? year + 1 : year; }
    return { fra: `${fraY}-${pad(fraM)}-20`, til: `${tilY}-${pad(tilM)}-19` };
  }, []);
  // Indeværende uge (man-søn)
  const uge = useMemo(() => {
    const today = new Date();
    const mon = new Date(today); mon.setDate(today.getDate() - ((today.getDay()+6)%7));
    const dage = Array.from({ length: 7 }, (_, i) => { const d = new Date(mon); d.setDate(mon.getDate()+i); return isoDag(d); });
    return { fra: dage[0], til: dage[6], dage };
  }, []);

  const hentMineFerier = useCallback(() => {
    if (!erEgenProfil) return;
    axios.get(`${API}/hub/mine-ferie-anmodninger`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => setMineFerier(r.data?.anmodninger || []))
      .catch(() => {});
  }, [erEgenProfil, token]);

  useEffect(() => {
    let aktiv = true;
    setLoading(true);
    const hdrs = { headers: { Authorization: `Bearer ${token}` } };
    // Hent vagter for et bredt vindue (lønperiode-start → +21 dage), så både
    // "denne uge", "lønperiode" og "kommende vagter" er dækket.
    const fraVindue = periode.fra < uge.fra ? periode.fra : uge.fra;
    const tilDato = new Date(); tilDato.setDate(tilDato.getDate() + 21);
    const tilVindue = isoDag(tilDato) > periode.til ? isoDag(tilDato) : periode.til;
    const reqs = [
      axios.get(`${API}/medarbejdere/${itemId}/feriebank`, hdrs).catch(() => null),
    ];
    if (medarbejderNavn) {
      reqs.push(axios.get(`${API}/hub/vagtplan`, { ...hdrs, params: { medarbejder: medarbejderNavn } }).catch(() => null));
      // Egen profil: brug self-endpoint (medarbejder uden godkend-rettighed må
      // ikke kalde /admin/clock-records). Andre: admin-endpoint.
      if (erEgenProfil) {
        reqs.push(axios.get(`${API}/hub/clock/min-historik`, { ...hdrs, params: { fra: fraVindue, til: tilVindue } }).catch(() => null));
      } else {
        reqs.push(axios.get(`${API}/admin/clock-records`, { ...hdrs, params: { medarbejder: medarbejderNavn, fra_dato: fraVindue, til_dato: tilVindue } }).catch(() => null));
      }
    }
    Promise.all(reqs).then(([f, v, c]) => {
      if (!aktiv) return;
      if (f) setFeriedata(f.data);
      if (v) setVagter(v.data?.vagter || v.data || []);
      if (c) setStemplinger(c.data?.records || c.data || []);
      setLoading(false);
    });
    return () => { aktiv = false; };
  }, [itemId, token, medarbejderNavn, periode.fra, periode.til, uge.fra, erEgenProfil]);

  useEffect(() => { hentMineFerier(); }, [hentMineFerier]);

  // #39.5: +/- timer pr. måned for indeværende kalenderår. Henter hele årets
  // vagter + stemplinger (kun for månedsløn; ellers vises boksen ikke).
  const aar = useMemo(() => new Date().getFullYear(), []);
  const [aarVagter, setAarVagter] = useState([]);
  const [aarStempler, setAarStempler] = useState([]);
  const [sidsteAar, setSidsteAar] = useState(null); // {aar, timer} låst saldo
  const [maanedOverride, setMaanedOverride] = useState({}); // #41.3 {"YYYY-MM": timer}
  const [showMaanedJuster, setShowMaanedJuster] = useState(null); // {maaned, beregnet}
  const [flexReloadKey, setFlexReloadKey] = useState(0);
  useEffect(() => {
    if (!erMaanedsloen || !medarbejderNavn) return;
    let aktiv = true;
    const hdrs = { headers: { Authorization: `Bearer ${token}` } };
    const fra = `${aar}-01-01`, til = `${aar}-12-31`;
    const reqs = [
      axios.get(`${API}/hub/vagtplan`, { ...hdrs, params: { medarbejder: medarbejderNavn, fra_dato: fra, til_dato: til } }).catch(() => null),
      (erEgenProfil
        ? axios.get(`${API}/hub/clock/min-historik`, { ...hdrs, params: { fra, til } })
        : axios.get(`${API}/admin/clock-records`, { ...hdrs, params: { medarbejder: medarbejderNavn, fra_dato: fra, til_dato: til } })
      ).catch(() => null),
      axios.get(`${API}/admin/medarbejdere/${itemId}/flex-saldo`, hdrs).catch(() => null),
    ];
    // #44.1: hent det autoritative YTD-saldo fra backend (samme som Timeoversigt).
    // Vi bruger backendens total OG måneds-array, så hele boksen er ét og samme tal.
    reqs.push(axios.get(`${API}/admin/medarbejdere/${itemId}/aarsaldo`, hdrs).catch(() => null));
    Promise.all(reqs).then(([v, c, s, a]) => {
      if (!aktiv) return;
      if (v) setAarVagter(v.data?.vagter || v.data || []);
      if (c) setAarStempler(c.data?.records || c.data || []);
      if (s && s.data?.sidste_aar) setSidsteAar(s.data.sidste_aar);
      if (s) setMaanedOverride(s.data?.maaned_override || {});
      if (a && a.data) setBackendSaldo({ total: a.data.total, months: a.data.months || [] });
    });
    return () => { aktiv = false; };
  }, [erMaanedsloen, medarbejderNavn, itemId, token, aar, erEgenProfil, flexReloadKey]);
  const [backendSaldo, setBackendSaldo] = useState(undefined); // {total, months:[{maaned,value,beregnet,override}]}

  // #40.4: hent vagter for den valgte kommende-periode hvis den rækker ud over
  // det default-indlæste vindue (fx tidligere 30 dage eller en custom periode).
  // #40.4: `vagter` indlæses uden dato-filter (hele medarbejderens vagtplan),
  // så periode-vælgeren kan filtrere direkte uden ekstra kald.

  // Sygefravær 12 mdr. (kun med fortrolig-adgang ELLER egen profil).
  useEffect(() => {
    if (!itemId || !(kanSeFortrolige || erEgenProfil)) return;
    let aktiv = true;
    axios.get(`${API}/admin/medarbejdere/${itemId}/sygefravaer-12mdr`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => { if (aktiv) setSyg(r.data); }).catch(() => { if (aktiv) setSyg(null); });
    return () => { aktiv = false; };
  }, [itemId, token, kanSeFortrolige, erEgenProfil]);

  // Per-dag maps
  const vagtPerDag = useMemo(() => {
    const m = {}; vagter.forEach(v => { (m[v.dato] = m[v.dato] || []).push(v); }); return m;
  }, [vagter]);
  const stemplerPerDag = useMemo(() => {
    const m = {}; stemplinger.forEach(r => { (m[r.dato] = m[r.dato] || []).push(r); }); return m;
  }, [stemplinger]);

  const ugeTimer = useMemo(() =>
    uge.dage.reduce((s, iso) => s + dagTimerMP(vagtPerDag[iso] || [], stemplerPerDag[iso] || []), 0), [uge, vagtPerDag, stemplerPerDag]);

  const periodePlanlagt = useMemo(() =>
    vagter.filter(v => v.dato >= periode.fra && v.dato <= periode.til)
      .reduce((s, v) => s + (parseFloat(v.timer) || 0), 0), [vagter, periode]);
  // #42.5: brug KUN godkendte stemplinger (samme som Timeoversigt), så Lønperiode-
  // boksens forecast altid stemmer med Difference-kolonnen. Records uden status
  // (legacy/importeret) tæller med.
  const erGodkendtStempel = (r) => !r.status || r.status === "approved";
  const periodeStemplet = useMemo(() =>
    stemplinger.filter(r => erGodkendtStempel(r) && r.dato >= periode.fra && r.dato <= periode.til)
      .reduce((s, r) => s + (parseFloat(r.timer_actual) || parseFloat(r.timer) || 0), 0), [stemplinger, periode]);
  const kontraktPeriode = useMemo(() => {
    if (!kontraktTimerPrUge) return 0;
    const dage = (new Date(periode.til) - new Date(periode.fra)) / 86400000 + 1;
    return +(kontraktTimerPrUge * (dage / 7)).toFixed(1);
  }, [kontraktTimerPrUge, periode]);
  // #42.5: planlagt arbejde for RESTERENDE dage (i dag og frem) uden godkendt
  // stempling, så +/- saldo bliver et forecast for hele perioden.
  const periodeRestPlanlagt = useMemo(() => {
    const idagIso = isoDag(new Date());
    const stempletKeys = new Set(
      stemplinger.filter(r => erGodkendtStempel(r) && r.dato >= periode.fra && r.dato <= periode.til).map(r => r.dato));
    return vagter
      .filter(v => v.dato >= periode.fra && v.dato <= periode.til)
      .filter(v => v.dato >= idagIso && !stempletKeys.has(v.dato))
      .reduce((s, v) => s + (parseFloat(v.timer) || 0), 0);
  }, [vagter, stemplinger, periode]);
  // Forecast: stemplet (afsluttede dage) + planlagt (resterende dage) − kontrakt.
  const saldo = +(periodeStemplet + periodeRestPlanlagt - kontraktPeriode).toFixed(1);

  // #40.4: periode-range + label for "Kommende vagter".
  const kommendeRange = useMemo(() => {
    const d0 = new Date();
    const shift = (n) => { const d = new Date(d0); d.setDate(d0.getDate() + n); return isoDag(d); };
    switch (kommendePeriode) {
      case "naeste7":     return { fra: idag, til: shift(7), label: "Kommende vagter", tidligere: false };
      case "naeste30":    return { fra: idag, til: shift(30), label: "Kommende vagter", tidligere: false };
      case "tidligere14": return { fra: shift(-14), til: idag, label: "Tidligere vagter", tidligere: true };
      case "tidligere30": return { fra: shift(-30), til: idag, label: "Tidligere vagter", tidligere: true };
      case "custom":      return { fra: kommendeCustomFra || idag, til: kommendeCustomTil || idag, label: "Vagter", tidligere: false };
      case "naeste14":
      default:            return { fra: idag, til: shift(14), label: "Kommende vagter", tidligere: false };
    }
  }, [kommendePeriode, kommendeCustomFra, kommendeCustomTil, idag]);

  // Vagter i den valgte kommende-periode (filtreres direkte fra hele vagtplanen).
  const kommende = useMemo(() => {
    return vagter.filter(v => v.dato >= kommendeRange.fra && v.dato <= kommendeRange.til)
      .sort((a, b) => (a.dato + (a.fra||"")).localeCompare(b.dato + (b.fra||"")));
  }, [vagter, kommendeRange]);

  // Uge-tabel: gruppér alle vagter pr. ISO-uge (planlagt + stemplet).
  // #46.4: ugetabellen skal dække HELE året (inkl. importerede uger uden for det
  // smalle lønperiode-vindue). vagter/stemplinger er kun et ~3-mdr vindue, mens
  // aarVagter/aarStempler er hele året. Byg per-dag maps fra unionen (dedup pr.
  // vagt_id/clock_id), så importerede stemplinger med faktiske timer ALTID tæller
  // med — præcis som +/- timer-boksen.
  const ugetabelVagtPerDag = useMemo(() => {
    const m = {}; const seen = new Set();
    [...aarVagter, ...vagter].forEach(v => {
      const key = v.vagt_id || `${v.dato}|${v.fra}|${v.til}|${v.medarbejder}`;
      if (seen.has(key)) return; seen.add(key);
      (m[v.dato] = m[v.dato] || []).push(v);
    });
    return m;
  }, [aarVagter, vagter]);
  const ugetabelStemplerPerDag = useMemo(() => {
    const m = {}; const seen = new Set();
    [...aarStempler, ...stemplinger].forEach(r => {
      const key = r.clock_id || r._id || `${r.dato}|${r.clock_in}|${r.clock_out}|${r.medarbejder}`;
      if (seen.has(key)) return; seen.add(key);
      (m[r.dato] = m[r.dato] || []).push(r);
    });
    return m;
  }, [aarStempler, stemplinger]);

  const ugetabel = useMemo(() => {
    const m = {};
    const alleDatoer = new Set([
      ...Object.keys(ugetabelVagtPerDag), ...Object.keys(ugetabelStemplerPerDag),
    ]);
    alleDatoer.forEach(iso => {
      if (!iso) return;
      const d = new Date(iso + "T00:00:00");
      const mon = new Date(d); mon.setDate(d.getDate() - ((d.getDay()+6)%7));
      const monIso = isoDag(mon);
      m[monIso] = m[monIso] || { mandag: monIso, uge: ugeNr(monIso), planlagt: 0, stemplet: 0, importeret: false };
    });
    Object.values(m).forEach(row => {
      for (let i = 0; i < 7; i++) {
        const d = new Date(row.mandag + "T00:00:00"); d.setDate(d.getDate() + i);
        const iso = isoDag(d);
        const dv = ugetabelVagtPerDag[iso] || [];
        const dr = ugetabelStemplerPerDag[iso] || [];
        row.planlagt += dv.reduce((s, v) => s + (parseFloat(v.timer)||0), 0);
        row.stemplet += dr.reduce((s, r) => s + (parseFloat(r.timer_actual)||parseFloat(r.timer)||0), 0);
        // #39.7: marker ugen som importeret hvis nogen vagt/stempling er importeret.
        if (dv.some(v => v.importeret) || dr.some(r => r.importeret)) row.importeret = true;
      }
    });
    return Object.values(m).sort((a, b) => b.mandag.localeCompare(a.mandag));
  }, [ugetabelVagtPerDag, ugetabelStemplerPerDag]);

  // #39.5: +/- timer pr. måned for indeværende år. Pr. måned: stemplede timer
  // (Syg/Ferie/Fri tæller som opfyldt kontrakt = deres planlagte timer) MINUS
  // kontrakttimer for måneden. Fremtidige måneder = null ("-").
  const fravaerMP = FRAVAERS_TYPER_MP;
  const maanedsSaldo = useMemo(() => {
    if (!erMaanedsloen) return null;
    const idagDate = new Date();
    const result = [];
    // Per-dag maps for hele året
    const vPer = {}; aarVagter.forEach(v => { (vPer[v.dato] = vPer[v.dato] || []).push(v); });
    const sPer = {}; aarStempler.forEach(r => { (sPer[r.dato] = sPer[r.dato] || []).push(r); });
    let total = 0; let harData = false;
    for (let mo = 0; mo < 12; mo++) {
      const mKey = `${aar}-${String(mo + 1).padStart(2, "0")}`;
      const first = new Date(aar, mo, 1);
      const last = new Date(aar, mo + 1, 0); // sidste dag i måneden
      const dageIMaaned = last.getDate();
      // Beregn altid det beregnede tal (til "nulstil" + visning).
      let opfyldt = 0;
      for (let d = 1; d <= dageIMaaned; d++) {
        const dd = new Date(aar, mo, d);
        if (dd > idagDate) break; // kun til og med i dag
        const iso = isoDag(dd);
        const dv = vPer[iso] || []; const dr = sPer[iso] || [];
        if (dr.length > 0) {
          opfyldt += dr.reduce((s, r) => s + (parseFloat(r.timer_actual) || parseFloat(r.timer) || 0), 0);
        } else {
          const fravaerTimer = dv.filter(v => fravaerMP.has(v.vagttype)).reduce((s, v) => s + (parseFloat(v.timer) || 0), 0);
          opfyldt += fravaerTimer;
        }
      }
      const slutDag = (idagDate.getFullYear() === aar && idagDate.getMonth() === mo) ? idagDate.getDate() : dageIMaaned;
      const kontraktMaaned = kontraktTimerPrUge ? (kontraktTimerPrUge * (slutDag / 7)) : 0;
      const beregnet = +(opfyldt - kontraktMaaned).toFixed(1);
      // #41.3: manuel override vinder over det beregnede tal (også for fremtidige mdr).
      const harOverride = Object.prototype.hasOwnProperty.call(maanedOverride, mKey);
      const erFremtid = first > idagDate;
      let value;
      if (harOverride) value = +Number(maanedOverride[mKey]).toFixed(1);
      else if (erFremtid) value = null; // fremtidig måned uden override → "-"
      else value = beregnet;
      result.push({ mo, mKey, value, beregnet: erFremtid ? null : beregnet, override: harOverride });
      if (value != null) { total += value; harData = true; }
    }
    return { months: result, total: harData ? +total.toFixed(1) : null };
  }, [erMaanedsloen, aarVagter, aarStempler, aar, kontraktTimerPrUge, fravaerMP, maanedOverride]);

  // #44.1: hele +/- timer-boksen (total OG månedsceller) kommer fra backendens
  // autoritative beregning, så headline, grid og I alt ALDRIG kan divergere — og
  // de er identiske med Timeoversigt-kolonnen. Falder tilbage på client-beregningen
  // hvis backend-svaret mangler (fx offline).
  const visSaldo = useMemo(() => {
    if (backendSaldo && Array.isArray(backendSaldo.months) && backendSaldo.months.length) {
      const months = backendSaldo.months.map(m => {
        const mo = parseInt(String(m.maaned).slice(5, 7), 10) - 1;
        return { mo, mKey: m.maaned, value: m.value, beregnet: m.beregnet, override: m.override };
      });
      return { total: backendSaldo.total, months };
    }
    return maanedsSaldo; // fallback
  }, [backendSaldo, maanedsSaldo]);
  const aarSaldoTotal = visSaldo ? visSaldo.total : null;

  const fmtT = (h) => (h == null || isNaN(h)) ? "—" : (Math.round(h*10)/10).toFixed(1).replace(".", ",") + " t";
  const fmtTsign = (h) => (h == null || isNaN(h)) ? "-" : (h > 0 ? "+" : "") + (Math.round(h*10)/10).toFixed(1).replace(".", ",");
  const fmtDKdato = (iso) => { if (!iso) return ""; const [y,m,d] = iso.split("-"); return `${d}.${m}.${y}`; };
  const MND_KORT = ["JAN","FEB","MAR","APR","MAJ","JUN","JUL","AUG","SEP","OKT","NOV","DEC"];

  if (loading) {
    return <div className="rounded-xl border border-border p-6 flex items-center gap-2 text-sm text-muted-foreground"><Loader2 className="w-4 h-4 animate-spin" /> Henter arbejdsplan…</div>;
  }

  return (
    <div className="space-y-5">
      {showFerieModal && (
        <FerieAnmodModal token={token} feriedata={feriedata} onClose={() => setShowFerieModal(false)} onSaved={() => { setShowFerieModal(false); hentMineFerier(); }} />
      )}
      {showFerieJuster && (
        <FerieSaldoModal itemId={itemId} token={token} nuvaerende={Number(feriedata?.saldo || 0)}
          onClose={() => setShowFerieJuster(false)}
          onSaved={(ny) => { setFeriedata(d => ({ ...(d || {}), saldo: ny })); setShowFerieJuster(false); }} />
      )}
      {showSidsteAar && (
        <SidsteAarModal itemId={itemId} token={token} aar={aar - 1} nuvaerende={sidsteAar?.timer ?? 0}
          onClose={() => setShowSidsteAar(false)}
          onSaved={(ny) => { setSidsteAar({ aar: aar - 1, timer: ny }); setShowSidsteAar(false); }} />
      )}
      {showMaanedJuster && (
        <MaanedJusterModal itemId={itemId} token={token} info={showMaanedJuster}
          onClose={() => setShowMaanedJuster(null)}
          onSaved={() => { setShowMaanedJuster(null); setFlexReloadKey(k => k + 1); }} />
      )}

      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <Calendar className="w-4 h-4 text-[#FF2A6D]" />
          <h2 className="text-sm font-bold uppercase tracking-wider text-muted-foreground">Arbejdsplan · {medarbejderNavn}</h2>
        </div>
        {/* Tæl ikke timer — superadmin kan ændre, andre ser read-only badge (#66) */}
        {isSuperAdmin && onToggleTaelIkke ? (
          <TaelIkkeTimerToggle aktiv={!!taelIkkeTimer} onToggle={onToggleTaelIkke} />
        ) : taelIkkeTimer ? (
          <span className="text-[12px] font-semibold text-muted-foreground bg-secondary px-2.5 py-1 rounded-full border border-border">Tæller ikke timer</span>
        ) : null}
      </div>

      {/* DEL A: Skjul Denne uge / Kommende vagter / Lønperiode / +-timer når
          medarbejderen ikke tæller timer. Ferie + Sygefravær bevares nedenfor. */}
      {!taelIkkeTimer && (<>
      {/* DENNE UGE */}
      <div className="rounded-xl border border-border p-5">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-[13px] font-bold text-muted-foreground uppercase tracking-wider">Denne uge</h3>
          <span className="text-[13px] font-semibold text-muted-foreground">Uge {ugeNr(uge.fra)} · {fmtT(ugeTimer)}</span>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-2">
          {uge.dage.map((iso, i) => {
            const dv = vagtPerDag[iso] || [];
            const dr = stemplerPerDag[iso] || [];
            const t = dagTimerMP(dv, dr);
            const erIdag = iso === idag;
            const fravaer = dv.find(v => FRAVAERS_TYPER_MP.has(v.vagttype));
            return (
              <div key={iso} className={`rounded-lg border p-2 text-center ${erIdag ? "border-[#FF2A6D] bg-[#FF2A6D]/5" : "border-border"}`}>
                <p className="text-[11px] font-semibold uppercase text-muted-foreground">{UGEDAGE_KORT[i]} {iso.slice(8,10)}</p>
                {fravaer ? (
                  <p className="text-[12px] font-semibold mt-1 text-amber-600 dark:text-amber-400">{fravaer.vagttype}</p>
                ) : dv.length || dr.length ? (
                  <>
                    <p className="text-sm font-bold mt-1 tabular-nums">{fmtT(t)}</p>
                    {dv[0]?.fra && <p className="text-[11px] text-muted-foreground">{dv[0].fra}–{dv[0].til}</p>}
                  </>
                ) : (
                  <p className="text-[12px] text-muted-foreground mt-1">—</p>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* KOMMENDE VAGTER — #39.4 foldbar + #40.4 periode-vælger + dynamisk header */}
      <div className="rounded-xl border border-border overflow-hidden">
        <button onClick={() => setVisKommende(o => !o)}
          className="w-full flex items-center justify-between px-5 py-3 text-[13px] font-bold text-muted-foreground uppercase tracking-wider hover:bg-secondary/40 transition-colors">
          <span>{kommendeRange.label} · {kommende.length} {kommende.length === 1 ? "vagt" : "vagter"}</span>
          {visKommende ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
        </button>
        {visKommende && (
          <div className="px-5 pb-4">
            {/* Periode-vælger */}
            <div className="flex flex-wrap items-center gap-2 mb-3 text-[13px]">
              <span className="text-muted-foreground">Periode:</span>
              <select value={kommendePeriode} onChange={e => setKommendePeriode(e.target.value)}
                className="bg-secondary border border-border rounded-lg px-2 h-8 text-[13px] focus:outline-none focus:ring-1 focus:ring-[#FF2A6D]">
                <option value="naeste7">Næste 7 dage</option>
                <option value="naeste14">Næste 14 dage</option>
                <option value="naeste30">Næste 30 dage</option>
                <option value="tidligere14">Tidligere 14 dage</option>
                <option value="tidligere30">Tidligere 30 dage</option>
                <option value="custom">Vælg periode…</option>
              </select>
              {kommendePeriode === "custom" ? (
                <>
                  <span className="text-muted-foreground">Fra:</span>
                  <input type="date" value={kommendeCustomFra} onChange={e => setKommendeCustomFra(e.target.value)}
                    className="bg-secondary border border-border rounded-lg px-2 h-8 text-[13px] focus:outline-none focus:ring-1 focus:ring-[#FF2A6D]" />
                  <span className="text-muted-foreground">Til:</span>
                  <input type="date" value={kommendeCustomTil} onChange={e => setKommendeCustomTil(e.target.value)}
                    className="bg-secondary border border-border rounded-lg px-2 h-8 text-[13px] focus:outline-none focus:ring-1 focus:ring-[#FF2A6D]" />
                </>
              ) : (
                <span className="text-muted-foreground">Fra: {fmtDKdato(kommendeRange.fra)} &nbsp; Til: {fmtDKdato(kommendeRange.til)}</span>
              )}
            </div>
            {kommende.length === 0 ? (
              <p className="text-sm text-muted-foreground">Ingen vagter i den valgte periode.</p>
            ) : (
              <div className="divide-y divide-border">
                {kommende.map(v => (
                  <div key={v.vagt_id} className="flex items-center justify-between py-2 text-sm">
                    <div className="flex items-center gap-3">
                      <span className="w-24 text-muted-foreground">{(() => { const d = new Date(v.dato+"T00:00:00"); return `${UGEDAGE_KORT[(d.getDay()+6)%7]} ${fmtDKdato(v.dato).slice(0,5)}`; })()}</span>
                      <span className="font-medium">{v.fra && v.til ? `${v.fra}–${v.til}` : (v.vagttype || "Vagt")}</span>
                      {FRAVAERS_TYPER_MP.has(v.vagttype) && <span className="text-[12px] rounded-full px-2 py-0.5 bg-amber-500/10 text-amber-600 dark:text-amber-400">{v.vagttype}</span>}
                      {v.importeret && <span title="Importeret fra det gamle vagtsystem" className="text-[12px] rounded-full px-1.5 py-0.5 bg-blue-500/10 text-blue-600 dark:text-blue-400">⤓</span>}
                      {v.city && <span className="text-[12px] text-muted-foreground">· {v.city}</span>}
                    </div>
                    <span className="text-muted-foreground tabular-nums">{v.timer ? fmtT(parseFloat(v.timer)) : ""}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* LØNPERIODE */}
      <div className="rounded-xl border border-border p-5">
        <h3 className="text-[13px] font-bold text-muted-foreground uppercase tracking-wider mb-3">
          Lønperiode {periode.fra.slice(8,10)}.{periode.fra.slice(5,7)} – {periode.til.slice(8,10)}.{periode.til.slice(5,7)}
        </h3>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          <div><p className="text-[13px] text-muted-foreground mb-0.5">Planlagt</p><p className="text-lg font-bold tabular-nums">{fmtT(periodePlanlagt)}</p></div>
          <div><p className="text-[13px] text-muted-foreground mb-0.5">Stemplet</p><p className="text-lg font-bold tabular-nums">{fmtT(periodeStemplet)}</p></div>
          <div><p className="text-[13px] text-muted-foreground mb-0.5">Kontrakt</p><p className="text-lg font-bold tabular-nums">{kontraktPeriode ? fmtT(kontraktPeriode) : "—"}</p></div>
          <div>
            <p className="text-[13px] text-muted-foreground mb-0.5">+/- saldo</p>
            <p className={`text-lg font-bold tabular-nums ${saldo >= 0 ? "text-green-600 dark:text-green-400" : "text-red-600 dark:text-red-400"}`}>
              {saldo > 0 ? "+" : ""}{fmtT(saldo)}
            </p>
            {kontraktPeriode > 0 && <p className="text-[12px] text-muted-foreground mt-0.5">{saldo < 0 ? "under kontrakt" : saldo > 0 ? "over kontrakt" : "på kontrakt"}</p>}
          </div>
        </div>
      </div>

      {/* +/- TIMER — #39.5: kun månedsløn (Timeløn/Vikar har ikke saldo-tracking) */}
      {erMaanedsloen && visSaldo && (
        <div className="rounded-xl border border-border p-5">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-[13px] font-bold text-muted-foreground uppercase tracking-wider">+/- Timer · {aar}</h3>
            <span className="text-[13px] text-muted-foreground"
              title="Pr. måned: stemplede timer minus kontrakttimer. Sygdom, ferie, fri og læge-fravær tæller som opfyldt kontrakt.">
              I alt år-til-dato: <span className={`font-bold tabular-nums ${(aarSaldoTotal ?? 0) >= 0 ? "text-green-600 dark:text-green-400" : "text-red-600 dark:text-red-400"}`}>{fmtTsign(aarSaldoTotal)}</span>
            </span>
          </div>
          <div className="overflow-x-auto">
          <div className="grid gap-1.5 min-w-[640px]" style={{ gridTemplateColumns: "repeat(13, minmax(0, 1fr))" }}>
            {visSaldo.months.map(({ mo, mKey, value, beregnet, override }) => (
              <div key={mo} className={`group relative rounded-lg border px-1.5 py-1.5 text-center ${override ? "border-amber-500/40 bg-amber-500/5" : "border-border"}`}>
                <p className="text-[10px] uppercase font-semibold text-muted-foreground">{MND_KORT[mo]}</p>
                <p className={`text-[13px] tabular-nums font-semibold ${value == null ? "text-muted-foreground/40" : value >= 0 ? "text-green-600 dark:text-green-400" : "text-red-600 dark:text-red-400"}`}>
                  {value == null ? "-" : fmtTsign(value)}
                </p>
                {override && <span title="Manuelt overskrevet (ikke beregnet)" className="absolute top-0.5 left-0.5 w-1.5 h-1.5 rounded-full bg-amber-500" />}
                {isSuperAdmin && (
                  <button
                    onClick={() => setShowMaanedJuster({ maaned: mKey, beregnet, override, navn: `${MND_KORT[mo]} ${aar}` })}
                    title={`Justér ${MND_KORT[mo]} ${aar} manuelt`}
                    className="absolute top-0.5 right-0.5 text-muted-foreground hover:text-[#FF2A6D] opacity-0 group-hover:opacity-100 transition-opacity">
                    <Edit3 className="w-3 h-3" />
                  </button>
                )}
              </div>
            ))}
            <div className="rounded-lg border border-foreground/20 bg-secondary/40 px-1.5 py-1.5 text-center">
              <p className="text-[10px] uppercase font-semibold text-muted-foreground">I alt</p>
              <p className={`text-[13px] tabular-nums font-bold ${(aarSaldoTotal ?? 0) >= 0 ? "text-green-600 dark:text-green-400" : "text-red-600 dark:text-red-400"}`}>
                {fmtTsign(aarSaldoTotal)}
              </p>
            </div>
          </div>
          </div>
          <div className="mt-3 pt-3 border-t border-border flex items-center gap-2 text-[13px]">
            <span className="text-muted-foreground">Sidste år ({aar - 1}) sluttede på:</span>
            <span className="font-bold tabular-nums">{sidsteAar?.timer != null ? fmtTsign(sidsteAar.timer) + " t" : "—"}</span>
            {isSuperAdmin && (
              <button onClick={() => setShowSidsteAar(true)} title="Redigér sidste års slutsaldo (logges)"
                className="text-muted-foreground hover:text-[#FF2A6D] transition-colors">
                <Edit3 className="w-3.5 h-3.5" />
              </button>
            )}
          </div>
        </div>
      )}
      </>)}

      {/* FERIE */}
      <div className="rounded-xl border border-border p-5">
        <h3 className="text-[13px] font-bold text-muted-foreground uppercase tracking-wider mb-3">Ferie</h3>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 items-start">
          <div>
            <p className="text-[13px] text-muted-foreground mb-0.5">Feriedage til gode</p>
            <p className="text-lg font-bold flex items-center gap-1.5">
              {feriedata != null ? Number(feriedata.saldo || 0).toFixed(2).replace(".", ",") + " dage" : "—"}
              {/* #39.3: manuel ferie-justering flyttet hertil (blyant). Kun superadmin. */}
              {isSuperAdmin && (
                <button onClick={() => setShowFerieJuster(true)} title="Justér feriesaldo (logges)"
                  className="text-muted-foreground hover:text-[#FF2A6D] transition-colors">
                  <Edit3 className="w-3.5 h-3.5" />
                </button>
              )}
            </p>
            {feriedata?.forventet_aar_slut != null && (
              <p className="text-[12px] text-muted-foreground mt-0.5">Forventet årsslut: {Number(feriedata.forventet_aar_slut).toFixed(1).replace(".", ",")}</p>
            )}
          </div>
          <div>
            <p className="text-[13px] text-muted-foreground mb-0.5">Næste planlagte ferie</p>
            {(() => {
              const nf = vagter.filter(v => v.vagttype === "Ferie" && v.dato >= idag).sort((a,b)=>a.dato.localeCompare(b.dato));
              if (!nf.length) return <p className="text-sm text-muted-foreground">Ingen planlagt</p>;
              return <p className="text-sm font-semibold">Uge {ugeNr(nf[0].dato)} <span className="text-muted-foreground font-normal">({nf.length} {nf.length === 1 ? "dag" : "dage"})</span></p>;
            })()}
          </div>
          {erEgenProfil && (
            <div className="sm:justify-self-end">
              <button onClick={() => setShowFerieModal(true)}
                className="inline-flex items-center gap-1.5 px-4 py-2 rounded-xl bg-[#FF2A6D] text-white text-[13px] font-semibold hover:bg-[#e02560] transition-colors">
                <Plus className="w-3.5 h-3.5" /> Anmod om ferie
              </button>
            </div>
          )}
        </div>
        {erEgenProfil && mineFerier.length > 0 && (
          <div className="mt-3 pt-3 border-t border-border space-y-1">
            {mineFerier.map(f => (
              <div key={f.periode_id} className="flex items-center justify-between text-[13px]">
                <span>{fmtDKdato(f.fra_dato)} – {fmtDKdato(f.til_dato)} <span className="text-muted-foreground">({f.antal_arbejdsdage} dage)</span></span>
                <span className={`font-semibold ${f.status === "afvist" ? "text-red-600 dark:text-red-400" : "text-yellow-600 dark:text-yellow-400"}`}>{f.status === "afvist" ? "Afvist" : "Afventer"}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* KØRSEL — DEL B: kun når kørselsregistrering er aktiveret. Placeret
          mellem Ferie og Sygefravær. */}
      {koerselAktiveret && (
        <KoerselBoks itemId={itemId} token={token} medarbejderNavn={medarbejderNavn} />
      )}

      {/* SYGEFRAVÆR — #40.5: alle tre tal vises for alle medarbejdere */}
      {(kanSeFortrolige || erEgenProfil) && syg && (
        <div className="rounded-xl border border-border p-5">
          <h3 className="text-[13px] font-bold text-muted-foreground uppercase tracking-wider mb-3">Sygefravær (12 mdr.)</h3>
          <div className="grid grid-cols-3 gap-4">
            <div>
              <p className="text-[13px] text-muted-foreground mb-0.5">Sygedage</p>
              <p className="text-lg font-bold">{(syg.sygedage ?? 0)} {syg.sygedage === 1 ? "dag" : "dage"}</p>
            </div>
            <div>
              <p className="text-[13px] text-muted-foreground mb-0.5">Syge timer</p>
              <p className="text-lg font-bold">{Number(syg.syge_timer ?? 0).toFixed(1).replace(".", ",")} timer</p>
            </div>
            <div>
              <p className="text-[13px] text-muted-foreground mb-0.5">Antal perioder</p>
              <p className="text-lg font-bold">{syg.antal_perioder ?? 0}</p>
            </div>
          </div>
        </div>
      )}

      {/* UGETABEL (fold-ud) */}
      <div className="rounded-xl border border-border overflow-hidden">
        <button onClick={() => setVisUgetabel(o => !o)}
          className="w-full flex items-center justify-between px-5 py-3 text-sm font-semibold hover:bg-secondary/40 transition-colors">
          <span>{visUgetabel ? "Skjul ugetabel" : "Vis ugetabel"}</span>
          {visUgetabel ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
        </button>
        {visUgetabel && (
          ugetabel.length === 0 ? (
            <p className="px-5 pb-4 text-sm text-muted-foreground">Ingen vagter eller stemplinger registreret endnu.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-t border-border bg-secondary/40">
                    {["Uge", "Periode", "Planlagt", "Stemplet", "Saldo"].map(h => (
                      <th key={h} className="text-left py-2 px-4 text-[12px] font-semibold text-muted-foreground uppercase tracking-wider">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {ugetabel.map(row => {
                    const s = +(row.stemplet - row.planlagt).toFixed(1);
                    const sondag = (() => { const d = new Date(row.mandag+"T00:00:00"); d.setDate(d.getDate()+6); return isoDag(d); })();
                    // #46.3: uger der HELT ligger før ansættelsesstart vises med "—"
                    // i Planlagt/Stemplet/Saldo (retroaktivt — følger ansaettelsesdato).
                    const foerAnsaettelse = ansaettelsesdato && sondag < ansaettelsesdato;
                    return (
                      <tr key={row.mandag} className="border-t border-border">
                        <td className="py-2 px-4 font-semibold tabular-nums">
                          <span className="inline-flex items-center gap-1">
                            {row.uge}
                            {row.importeret && <span title="Importeret fra det gamle vagtsystem" className="text-[11px] rounded px-1 bg-blue-500/10 text-blue-600 dark:text-blue-400">⤓</span>}
                          </span>
                        </td>
                        <td className="py-2 px-4 text-muted-foreground tabular-nums">{fmtDKdato(row.mandag).slice(0,5)}–{fmtDKdato(sondag).slice(0,5)}</td>
                        {foerAnsaettelse ? (
                          <>
                            <td className="py-2 px-4 tabular-nums text-muted-foreground">—</td>
                            <td className="py-2 px-4 tabular-nums text-muted-foreground">—</td>
                            <td className="py-2 px-4 tabular-nums text-muted-foreground">—</td>
                          </>
                        ) : (
                          <>
                            <td className="py-2 px-4 tabular-nums">{fmtT(row.planlagt)}</td>
                            <td className="py-2 px-4 tabular-nums">{fmtT(row.stemplet)}</td>
                            <td className={`py-2 px-4 tabular-nums font-semibold ${s >= 0 ? "text-green-600 dark:text-green-400" : "text-red-600 dark:text-red-400"}`}>{s > 0 ? "+" : ""}{fmtT(s)}</td>
                          </>
                        )}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )
        )}
      </div>
    </div>
  );
}

// Hjælper til sygefravær-enhed (genbrugt fra StamdataOversigtKort).
function fmtEnhedMP(n, enhed) {
  const num = Number(n || 0);
  const str = (enhed === "timer" ? num.toFixed(1) : num.toFixed(num % 1 === 0 ? 0 : 1)).replace(".", ",");
  return `${str} ${enhed}`;
}

// Ferie-anmodnings-modal (flyttet fra HubView til Arbejdsplan-fanen, #37).
function FerieAnmodModal({ token, feriedata, onClose, onSaved }) {
  const [fra, setFra] = useState("");
  const [til, setTil] = useState("");
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);
  const [fejl, setFejl] = useState("");

  const gem = async () => {
    setFejl("");
    if (!fra || !til) { setFejl("Vælg fra- og til-dato."); return; }
    if (til < fra) { setFejl("Til-dato skal være efter fra-dato."); return; }
    setSaving(true);
    try {
      await axios.post(`${API}/hub/ferie-anmodning`, { fra_dato: fra, til_dato: til, note }, { headers: { Authorization: `Bearer ${token}` } });
      onSaved();
    } catch (e) { setFejl(e?.response?.data?.detail || "Kunne ikke sende anmodning"); }
    setSaving(false);
  };

  return (
    <ModalPortal onClose={onClose}>
      <div className="bg-background border border-border rounded-2xl shadow-2xl w-full max-w-sm">
        <div className="flex items-center justify-between px-5 py-4 border-b border-border">
          <h2 className="font-semibold text-sm">Anmod om ferie</h2>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground"><X className="w-4 h-4" /></button>
        </div>
        <div className="p-5 flex flex-col gap-4">
          {feriedata?.saldo != null && (
            <p className="text-[13px] text-muted-foreground">Feriedage til gode: <span className="font-semibold text-foreground">{Number(feriedata.saldo).toFixed(2).replace(".", ",")}</span></p>
          )}
          <div className="grid grid-cols-2 gap-3">
            <div><label className="text-[13px] text-muted-foreground mb-1 block">Fra dato</label>
              <input type="date" value={fra} onChange={e => setFra(e.target.value)} className="w-full bg-secondary border border-border rounded-lg px-3 h-9 text-sm focus:outline-none focus:ring-1 focus:ring-[#FF2A6D]" /></div>
            <div><label className="text-[13px] text-muted-foreground mb-1 block">Til dato</label>
              <input type="date" value={til} onChange={e => setTil(e.target.value)} className="w-full bg-secondary border border-border rounded-lg px-3 h-9 text-sm focus:outline-none focus:ring-1 focus:ring-[#FF2A6D]" /></div>
          </div>
          <div><label className="text-[13px] text-muted-foreground mb-1 block">Note (valgfri)</label>
            <textarea value={note} onChange={e => setNote(e.target.value)} rows={2} className="w-full bg-secondary border border-border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-[#FF2A6D] resize-y" /></div>
          {fejl && <p className="text-[13px] text-red-500">{fejl}</p>}
        </div>
        <div className="flex justify-end gap-2 px-5 pb-5">
          <button onClick={onClose} className="px-4 py-2 rounded-lg text-sm border border-border hover:bg-secondary transition-colors">Annuller</button>
          <button onClick={gem} disabled={saving} className="flex items-center gap-1.5 px-5 py-2 rounded-lg bg-[#FF2A6D] text-white text-sm font-semibold hover:bg-[#e02560] transition-colors disabled:opacity-60">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />} Send anmodning
          </button>
        </div>
      </div>
    </ModalPortal>
  );
}

// #39.3: manuel ferie-justering (flyttet fra Sammenfatning). Sætter saldo direkte;
// backend beregner delta og logger (hvem/hvornår/fra-tal/til-tal).
function FerieSaldoModal({ itemId, token, nuvaerende, onClose, onSaved }) {
  const [vaerdi, setVaerdi] = useState(String(nuvaerende ?? 0));
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);
  const [fejl, setFejl] = useState("");
  const gem = async () => {
    setFejl("");
    const ny = parseFloat(String(vaerdi).replace(",", "."));
    if (isNaN(ny)) { setFejl("Indtast et tal."); return; }
    setSaving(true);
    try {
      await axios.post(`${API}/medarbejdere/${itemId}/feriebank/set-saldo`,
        { ny_saldo: ny, beskrivelse: note }, { headers: { Authorization: `Bearer ${token}` } });
      onSaved(ny);
    } catch (e) { setFejl(e?.response?.data?.detail || "Kunne ikke gemme"); setSaving(false); }
  };
  return (
    <ModalPortal onClose={onClose}>
      <div className="bg-background border border-border rounded-2xl shadow-2xl w-full max-w-sm">
        <div className="flex items-center justify-between px-5 py-4 border-b border-border">
          <h2 className="font-semibold text-sm">Justér feriesaldo</h2>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground"><X className="w-4 h-4" /></button>
        </div>
        <div className="p-5 flex flex-col gap-4">
          <div><label className="text-[13px] text-muted-foreground mb-1 block">Ny saldo (feriedage)</label>
            <input type="number" step="0.01" value={vaerdi} onChange={e => setVaerdi(e.target.value)}
              className="w-full bg-secondary border border-border rounded-lg px-3 h-9 text-sm focus:outline-none focus:ring-1 focus:ring-[#FF2A6D]" /></div>
          <div><label className="text-[13px] text-muted-foreground mb-1 block">Note (valgfri)</label>
            <input value={note} onChange={e => setNote(e.target.value)} placeholder="Fx korrektion ved overgang"
              className="w-full bg-secondary border border-border rounded-lg px-3 h-9 text-sm focus:outline-none focus:ring-1 focus:ring-[#FF2A6D]" /></div>
          {fejl && <p className="text-[13px] text-red-500">{fejl}</p>}
          <p className="text-[12px] text-muted-foreground">Ændringen logges (hvem, hvornår, fra-tal, til-tal).</p>
        </div>
        <div className="flex justify-end gap-2 px-5 pb-5">
          <button onClick={onClose} className="px-4 py-2 rounded-lg text-sm border border-border hover:bg-secondary transition-colors">Annuller</button>
          <button onClick={gem} disabled={saving} className="flex items-center gap-1.5 px-5 py-2 rounded-lg bg-[#FF2A6D] text-white text-sm font-semibold hover:bg-[#e02560] transition-colors disabled:opacity-60">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />} Gem
          </button>
        </div>
      </div>
    </ModalPortal>
  );
}

// #39.5: redigér sidste års +/- timer slutsaldo (låst tal, kun superadmin, logges).
function SidsteAarModal({ itemId, token, aar, nuvaerende, onClose, onSaved }) {
  const [vaerdi, setVaerdi] = useState(String(nuvaerende ?? 0));
  const [saving, setSaving] = useState(false);
  const [fejl, setFejl] = useState("");
  const gem = async () => {
    setFejl("");
    const ny = parseFloat(String(vaerdi).replace(",", "."));
    if (isNaN(ny)) { setFejl("Indtast et tal."); return; }
    setSaving(true);
    try {
      await axios.put(`${API}/admin/medarbejdere/${itemId}/flex-saldo`,
        { aar, timer: ny }, { headers: { Authorization: `Bearer ${token}` } });
      onSaved(ny);
    } catch (e) { setFejl(e?.response?.data?.detail || "Kunne ikke gemme"); setSaving(false); }
  };
  return (
    <ModalPortal onClose={onClose}>
      <div className="bg-background border border-border rounded-2xl shadow-2xl w-full max-w-sm">
        <div className="flex items-center justify-between px-5 py-4 border-b border-border">
          <h2 className="font-semibold text-sm">Sidste års slutsaldo ({aar})</h2>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground"><X className="w-4 h-4" /></button>
        </div>
        <div className="p-5 flex flex-col gap-4">
          <div><label className="text-[13px] text-muted-foreground mb-1 block">+/- timer ved årets udgang</label>
            <input type="number" step="0.1" value={vaerdi} onChange={e => setVaerdi(e.target.value)}
              className="w-full bg-secondary border border-border rounded-lg px-3 h-9 text-sm focus:outline-none focus:ring-1 focus:ring-[#FF2A6D]" /></div>
          {fejl && <p className="text-[13px] text-red-500">{fejl}</p>}
          <p className="text-[12px] text-muted-foreground">Ændringen logges. Bruges som startsaldo for {aar}.</p>
        </div>
        <div className="flex justify-end gap-2 px-5 pb-5">
          <button onClick={onClose} className="px-4 py-2 rounded-lg text-sm border border-border hover:bg-secondary transition-colors">Annuller</button>
          <button onClick={gem} disabled={saving} className="flex items-center gap-1.5 px-5 py-2 rounded-lg bg-[#FF2A6D] text-white text-sm font-semibold hover:bg-[#e02560] transition-colors disabled:opacity-60">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />} Gem
          </button>
        </div>
      </div>
    </ModalPortal>
  );
}

// #41.3: manuel justering af en enkelt måneds +/- timer-saldo (kun superadmin).
function MaanedJusterModal({ itemId, token, info, onClose, onSaved }) {
  const [vaerdi, setVaerdi] = useState(
    info.override != null && typeof info.override === "number" ? String(info.override) :
    (info.beregnet != null ? String(info.beregnet) : "0"));
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);
  const [fejl, setFejl] = useState("");

  const gem = async () => {
    setFejl("");
    const ny = parseFloat(String(vaerdi).replace(",", "."));
    if (isNaN(ny)) { setFejl("Indtast et tal."); return; }
    setSaving(true);
    try {
      await axios.put(`${API}/admin/medarbejdere/${itemId}/flex-maaned`,
        { maaned: info.maaned, timer: ny, note }, { headers: { Authorization: `Bearer ${token}` } });
      onSaved();
    } catch (e) { setFejl(e?.response?.data?.detail || "Kunne ikke gemme"); setSaving(false); }
  };
  const nulstil = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/admin/medarbejdere/${itemId}/flex-maaned`,
        { maaned: info.maaned, reset: true, note }, { headers: { Authorization: `Bearer ${token}` } });
      onSaved();
    } catch (e) { setFejl(e?.response?.data?.detail || "Kunne ikke nulstille"); setSaving(false); }
  };

  return (
    <ModalPortal onClose={onClose}>
      <div className="bg-background border border-border rounded-2xl shadow-2xl w-full max-w-sm">
        <div className="flex items-center justify-between px-5 py-4 border-b border-border">
          <h2 className="font-semibold text-sm">Justér {info.navn}</h2>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground"><X className="w-4 h-4" /></button>
        </div>
        <div className="p-5 flex flex-col gap-4">
          {info.beregnet != null && (
            <p className="text-[13px] text-muted-foreground">Beregnet: <span className="font-semibold text-foreground">{(info.beregnet > 0 ? "+" : "") + info.beregnet} t</span></p>
          )}
          <div><label className="text-[13px] text-muted-foreground mb-1 block">Ny saldo (+/- timer)</label>
            <input type="number" step="0.1" value={vaerdi} onChange={e => setVaerdi(e.target.value)}
              className="w-full bg-secondary border border-border rounded-lg px-3 h-9 text-sm focus:outline-none focus:ring-1 focus:ring-[#FF2A6D]" /></div>
          <div><label className="text-[13px] text-muted-foreground mb-1 block">Note (valgfri)</label>
            <input value={note} onChange={e => setNote(e.target.value)} placeholder="Fx korrektion af forkert beregning"
              className="w-full bg-secondary border border-border rounded-lg px-3 h-9 text-sm focus:outline-none focus:ring-1 focus:ring-[#FF2A6D]" /></div>
          {fejl && <p className="text-[13px] text-red-500">{fejl}</p>}
          <p className="text-[12px] text-muted-foreground">Manuelt tal vises med markering og indgår i "I alt". Ændringen logges.</p>
        </div>
        <div className="flex items-center justify-between gap-2 px-5 pb-5">
          {info.override ? (
            <button onClick={nulstil} disabled={saving}
              className="px-3 py-2 rounded-lg text-[13px] border border-border text-muted-foreground hover:text-foreground hover:bg-secondary transition-colors disabled:opacity-60">
              Nulstil til beregnet
            </button>
          ) : <span />}
          <div className="flex gap-2">
            <button onClick={onClose} className="px-4 py-2 rounded-lg text-sm border border-border hover:bg-secondary transition-colors">Annuller</button>
            <button onClick={gem} disabled={saving} className="flex items-center gap-1.5 px-5 py-2 rounded-lg bg-[#FF2A6D] text-white text-sm font-semibold hover:bg-[#e02560] transition-colors disabled:opacity-60">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />} Gem
            </button>
          </div>
        </div>
      </div>
    </ModalPortal>
  );
}

// #41.5: menneskelæsbare labels for log-handlinger + filter-grupper.
const LOG_ACTIONS = {
  login:                       "Login",
  stamdata_opdateret:          "Stamdata opdateret",
  status_aendret:              "Status ændret",
  rolle_aendret:               "Rolle ændret",
  rettigheder_aendret:         "Rettigheder ændret",
  ferie_justeret:              "Ferie justeret",
  flex_saldo_sidste_aar:       "+/- timer: sidste år",
  flex_saldo_maaned:           "+/- timer: måned",
  dokument_uploadet:           "Dokument uploadet",
  dokument_slettet:            "Dokument slettet",
  ansaettelseshistorik_tilfojet: "Ansættelseshistorik tilføjet",
  tilfoej_historisk_sygedag:   "Sygedag tilføjet",
  slet_historisk_sygedag:      "Sygedag slettet",
  loenaendring_fortrudt:       "Lønændring fortrudt",
  medarbejder_arkiveret:       "Medarbejder arkiveret",
  medarbejder_gendannet:       "Medarbejder gendannet",
  cv_aendret:                  "CV ændret",
};
function logActionLabel(a) { return LOG_ACTIONS[a] || a || "Handling"; }

// Log-fane: nyeste øverst, fritekst-søgning + filter pr. handlings-type.
function LogFane({ log }) {
  const [sog, setSog] = useState("");
  const [typeFilter, setTypeFilter] = useState("alle");
  // Sortér nyeste først (defensivt — backend sorterer også).
  const sorteret = useMemo(() => [...(log || [])].sort((a, b) => (b.ts || "").localeCompare(a.ts || "")), [log]);
  // Hvilke handlings-typer findes faktisk i loggen (til filter-dropdown).
  const typer = useMemo(() => {
    const s = new Set(sorteret.map(e => e.action).filter(Boolean));
    return ["alle", ...[...s].sort((a, b) => logActionLabel(a).localeCompare(logActionLabel(b), "da"))];
  }, [sorteret]);
  const filtreret = useMemo(() => {
    const q = sog.trim().toLowerCase();
    return sorteret.filter(e => {
      if (typeFilter !== "alle" && e.action !== typeFilter) return false;
      if (!q) return true;
      const hay = [e.user, logActionLabel(e.action), e.detalje, e.note, e.felt,
        (e.felter || []).join(" "), e.gammel, e.ny].filter(Boolean).join(" ").toLowerCase();
      return hay.includes(q);
    });
  }, [sorteret, sog, typeFilter]);

  const fmtVal = (v) => (v === null || v === undefined || v === "") ? "—" : String(v);

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <input value={sog} onChange={e => setSog(e.target.value)} placeholder="Søg i log…"
          className="bg-secondary border border-border rounded-lg px-3 h-9 text-sm w-56 focus:outline-none focus:ring-1 focus:ring-[#FF2A6D]" />
        <select value={typeFilter} onChange={e => setTypeFilter(e.target.value)}
          className="bg-secondary border border-border rounded-lg px-2 h-9 text-sm focus:outline-none">
          {typer.map(t => <option key={t} value={t}>{t === "alle" ? "Alle handlinger" : logActionLabel(t)}</option>)}
        </select>
        <span className="text-[13px] text-muted-foreground ml-auto">{filtreret.length} {filtreret.length === 1 ? "post" : "poster"}</span>
      </div>
      <div className="rounded-xl border border-border divide-y divide-border overflow-hidden">
        {filtreret.length === 0 && (
          <p className="text-sm text-muted-foreground italic p-5">Ingen log-poster matcher.</p>
        )}
        {filtreret.map((entry, i) => {
          const erLogin = entry.action === "login";
          return (
            <div key={i} className={`flex items-start gap-3 px-5 py-3 ${erLogin ? "bg-green-500/5" : ""}`}>
              <div className={`w-7 h-7 rounded-full flex items-center justify-center shrink-0 mt-0.5 ${erLogin ? "bg-green-500/15" : "bg-secondary"}`}>
                <User className={`w-3.5 h-3.5 ${erLogin ? "text-green-500" : "text-muted-foreground"}`} />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm">
                  <span className={`font-semibold ${erLogin ? "text-green-600 dark:text-green-400" : ""}`}>{logActionLabel(entry.action)}</span>
                  {entry.user && <span className="text-muted-foreground"> · {entry.user}</span>}
                </p>
                {/* Detaljer: fra→til, felt-liste, note */}
                {(entry.gammel !== undefined || entry.ny !== undefined) && (
                  <p className="text-[13px] text-muted-foreground mt-0.5">
                    {entry.felt ? `${entry.felt}: ` : ""}{fmtVal(entry.gammel)} → {fmtVal(entry.ny)}
                  </p>
                )}
                {entry.detalje && <p className="text-[13px] text-muted-foreground mt-0.5">{entry.detalje}</p>}
                {!entry.detalje && entry.felter?.length > 0 && (
                  <p className="text-[13px] text-muted-foreground mt-0.5">{entry.felter.join(", ")}</p>
                )}
                {entry.note && <p className="text-[13px] text-muted-foreground mt-0.5 italic">“{entry.note}”</p>}
                <p className="text-[12px] text-muted-foreground mt-0.5">{fmtTs(entry.ts)}</p>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// #43: Generer CV-dialog (kun superadmin). Henter LLM-forfinet preview fra
// backend, viser den, og tilbyder PDF/Word-download + "Generer igen".
function GenererCvDialog({ itemId, token, navn, onClose }) {
  const [loading, setLoading] = useState(true);
  const [html, setHtml] = useState("");
  const [llmBrugt, setLlmBrugt] = useState(false);
  const [fejl, setFejl] = useState("");

  const generer = useCallback(async () => {
    setLoading(true); setFejl("");
    try {
      const r = await axios.post(`${API}/admin/medarbejdere/${itemId}/generer-cv`, {},
        { headers: { Authorization: `Bearer ${token}` } });
      setHtml(r.data?.html || "");
      setLlmBrugt(!!r.data?.llm_brugt);
    } catch (e) {
      setFejl(e?.response?.data?.detail || "Kunne ikke generere CV");
    } finally { setLoading(false); }
  }, [itemId, token]);

  useEffect(() => { generer(); }, [generer]);

  // Download PDF/Word via blob (kræver Authorization-header → kan ikke bruge ren <a>).
  const download = async (format) => {
    try {
      const r = await axios.get(`${API}/admin/medarbejdere/${itemId}/generer-cv/${format}`, {
        headers: { Authorization: `Bearer ${token}` }, responseType: "blob",
      });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(r.data);
      const ext = format === "pdf" ? "pdf" : "docx";
      a.download = `CV - ${navn} - ${new Date().toISOString().slice(0,10)}.${ext}`;
      a.click();
      URL.revokeObjectURL(a.href);
    } catch (e) {
      alert(e?.response?.data?.detail || `Kunne ikke hente ${format.toUpperCase()}`);
    }
  };

  return (
    <ModalPortal onClose={onClose}>
      <div className="bg-background border border-border rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between px-5 py-4 border-b border-border">
          <div>
            <h2 className="font-semibold text-sm">Generer CV — {navn}</h2>
            {!loading && (
              <p className="text-[12px] text-muted-foreground mt-0.5">
                {llmBrugt ? "Sproget er forfinet med AI ud fra medarbejderens egne tekster." : "Uden AI-forfining (ingen API-nøgle) — viser medarbejderens rå tekst."}
              </p>
            )}
          </div>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground"><X className="w-4 h-4" /></button>
        </div>

        <div className="flex-1 overflow-auto bg-secondary/30 p-4">
          {loading ? (
            <div className="flex flex-col items-center justify-center py-24 gap-3 text-muted-foreground">
              <Loader2 className="w-7 h-7 animate-spin" />
              <p className="text-sm">Genererer CV…</p>
            </div>
          ) : fejl ? (
            <div className="flex flex-col items-center justify-center py-24 gap-3">
              <p className="text-sm text-red-500">{fejl}</p>
              <button onClick={generer} className="px-4 py-2 rounded-lg text-[13px] font-semibold bg-secondary hover:bg-secondary/80">Prøv igen</button>
            </div>
          ) : (
            <iframe title="CV preview" srcDoc={html} className="w-full h-[60vh] bg-white rounded-lg border border-border" />
          )}
        </div>

        <div className="flex items-center justify-between gap-2 px-5 py-4 border-t border-border">
          <button onClick={generer} disabled={loading}
            className="px-4 py-2 rounded-lg text-[13px] font-semibold border border-border hover:bg-secondary transition-colors disabled:opacity-50">
            Generer igen
          </button>
          <div className="flex gap-2">
            <button onClick={() => download("pdf")} disabled={loading || !!fejl}
              className="px-4 py-2 rounded-lg text-[13px] font-semibold bg-secondary hover:bg-secondary/80 transition-colors disabled:opacity-50 inline-flex items-center gap-1.5">
              <Download className="w-3.5 h-3.5" /> Download som PDF
            </button>
            <button onClick={() => download("word")} disabled={loading || !!fejl}
              className="px-4 py-2 rounded-lg text-[13px] font-semibold bg-[#FF2A6D] text-white hover:bg-[#e02560] transition-colors disabled:opacity-50 inline-flex items-center gap-1.5">
              <Download className="w-3.5 h-3.5" /> Download som Word
            </button>
          </div>
        </div>
      </div>
    </ModalPortal>
  );
}

// ─── Ændre kode-dialog (#61 DEL B) — kun SuperAdmin ──────────────────────────
// Sætter en ny adgangskode på medarbejderens login-konto. Backend logger
// medarbejderen ud af alle sessioner og kan valgfrit sende koden på e-mail.
function AendreKodeDialog({ itemId, token, navn, onClose }) {
  const [nyKode, setNyKode] = useState("");
  const [bekraeftKode, setBekraeftKode] = useState("");
  const [visKode, setVisKode] = useState(false);
  const [sendEmail, setSendEmail] = useState(true);
  const [fejl, setFejl] = useState(null);
  const [saving, setSaving] = useState(false);

  const handleGem = async () => {
    setFejl(null);
    if (nyKode.length < 6) { setFejl("Koden skal være mindst 6 tegn."); return; }
    if (nyKode !== bekraeftKode) { setFejl("Koderne er ikke ens"); return; }
    setSaving(true);
    try {
      const r = await axios.post(
        `${API}/admin/medarbejdere/${itemId}/aendre-kode`,
        { ny_kode: nyKode, send_email: sendEmail },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      let besked = "Koden er ændret";
      if (sendEmail) {
        besked += r.data?.email_sendt
          ? " og sendt på e-mail."
          : " (kunne ikke sendes på e-mail — mail er ikke konfigureret).";
      } else {
        besked += ".";
      }
      alert(besked);
      onClose();
    } catch (e) {
      setFejl(e?.response?.data?.detail || "Kunne ikke ændre koden.");
      setSaving(false);
    }
  };

  return (
    <ModalPortal onClose={onClose}>
      <div className="bg-background border border-border rounded-2xl shadow-2xl w-full max-w-md" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between px-6 py-4 border-b border-border">
          <h2 className="font-semibold text-sm flex items-center gap-2 min-w-0">
            <KeyRound className="w-4 h-4 text-[#FF2A6D] shrink-0" />
            <span className="break-words min-w-0">Ændre kode - {navn || "medarbejder"}</span>
          </h2>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground shrink-0"><X className="w-4 h-4" /></button>
        </div>
        <div className="p-6 flex flex-col gap-4">
          <div>
            <label className="text-[13px] text-muted-foreground mb-1 block">Ny kode</label>
            <div className="relative">
              <input
                type={visKode ? "text" : "password"}
                value={nyKode}
                onChange={e => setNyKode(e.target.value)}
                autoComplete="new-password"
                placeholder="Min. 6 tegn"
                className="w-full bg-secondary border border-border rounded-lg pl-3 pr-20 h-9 text-sm focus:outline-none focus:ring-1 focus:ring-[#FF2A6D]"
              />
              <button type="button" onClick={() => setVisKode(v => !v)}
                className="absolute right-2 top-1/2 -translate-y-1/2 flex items-center gap-1 text-[12px] text-muted-foreground hover:text-foreground">
                {visKode ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
                {visKode ? "Skjul" : "Vis"}
              </button>
            </div>
          </div>
          <div>
            <label className="text-[13px] text-muted-foreground mb-1 block">Bekræft kode</label>
            <input
              type={visKode ? "text" : "password"}
              value={bekraeftKode}
              onChange={e => setBekraeftKode(e.target.value)}
              autoComplete="new-password"
              className="w-full bg-secondary border border-border rounded-lg px-3 h-9 text-sm focus:outline-none focus:ring-1 focus:ring-[#FF2A6D]"
            />
          </div>
          <label className="flex items-start gap-2.5 cursor-pointer select-none">
            <input type="checkbox" checked={sendEmail} onChange={e => setSendEmail(e.target.checked)}
              className="mt-0.5 accent-[#FF2A6D]" />
            <span className="text-sm">Send ny kode til medarbejderen på e-mail</span>
          </label>
          {fejl && (
            <p className="text-[13px] text-red-500 bg-red-500/10 border border-red-500/20 rounded-lg px-3 py-2">{fejl}</p>
          )}
        </div>
        <div className="flex justify-end gap-2 px-6 pb-5">
          <button onClick={onClose} className="border border-border bg-background text-sm font-medium px-4 py-2 rounded-xl hover:bg-secondary transition-colors focus:outline-none focus:ring-2 focus:ring-[#FF2A6D]/30">Annullér</button>
          <button onClick={handleGem} disabled={saving}
            className="flex items-center gap-1.5 px-5 py-2 rounded-xl bg-[#FF2A6D] text-white text-sm font-semibold hover:opacity-90 transition-opacity disabled:opacity-60 focus:outline-none focus:ring-2 focus:ring-[#FF2A6D]/30">
            {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <KeyRound className="w-4 h-4" />}
            Gem ny kode
          </button>
        </div>
      </div>
    </ModalPortal>
  );
}

const FANER = [
  { id: "stamdata",    label: "Stamdata",                            icon: User },
  { id: "arbejdsplan", label: "Arbejdsplan",                         icon: Calendar },
  { id: "ansaettelse", label: "Fortrolige medarbejderoplysninger",   icon: Briefcase },
  { id: "cv",          label: "CV",                                  icon: FileText },
  { id: "log",         label: "Log",                                 icon: History },
];

// Subfaner under Ansættelse — "Historik" er fjernet som subtab og er nu en
// udfoldelig dropdown nederst på "Nuværende ansættelse"-visningen.
// #40.1: Feriebank-fanen er fjernet — ferie justeres nu fra Arbejdsplan-fanens
// Ferie-boks (blyant-ikon). Tilbage: Nuværende ansættelse, Lønberegner, Dokumenter.
const ANSAETTELSE_SUBTABS = [
  { id: "nuvAnsaettelse", label: "Nuværende ansættelse" },
  { id: "loenberegner",   label: "Lønberegner" },
  { id: "dokumenter",     label: "Dokumenter" },
];

// ─── Hoved-komponent ────────────────────────────────────────────────────────
export default function MedarbejdereProfilPage() {
  const { item_id } = useParams();
  const { user, token, logout, hasRettighed } = useAuth();
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const cvDocInputRef = useRef(null);

  const [profil, setProfil] = useState(null);
  const [stamdata, setStamdata] = useState({});
  const [dokumenter, setDokumenter] = useState([]);
  const [kontrakter, setKontrakter] = useState([]);
  const [log, setLog] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [uploadingDoc, setUploadingDoc] = useState(false);
  const [uploadingKontrakt, setUploadingKontrakt] = useState(false);
  const [aktivFane, setAktivFane] = useState("stamdata");
  const [ansaettelseSubTab, setAnsaettelseSubTab] = useState("nuvAnsaettelse");
  // Ansættelses-historik (perioder) — vises på Stamdata kun hvis >1 periode.
  const [ansHistorik, setAnsHistorik] = useState([]);
  const [showOpsigModal, setShowOpsigModal] = useState(false);
  const [opsigSaving, setOpsigSaving] = useState(false);
  // Toast for lønændring med Fortryd-knap (vises i 30 sek efter ny løn er gemt).
  const [loenToast, setLoenToast] = useState(null); // { gammelLoen, nyLoen, expiresAt }
  const [visHistorikDropdown, setVisHistorikDropdown] = useState(false);
  // #38.2: gyldig-fra-dato for ændring af "Timer pr. uge" (kontrakttimer-historik).
  const [timerGyldigFra, setTimerGyldigFra] = useState("");
  const kontraktInputRef = useRef(null);
  // Ansættelses-selskaber (hentes til dropdown i Fortrolige-fanen)
  const [selskaber, setSelskaber] = useState([]);

  // ── Adgangskontekst ─────────────────────────────────────────────────────
  const isSuperAdmin = ["superadmin", "owner", "api_admin"].includes(user?.role);
  const isAdmin = ["admin", "superadmin", "owner", "api_admin"].includes(user?.role);
  const rettighederHar = user?.rettigheder || {};
  const kanSeMedarbejdere = isAdmin || !!rettighederHar.kan_se_medarbejdere;
  const [accessLevel, setAccessLevel] = useState(isSuperAdmin ? "superadmin" : (isAdmin ? "admin" : null));
  const [accessDenied, setAccessDenied] = useState(false);

  const handleLogout = () => { logout(); navigate("/login"); };

  const load = useCallback(() => {
    if (!user) { navigate("/"); return; }
    // Adgang: superadmin, admin, eller medarbejder med kan_se_medarbejdere.
    // Medarbejdere uden den rettighed kan stadig se deres EGET stamkort.
    if (!isSuperAdmin && !isAdmin && !kanSeMedarbejdere) {
      const ownId = user?.medarbejder_item_id;
      if (!ownId || String(ownId) !== String(item_id)) {
        setAccessDenied(true);
        setLoading(false);
        return;
      }
    }
    setAccessDenied(false);
    setLoading(true);
    axios.get(`${API}/admin/medarbejdere/${item_id}`, {
      headers: { Authorization: `Bearer ${token}` },
    }).then(r => {
      setProfil(r.data.profil || {});
      setStamdata(r.data.stamdata || {});
      setDokumenter(r.data.dokumenter || []);
      setKontrakter(r.data.kontrakter || []);
      setLog(r.data.log || []);
      setAccessLevel(r.data.access_level || (isSuperAdmin ? "superadmin" : "admin"));
    }).catch((err) => {
      if (err?.response?.status === 403) setAccessDenied(true);
    }).finally(() => setLoading(false));
  }, [item_id, user, token, navigate, isSuperAdmin, isAdmin, kanSeMedarbejdere]);

  useEffect(() => { load(); }, [load]);

  // Hent selskaber til Ansættelses-selskab-dropdown (kun for superadmin)
  useEffect(() => {
    if (!isSuperAdmin || !token) return;
    axios.get(`${API}/admin/selskaber`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => setSelskaber(r.data || []))
      .catch(() => {});
  }, [isSuperAdmin, token]);

  // Hent ansættelses-historik (perioder) til Stamdata-fanens historik-boks.
  // Egen useEffect så den ikke blokerer hovedindlæsningen; fejler stille.
  useEffect(() => {
    if (!item_id || !token) return;
    let aktiv = true;
    axios.get(`${API}/admin/medarbejdere/${item_id}/ansaettelses-historik`, {
      headers: { Authorization: `Bearer ${token}` },
    }).then(r => {
      if (aktiv) setAnsHistorik(r.data?.ansaettelses_historik || []);
    }).catch(() => { if (aktiv) setAnsHistorik([]); });
    return () => { aktiv = false; };
  }, [item_id, token]);

  const val = (key) => stamdata[key] !== undefined && stamdata[key] !== null ? stamdata[key] : (profil || {})[key];
  const set = (key) => (v) => setStamdata(s => ({ ...s, [key]: v }));

  // #40.2: "Gælder fra"-feltet vises KUN når et ansættelsesfelt der kræver en
  // gyldighedsdato faktisk er ændret (timer pr. uge, løn, pension, afdeling).
  // Default sættes til i dag når feltet dukker op; forsvinder igen hvis ændringen
  // fortrydes. Sammenlign stamdata-edit mod den oprindeligt indlæste profil.
  const GYLDIGFRA_FELTER = ["timer_pr_uge", "loen", "pension_arbejdsgiver", "pension_medarbejder", "afdeling"];
  const kraeverGyldigFra = useMemo(() => {
    if (!profil) return false;
    return GYLDIGFRA_FELTER.some(k => {
      if (!(k in stamdata)) return false;
      const ny = stamdata[k];
      const gammel = (profil || {})[k];
      return String(ny ?? "") !== String(gammel ?? "");
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stamdata, profil]);
  useEffect(() => {
    if (kraeverGyldigFra) {
      // Sæt default til i dag når feltet først dukker op (men overskriv ikke
      // en værdi brugeren allerede har valgt).
      setTimerGyldigFra(prev => prev || new Date().toISOString().slice(0, 10));
    } else {
      setTimerGyldigFra("");
    }
  }, [kraeverGyldigFra]);

  const cvPoster = val("cv_poster") || [];
  const setCvPoster = (v) => set("cv_poster")(v);
  const kurser = val("kurser") || [];
  const setKurser = (v) => set("kurser")(v);
  // #41.2: ny selvstændig Uddannelse-sektion (separat fra Kurser).
  const uddannelse = val("uddannelse") || [];
  const setUddannelse = (v) => set("uddannelse")(v);
  // #43: fritekst "Lidt om mig" (string, ikke liste).
  const omkringMig = val("omkring_mig") || "";
  const setOmkringMig = (v) => set("omkring_mig")(v);
  // #43: hvilket kort er i input-mode pr. sektion. null = ingen.
  //  - tal: index på eksisterende post der redigeres
  //  - "new": nyt tomt kort der tilføjes
  const [cvEditing, setCvEditing] = useState(null);
  const [uddEditing, setUddEditing] = useState(null);
  const [kurEditing, setKurEditing] = useState(null);
  const [omkringEditing, setOmkringEditing] = useState(false);
  // #43: "Generer CV" (kun superadmin) — placeholder-hook indtil backend bygges.
  // onGenererCv kan senere injiceres (fx via prop/context); indtil da → undefined,
  // hvorefter knappen falder tilbage til placeholder-dialogen.
  const onGenererCv = undefined;
  const [showGenererCv, setShowGenererCv] = useState(false);
  const [showAendreKode, setShowAendreKode] = useState(false);
  const [cvSaving, setCvSaving] = useState(false);
  const advarsler = val("advarsler") || [];
  const setAdvarsler = (v) => set("advarsler")(v);
  const ansaettelseshistorik = (profil || {}).ansaettelseshistorik || [];

  // Opf. #59: gem ét enkelt stamdata-felt øjeblikkeligt (uden den globale
  // Gem-knap). Bruges af toggles "Tæl ikke timer" + "Kørselsregistrering", så
  // ændringen persisteres med det samme. Opdaterer lokal state optimistisk og
  // ruller tilbage ved fejl.
  const putStamdataFelt = async (key, value) => {
    const forrige = stamdata[key];
    setStamdata(s => ({ ...s, [key]: value }));
    try {
      await axios.put(`${API}/admin/medarbejdere/${item_id}/stamdata`, { [key]: value }, {
        headers: { Authorization: `Bearer ${token}` },
      });
    } catch {
      setStamdata(s => ({ ...s, [key]: forrige }));
      alert("Kunne ikke gemme ændringen");
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      // #38.2: send gyldig-fra-dato med hvis timer_pr_uge ændres, så
      // kontrakttimer-historikken får korrekt ikrafttrædelse (ikke bagud).
      const payload = { ...stamdata };
      if (timerGyldigFra) payload.timer_pr_uge_gyldig_fra = timerGyldigFra;
      await axios.put(`${API}/admin/medarbejdere/${item_id}/stamdata`, payload, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setTimerGyldigFra("");
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
      load();
    } catch { alert("Kunne ikke gemme stamdata"); }
    setSaving(false);
  };

  // #41.1/#43: CV gemmes via et dedikeret endpoint, så medarbejderen selv kan
  // redigere sin egen CV (admin-only stamdata-endpoint blokerer ellers members).
  // #43: delt save-funktion der ALTID sender hele CV-payloaden. `overrides`
  // tillader at sende friske værdier direkte (undgår stale-closure ved gem-pr-post,
  // hvor setStamdata endnu ikke er flushet). Returnerer true ved succes.
  const saveCvData = async (overrides = {}) => {
    const payload = {
      cv_poster: cvPoster,
      uddannelse,
      kurser,
      omkring_mig: omkringMig,
      ...overrides,
    };
    setCvSaving(true);
    try {
      await axios.put(`${API}/admin/medarbejdere/${item_id}/cv`, payload, {
        headers: { Authorization: `Bearer ${token}` },
      });
      // Synk lokal stamdata med det gemte, så UI matcher serveren uden reload.
      setStamdata(s => ({ ...s, ...payload }));
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
      setCvSaving(false);
      return true;
    } catch {
      alert("Kunne ikke gemme CV");
      setCvSaving(false);
      return false;
    }
  };

  const handleOpsig = async (aarsag, dato) => {
    setOpsigSaving(true);
    try {
      await axios.put(`${API}/admin/medarbejdere/${item_id}/stamdata`, { status: aarsag, ophørsdato: dato }, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setShowOpsigModal(false);
      load();
    } catch { alert("Kunne ikke gemme opsigelse"); }
    setOpsigSaving(false);
  };

  const handleDocUpload = async (e, kilde = "ansaettelse") => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingDoc(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await axios.post(`${API}/admin/medarbejdere/${item_id}/dokument`, fd, {
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "multipart/form-data" },
      });
      setDokumenter(prev => [{ ...r.data }, ...prev]);
    } catch { alert("Upload fejlede"); }
    setUploadingDoc(false);
    e.target.value = "";
  };

  const handleKontraktUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingKontrakt(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await axios.post(`${API}/admin/medarbejdere/${item_id}/kontrakt`, fd, {
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "multipart/form-data" },
      });
      setDokumenter(prev => [{ ...r.data }, ...prev]);
    } catch { alert("Upload fejlede"); }
    setUploadingKontrakt(false);
    e.target.value = "";
  };

  const handleDocDelete = async (doc_id, filename) => {
    if (!window.confirm(`Slet "${filename}"?`)) return;
    await axios.delete(`${API}/admin/medarbejdere/${item_id}/dokument/${doc_id}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    setDokumenter(prev => prev.filter(d => d.doc_id !== doc_id));
  };

  const [viewingDoc, setViewingDoc] = useState(null); // { doc_id, filename, content_type, blobUrl }

  const downloadDoc = async (doc_id, filename) => {
    const r = await fetch(`${API}/admin/medarbejdere/${item_id}/dokument/${doc_id}`, { headers: { Authorization: `Bearer ${token}` } });
    const blob = await r.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = filename; a.click();
    URL.revokeObjectURL(url);
  };

  const isViewable = (ct = "") => ct.includes("pdf") || ct.includes("image");

  const openDoc = async (doc) => {
    if (!isViewable(doc.content_type)) { downloadDoc(doc.doc_id, doc.filename); return; }
    const r = await fetch(`${API}/admin/medarbejdere/${item_id}/dokument/${doc.doc_id}?inline=true`, { headers: { Authorization: `Bearer ${token}` } });
    const blob = await r.blob();
    const blobUrl = URL.createObjectURL(blob);
    setViewingDoc({ ...doc, blobUrl });
  };

  const closeDoc = () => {
    if (viewingDoc?.blobUrl) URL.revokeObjectURL(viewingDoc.blobUrl);
    setViewingDoc(null);
  };

  // Lønændring fortryd-flow: kaldes fra LoenBeregner når en ny løn er gemt.
  // Toast vises i 30 sek og kan fortrydes inden for samme vindue.
  const visLoenToast = (gammelLoen, nyLoen) => {
    setLoenToast({
      gammelLoen,
      nyLoen,
      expiresAt: Date.now() + 30000,
    });
  };

  useEffect(() => {
    if (!loenToast) return;
    const t = setTimeout(() => setLoenToast(null), Math.max(0, loenToast.expiresAt - Date.now()));
    return () => clearTimeout(t);
  }, [loenToast]);

  const fortrydLoenAendring = async () => {
    try {
      await axios.post(`${API}/admin/medarbejdere/${item_id}/loen/fortryd-seneste`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setLoenToast(null);
      load();
    } catch (e) {
      alert(e?.response?.data?.detail || "Kunne ikke fortryde lønændring");
    }
  };

  if (accessDenied) return (
    <div className="min-h-screen bg-background text-foreground">
      <DashboardHeader user={user} onLogout={handleLogout} />
      <AccessDenied
        title="Adgang begrænset"
        message="Du har ikke adgang til at se denne medarbejders stamkort. Kontakt en superadmin hvis du har brug for at se denne information."
      />
    </div>
  );

  if (loading) return (
    <div className="min-h-screen bg-background text-foreground">
      <DashboardHeader user={user} onLogout={handleLogout} />
      <div className="flex items-center justify-center py-24 text-muted-foreground text-sm">Henter profil...</div>
    </div>
  );

  if (!profil) return (
    <div className="min-h-screen bg-background text-foreground">
      <DashboardHeader user={user} onLogout={handleLogout} />
      <div className="flex items-center justify-center py-24 text-muted-foreground text-sm">Medarbejder ikke fundet</div>
    </div>
  );

  // ── Tab-synlighed baseret på adgangsniveau ──────────────────────────────
  // - Superadmin: alle tabs (Stamdata, Fortrolige medarbejderoplysninger, CV, Log)
  // - Admin/Member m. `kan_se_fortrolige_medarbejderoplysninger`: samme som superadmin
  // - Admin uden rettighed: kun Stamdata — fortrolige fanen er off-limits
  // - Self (medarbejder ser sit eget): kun Stamdata
  const kanSeFortrolige = isSuperAdmin
    || accessLevel === "superadmin"
    || accessLevel === "fortrolig"
    || (hasRettighed && hasRettighed("kan_se_fortrolige_medarbejderoplysninger"));

  // #40.3 Arbejdsplan-fane synlighed:
  //  - medarbejderen selv (egen profil) — dog IKKE rollen 'light' (tilkalder/læge
  //    uden saldo-tracking)
  //  - admin/superadmin (alle medarbejdere)
  // Generel kan_se_vagtplan giver IKKE længere adgang til Arbejdsplan-fanen.
  const erEgenProfil = !!user?.medarbejder_item_id && String(user.medarbejder_item_id) === String(item_id);
  const erLight = user?.role === "light";
  const kanSeArbejdsplan = isAdmin || (erEgenProfil && !erLight);
  // #41.1: CV-fanen — medarbejderen selv (ikke light) + admin/super. Light ingen adgang.
  const kanSeCv = isAdmin || (erEgenProfil && !erLight);
  // #44.6: en medarbejder der ser sit EGET stamkort må kun SE Stamdata (ikke
  // redigere). Kun admin/superadmin kan redigere stamdata-felter + Gem-knap.
  const kanRedigereStamdata = isAdmin;

  const synligeFaner = FANER.filter(f => {
    if (f.id === "stamdata") return true;
    if (f.id === "arbejdsplan") return kanSeArbejdsplan;
    if (f.id === "cv") return kanSeCv;
    // Fortrolige (ansaettelse) + Log kræver fortrolig-adgang.
    return kanSeFortrolige;
  });
  // Hvis aktivFane ikke er synlig (fx admin lander på "ansaettelse"), tving til stamdata
  const aktivFaneSafe = synligeFaner.some(f => f.id === aktivFane) ? aktivFane : "stamdata";

  const statusVal = val("status") || "Aktiv";
  const sStyle = STATUS_STYLES[statusVal] || {};
  const alleEmails = val("alle_emails") || [];
  const emailRaw = val("email") || val("arbejds_email") || "";
  const arbejdsEmailRaw = val("arbejds_email") || val("email") || "";
  const arbejdsEmail = alleEmails.find(e => isArbeejdsmail(e)) || (isArbeejdsmail(arbejdsEmailRaw) ? arbejdsEmailRaw : null);
  const privatEmail = alleEmails.find(e => !isArbeejdsmail(e)) || (!isArbeejdsmail(emailRaw) ? emailRaw : null);

  return (
    <div className="min-h-screen bg-background text-foreground">
      <DashboardHeader user={user} onLogout={handleLogout} />
      <div className="max-w-[960px] mx-auto px-4 sm:px-6 lg:px-8 py-8">

        {/* Tilbage + Opsig-knap */}
        <div className="flex items-center justify-between mb-6">
          <button onClick={() => navigate("/admin/medarbejdere")}
            className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors">
            <ArrowLeft className="w-4 h-4" /> Tilbage til medarbejdere
          </button>
          {(() => {
            const ophDato = val("ophørsdato");
            const today = new Date().toISOString().slice(0, 10);
            const erOphørt = ophDato && ophDato <= today;
            if (erOphørt) {
              return (
                <span className="flex items-center gap-1.5 text-[13px] font-semibold px-3 py-1.5 rounded-full bg-red-500/10 text-red-500 border border-red-500/20">
                  <UserX className="w-3.5 h-3.5" /> Ophørt siden {fmtDate(ophDato)}
                </span>
              );
            }
            // Opsig-knap er kun synlig for superadmin (ikke admin, ikke selv-opslag)
            if (accessLevel !== "superadmin") return null;
            return (
              <button onClick={() => setShowOpsigModal(true)}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-red-500/10 text-red-500 border border-red-500/20 text-[13px] font-semibold hover:bg-red-500/20 transition-colors">
                <UserX className="w-3.5 h-3.5" /> Opsig medarbejder
              </button>
            );
          })()}
        </div>
        {showOpsigModal && (
          <OpsigModal onClose={() => setShowOpsigModal(false)} onConfirm={handleOpsig} saving={opsigSaving} />
        )}

        {/* Hero-kort */}
        <div className="rounded-2xl border border-border p-6 mb-6 flex flex-col sm:flex-row items-start sm:items-center gap-5">
          <div className="relative shrink-0">
            {profil.image_file_id ? (
              <img src={`${API}/podio/image/${profil.image_file_id}`} alt={profil.name}
                className="w-24 h-24 rounded-full object-cover"
                style={{ border: `3px solid ${sStyle.ring || "#FF2A6D"}` }} />
            ) : (
              <div className="w-24 h-24 rounded-full bg-secondary flex items-center justify-center"
                style={{ border: `3px solid ${sStyle.ring || "#FF2A6D"}` }}>
                <span className="text-3xl font-bold text-muted-foreground">{profil.name?.charAt(0) || "?"}</span>
              </div>
            )}
          </div>
          <div className="flex-1 min-w-0">
            <h1 className="text-2xl font-bold tracking-tight">{profil.name || "—"}</h1>
            <p className="text-sm text-muted-foreground mt-0.5">{val("stilling") || "Ingen stilling angivet"}</p>
            <div className="flex flex-wrap items-center gap-2 mt-2.5">
              {val("afdeling") && (
                <span className="text-[13px] bg-secondary px-2.5 py-1 rounded-full text-muted-foreground">
                  {displayCity(val("afdeling"))}
                </span>
              )}
              {val("ansaettelsestype") && (
                <span className="text-[13px] bg-secondary px-2.5 py-1 rounded-full text-muted-foreground">
                  {ANSAETTELSESTYPE_LABEL[val("ansaettelsestype")] || val("ansaettelsestype")}
                </span>
              )}
              {/* #44.6: kun admin må ændre status; medarbejder ser den read-only. */}
              {kanRedigereStamdata ? (
                <select value={statusVal} onChange={e => set("status")(e.target.value)}
                  className={`appearance-none text-[13px] font-semibold rounded-full px-2.5 py-1 border-0 focus:outline-none cursor-pointer ${sStyle.bg || "bg-secondary"} ${sStyle.text || "text-muted-foreground"}`}>
                  {STATUS_OPTIONS.map(o => <option key={o} value={o}>{o}</option>)}
                </select>
              ) : (
                <span className={`text-[13px] font-semibold rounded-full px-2.5 py-1 ${sStyle.bg || "bg-secondary"} ${sStyle.text || "text-muted-foreground"}`}>{statusVal}</span>
              )}
            </div>
          </div>
          {accessLevel === "superadmin" && (
            <div className="flex items-center gap-2 shrink-0">
              <button onClick={handleSave} disabled={saving}
                className="flex items-center gap-1.5 px-5 py-2 rounded-xl bg-[#FF2A6D] text-white text-sm font-semibold hover:bg-[#e02560] transition-colors disabled:opacity-60">
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                {saved ? "Gemt!" : "Gem"}
              </button>
            </div>
          )}
        </div>

        {/* Fane-navigation */}
        <div className="flex flex-wrap gap-1.5 mb-6 border-b border-border pb-4">
          {synligeFaner.map(({ id, label, icon: Icon }) => (
            <button key={id} onClick={() => setAktivFane(id)}
              className={`flex items-center gap-1.5 px-4 py-2 rounded-xl text-sm font-semibold transition-colors ${
                aktivFaneSafe === id
                  ? "bg-foreground text-background"
                  : "bg-secondary text-muted-foreground hover:text-foreground hover:bg-secondary/80"
              }`}>
              <Icon className="w-3.5 h-3.5" />
              {label}
            </button>
          ))}
        </div>

        {/* ── STAMDATA FANE ─────────────────────────────────── */}
        {/* Indeholder kun grund-stamdata + sammenfatning. Bank, CPR, kontrakt og
            ad-hoc-tilladelse er flyttet til Ansættelse-fanen. */}
        {aktivFaneSafe === "stamdata" && (
          <div className="flex flex-col gap-5">
            <div className="rounded-xl border border-border p-6">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <EditableField label="Navn" value={profil.name} onChange={() => {}} readOnly />
                <div>
                  <p className="text-[13px] text-muted-foreground mb-0.5">Podio medarbejder-ID</p>
                  <p className="text-sm font-medium font-mono text-muted-foreground">{profil.app_item_id || profil.item_id || "—"}</p>
                </div>
                <div className="sm:col-span-2">
                  <EditableField label="Adresse" value={val("adresse")} onChange={set("adresse")} readOnly={!kanRedigereStamdata} />
                </div>
                <EditableField label="Privat telefon" value={val("telefon")} onChange={set("telefon")} readOnly={!kanRedigereStamdata} />
                <EditableField label="Privat e-mail" value={privatEmail || val("email")} onChange={set("email")} readOnly={!kanRedigereStamdata} />
                {/* Arbejdstelefon — kanon-feltet hedder `arbejds_telefon`
                    (samme format som `arbejds_email`). Fall'er tilbage på det
                    gamle `arbejdstelefon`-felt så ældre records også vises. */}
                <EditableField label="Arbejdstelefon" value={val("arbejds_telefon") || val("arbejdstelefon")} onChange={set("arbejds_telefon")} readOnly={!kanRedigereStamdata} />
                <EditableField label="Arbejds-e-mail" value={arbejdsEmail || val("arbejds_email")} onChange={set("arbejds_email")} readOnly={!kanRedigereStamdata} />
                {/* Adgangskode — kun synlig for superadmin og medarbejderen selv */}
                {(isSuperAdmin || erEgenProfil) && (
                  <div>
                    <p className="text-[13px] text-muted-foreground mb-0.5">Adgangskode</p>
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-muted-foreground tracking-widest">••••••</span>
                      <button onClick={() => setShowAendreKode(true)}
                        className="flex items-center gap-1 px-2.5 py-1 rounded-lg border border-border text-[12px] font-semibold hover:bg-secondary transition-colors">
                        <KeyRound className="w-3.5 h-3.5" /> Ændre kode
                      </button>
                    </div>
                  </div>
                )}
                {/* Fødselsdag: brug foedselsdag-feltet hvis det findes (udledt og
                    gemt da CPR blev maskeret). CPR er nu altid DDMMYY-XXXX, så
                    fmtFoedselsdagFraCpr kan ikke laengere udlede aar fra 7. ciffer. */}
                <div>
                  <p className="text-[13px] text-muted-foreground mb-0.5">Fødselsdag</p>
                  <p className="text-sm font-medium">
                    {fmtFoedselsdagFraIso(val("foedselsdag")) !== "—"
                      ? fmtFoedselsdagFraIso(val("foedselsdag"))
                      : fmtFoedselsdagFraCpr(val("cpr"))}
                  </p>
                </div>
                <div>
                  <p className="text-[13px] text-muted-foreground mb-0.5">Afdeling</p>
                  {(isSuperAdmin || isAdmin) ? (
                    <select value={val("afdeling") || ""}
                      onChange={e => set("afdeling")(e.target.value)}
                      className="w-full bg-secondary border border-border rounded-lg px-3 h-9 text-sm text-foreground focus:outline-none focus:ring-1 focus:ring-[#FF2A6D]">
                      <option value="">— vælg —</option>
                      {ALLE_BYER.map(b => <option key={b} value={b}>{b}</option>)}
                    </select>
                  ) : (
                    <span className="inline-flex items-center px-2 py-1 rounded-md bg-secondary text-sm">{val("afdeling") || "—"}</span>
                  )}
                </div>
                <EditableField label="Stilling" value={val("stilling")} onChange={set("stilling")} readOnly={!kanRedigereStamdata} />
                {isSuperAdmin && (
                  <div className="sm:col-span-2">
                    <p className="text-[13px] text-muted-foreground mb-0.5">Ansættelsestype</p>
                    <div className="flex flex-wrap gap-2">
                      {ANSAETTELSESFORM_OPTIONS.map(o => {
                        const aktiv = (val("ansaettelsestype") || "") === o.value;
                        return (
                          <button key={o.value} type="button"
                            onClick={() => set("ansaettelsestype")(aktiv ? "" : o.value)}
                            className={`px-3 py-1.5 rounded-full text-[13px] font-semibold border transition-colors ${
                              aktiv
                                ? "bg-[#FF2A6D] text-white border-[#FF2A6D]"
                                : "bg-secondary text-muted-foreground border-border hover:text-foreground"
                            }`}>
                            {o.label}
                          </button>
                        );
                      })}
                    </div>
                    {val("ansaettelsestype") === "vikar" && (
                      <p className="text-[13px] text-muted-foreground italic mt-1.5">
                        Vikarer har som standard ingen fastlagte timer og bookes ad-hoc via vagtplanen.
                      </p>
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* #39.3: "Sammenfatning"-boksen er fjernet fra Stamdata. Tallene
                ligger nu i Arbejdsplan-fanen (denne uge, lønperiode, ferie, syg).
                Den manuelle ferie-justering er flyttet til Ferie-boksen dér. */}

            {/* Ansættelseshistorik — vis kun hvis medarbejderen har været ansat
                mere end én periode (dvs. er blevet genansat mindst én gang). */}
            {ansHistorik.length > 1 && (
              <div className="rounded-xl border border-border p-6">
                <div className="flex items-center gap-2 mb-4">
                  <History className="w-4 h-4 text-muted-foreground" />
                  <h3 className="text-[13px] font-bold uppercase tracking-wider text-muted-foreground">Ansættelseshistorik</h3>
                </div>
                <div className="flex flex-col gap-3">
                  {[...ansHistorik]
                    .sort((a, b) => (b.start || "").localeCompare(a.start || ""))
                    .map((p, i) => {
                      const nuvaerende = !p.slut;
                      const typeLabel = ANSAETTELSESTYPE_LABEL[p.ansaettelsestype] || p.ansaettelsestype || "";
                      const stillingLinje = [
                        p.stilling ? `Stilling: ${p.stilling}` : null,
                        [typeLabel, p.loen != null && p.loen !== "" ? String(p.loen) : null].filter(Boolean).join(" ") || null,
                        p.afdeling ? displayCity(p.afdeling) : null,
                        (p.timer_uge != null && p.timer_uge !== "") ? `${p.timer_uge} t/uge` : null,
                      ].filter(Boolean).join(" · ");
                      return (
                        <div key={`${p.periode_nr ?? i}-${p.start || i}`}
                          className={`rounded-lg border p-3 ${nuvaerende ? "border-[#FF2A6D]/40 bg-[#FF2A6D]/5" : "border-border bg-secondary/20"}`}>
                          <p className="text-sm font-semibold">
                            Periode {p.periode_nr ?? (ansHistorik.length - i)}
                            {nuvaerende && <span className="text-[#FF2A6D]"> (nuværende)</span>}
                            {": "}
                            {fmtDate(p.start)}{" – "}{p.slut ? fmtDate(p.slut) : ""}
                          </p>
                          {stillingLinje && (
                            <p className="text-[13px] text-muted-foreground mt-0.5">{stillingLinje}</p>
                          )}
                          {!nuvaerende && (p.opsagt_af || p.opsagt_aarsag) && (
                            <p className="text-[13px] text-muted-foreground mt-0.5">
                              {p.opsagt_af ? `Opsagt af: ${p.opsagt_af}` : ""}
                              {p.opsagt_af && p.opsagt_aarsag ? " · " : ""}
                              {p.opsagt_aarsag ? `Årsag: ${p.opsagt_aarsag}` : ""}
                            </p>
                          )}
                        </div>
                      );
                    })}
                </div>
              </div>
            )}

            {/* Arbejdslokationer er flyttet til Ansættelse-fanen under
                "Arbejdsrelationer" — samme komponent vises kun ét sted nu. */}

            {/* Rettigheder er flyttet til Fortrolige medarbejderoplysninger-fanen (#66) */}
          </div>
        )}

        {/* ── ARBEJDSPLAN FANE (#37) ────────────────────────── */}
        {aktivFaneSafe === "arbejdsplan" && (
          <ArbejdsplanFane
            itemId={item_id}
            token={token}
            medarbejderNavn={profil?.name}
            kontraktTimerPrUge={parseFloat(val("timer_pr_uge")) || 0}
            erEgenProfil={erEgenProfil}
            kanSeFortrolige={kanSeFortrolige}
            isSuperAdmin={isSuperAdmin}
            ansaettelsestype={val("ansaettelsestype") || "manedsloen"}
            ansaettelsesdato={(val("anciennitetsdato") || val("ansaettelsesdato") || "").slice(0,10)}
            taelIkkeTimer={!!val("tael_ikke_timer")}
            koerselAktiveret={!!val("koersel_aktiveret")}
            onToggleTaelIkke={kanRedigereStamdata ? (v) => putStamdataFelt("tael_ikke_timer", v) : undefined}
          />
        )}

        {/* ── ANSÆTTELSE FANE ───────────────────────────────── */}
        {aktivFaneSafe === "ansaettelse" && (() => {
          const ansType = (val("ansaettelsestype") || "manedsloen");
          const erTime = ansType === "timeloen";
          const erVikar = ansType === "vikar";
          // #40.1: Feriebank-fanen er fjernet helt (ferie justeres fra Arbejdsplan).
          const subtabs = ANSAETTELSE_SUBTABS;
          // Fald tilbage hvis brugeren står på en subtab der ikke længere vises.
          const aktivSub = subtabs.some(st => st.id === ansaettelseSubTab)
            ? ansaettelseSubTab
            : "nuvAnsaettelse";
          return (
          <div className="flex flex-col gap-5">
            <div className="flex gap-1.5 flex-wrap">
              {subtabs.map(st => (
                <button key={st.id} onClick={() => setAnsaettelseSubTab(st.id)}
                  className={`px-4 py-1.5 rounded-full text-sm font-semibold transition-colors ${
                    aktivSub === st.id
                      ? "bg-[#FF2A6D] text-white"
                      : "bg-secondary text-muted-foreground hover:text-foreground hover:bg-secondary/80"
                  }`}>
                  {st.label}
                </button>
              ))}
            </div>

            {/* Rettigheder — flyttes hertil fra Stamdata-fanen (#66) */}
            {accessLevel === "superadmin" ? (
              <RettighedSektion
                itemId={item_id}
                token={token}
                aktiveRettigheder={val("rettigheder") || {}}
                onUpdated={load}
                rolle={val("rolle") || (profil?.role) || "member"}
                brugerNavn={profil?.name || ""}
                primaerAfdeling={val("afdeling") || ""}
              />
            ) : kanRedigereStamdata && (
              <div className="rounded-xl border border-border p-5">
                <h3 className="text-[13px] font-bold text-muted-foreground uppercase tracking-wider mb-3 flex items-center gap-1.5">
                  <Shield className="w-3.5 h-3.5" /> Rettigheder
                </h3>
                <p className="text-[13px] text-muted-foreground">Administreres af superadmin.</p>
              </div>
            )}

            {aktivSub === "nuvAnsaettelse" && (
              <div className="flex flex-col gap-5">
                <div className="rounded-xl border border-border p-5">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-[13px] font-bold text-muted-foreground uppercase tracking-wider">Ansættelsesoplysninger</h3>
                    {/* Read-only ansættelsestype-badge — primær redigering sker på Stamdata-fanen */}
                    {(() => {
                      const t = val("ansaettelsestype");
                      if (!t) return null;
                      const cls = t === "timeloen"
                        ? "bg-blue-500/10 text-blue-500"
                        : t === "vikar"
                          ? "bg-amber-500/10 text-amber-500"
                          : "bg-emerald-500/10 text-emerald-500";
                      return (
                        <span
                          title="Redigeres på Stamdata-fanen"
                          className={`text-[13px] font-semibold px-2.5 py-0.5 rounded-full ${cls}`}>
                          {ANSAETTELSESTYPE_LABEL[t] || t}
                        </span>
                      );
                    })()}
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                    <div>
                      <p className="text-[13px] text-muted-foreground mb-0.5">Anciennitet</p>
                      {(val("anciennitetsdato") || val("ansaettelsesdato")) ? (() => {
                        const dato = val("anciennitetsdato") || val("ansaettelsesdato");
                        const [y, m, d] = dato.split("-");
                        const fra = new Date(dato);
                        const nu = new Date();
                        const mdr = (nu.getFullYear() - fra.getFullYear()) * 12 + (nu.getMonth() - fra.getMonth());
                        const aarVis = Math.floor(mdr / 12);
                        const mdrRest = mdr % 12;
                        const varighedStr = aarVis > 0
                          ? `${aarVis} år${mdrRest > 0 ? ` og ${mdrRest} md.` : ""}`
                          : `${mdrRest} md.`;
                        return (
                          <div>
                            <EditableField label="" type="date" value={dato} onChange={set("anciennitetsdato")} readOnly={!isSuperAdmin} />
                            <p className="text-[13px] text-muted-foreground mt-0.5">{d}.{m}.{y} · ansat i {varighedStr}</p>
                          </div>
                        );
                      })() : (
                        <EditableField label="" type="date" value="" onChange={set("anciennitetsdato")} readOnly={!isSuperAdmin} />
                      )}
                    </div>
                    {/* Timer pr. uge — skjult for vikarer (vikar = ad-hoc, 0 faste timer). */}
                    {!erVikar && (
                      <div>
                        <p className="text-[13px] text-muted-foreground mb-0.5">
                          Timer pr. uge{erTime ? " (forventet)" : ""}
                        </p>
                        <EditableField label="" type="number" value={val("timer_pr_uge")} onChange={set("timer_pr_uge")} readOnly={!isSuperAdmin} />
                        {val("timer_pr_uge") && (
                          <p className="text-[13px] text-muted-foreground mt-0.5">
                            {(parseFloat(val("timer_pr_uge")) * 4.33).toFixed(1)} t/md
                          </p>
                        )}
                      </div>
                    )}
                    {/* Løn-felt — månedsløn for fastansatte, timeløn for timeløns- og vikar-ansatte. */}
                    {(erTime || erVikar) ? (
                      <EditableField
                        label="Timeløn (kr./t)"
                        value={val("loen") || val("manedligt_vederlag")} onChange={set("loen")} readOnly={!isSuperAdmin} />
                    ) : (
                      <EditableField
                        label="Månedsløn (kr./md)"
                        value={val("loen") || val("manedligt_vederlag")} onChange={set("loen")} readOnly={!isSuperAdmin} />
                    )}
                    <div>
                      <p className="text-[13px] text-muted-foreground mb-0.5">Pension arb.giver %</p>
                      {isSuperAdmin ? (
                        <input type="number" step="0.5" min="0" max="100"
                          value={val("pension_arbejdsgiver") ?? ""}
                          onChange={e => set("pension_arbejdsgiver")(e.target.value ? parseFloat(e.target.value) : null)}
                          placeholder="0"
                          className="w-full bg-secondary border border-border rounded-lg px-3 h-9 text-sm text-foreground focus:outline-none focus:ring-1 focus:ring-[#FF2A6D]" />
                      ) : (
                        <p className="text-sm font-medium">{val("pension_arbejdsgiver") ?? "—"}</p>
                      )}
                    </div>
                    <div>
                      <p className="text-[13px] text-muted-foreground mb-0.5">Pension medarbejder %</p>
                      {isSuperAdmin ? (
                        <input type="number" step="0.5" min="0" max="100"
                          value={val("pension_medarbejder") ?? ""}
                          onChange={e => set("pension_medarbejder")(e.target.value ? parseFloat(e.target.value) : null)}
                          placeholder="0"
                          className="w-full bg-secondary border border-border rounded-lg px-3 h-9 text-sm text-foreground focus:outline-none focus:ring-1 focus:ring-[#FF2A6D]" />
                      ) : (
                        <p className="text-sm font-medium">{val("pension_medarbejder") ?? "—"}</p>
                      )}
                    </div>
                    <div>
                      <p className="text-[13px] text-muted-foreground mb-0.5">Afdeling</p>
                      <select value={val("afdeling") || ""}
                        onChange={e => set("afdeling")(e.target.value)}
                        className="w-full bg-secondary border border-border rounded-lg px-3 h-9 text-sm text-foreground focus:outline-none focus:ring-1 focus:ring-[#FF2A6D]">
                        <option value="">— vælg —</option>
                        {ALLE_BYER.map(b => <option key={b} value={b}>{b}</option>)}
                      </select>
                    </div>
                    <EditableField label="Titel/stilling" value={val("stilling")} onChange={set("stilling")} />
                    <div className="sm:col-span-2">
                      <EditableField label="Øvrige vilkår" value={val("ansaettelse_noter")} onChange={set("ansaettelse_noter")} multiline />
                    </div>
                    {/* #40.2: "Gælder fra" vises kun når et felt med gyldighedsdato
                        (timer/løn/pension/afdeling) faktisk er ændret. Default i dag. */}
                    {isSuperAdmin && kraeverGyldigFra && (
                      <div className="sm:col-span-2 rounded-lg border border-[#FF2A6D]/30 bg-[#FF2A6D]/5 px-3 py-2.5">
                        <label className="text-[13px] font-semibold text-foreground block mb-1"
                          title="Den ændrede sats træder først i kraft fra denne dato — den påvirker ikke perioder bagud i timeoversigt/+/- timer.">
                          Gælder fra
                        </label>
                        <input type="date" value={timerGyldigFra} onChange={e => setTimerGyldigFra(e.target.value)}
                          className="bg-background border border-border rounded-lg px-2 h-8 text-[13px] focus:outline-none focus:ring-1 focus:ring-[#FF2A6D]" />
                        <p className="text-[12px] text-muted-foreground mt-1">Ændringen træder i kraft fra denne dato (påvirker ikke bagud).</p>
                      </div>
                    )}
                  </div>
                </div>

                {/* Kørselsregistrering (Opf. #59, DEL B) — aktiveres pr. medarbejder.
                    Gemmer øjeblikkeligt via stamdata-PUT (koersel_aktiveret). */}
                <div className="rounded-xl border border-border p-5">
                  <h3 className="text-[13px] font-bold text-muted-foreground uppercase tracking-wider mb-3 flex items-center gap-1.5">
                    <Car className="w-3.5 h-3.5 text-[#FF2A6D]" /> Kørselsregistrering
                  </h3>
                  <div className={`rounded-lg border px-3 py-2.5 transition-colors ${val("koersel_aktiveret") ? "border-[#FF2A6D] bg-[#FF2A6D]/10" : "border-border bg-secondary/20"}`}>
                    <label className="flex items-center gap-2 cursor-pointer select-none">
                      <input type="checkbox"
                        checked={!!val("koersel_aktiveret")}
                        disabled={!isAdmin}
                        onChange={e => putStamdataFelt("koersel_aktiveret", e.target.checked)}
                        className="w-4 h-4 rounded border-border bg-secondary accent-[#FF2A6D] cursor-pointer disabled:cursor-not-allowed" />
                      <span className={`text-sm font-semibold ${val("koersel_aktiveret") ? "text-[#FF2A6D]" : "text-foreground"}`}>
                        Kørselsregistrering
                      </span>
                    </label>
                    <p className="text-[12px] text-muted-foreground mt-1.5">
                      Aktiverer kørselsregistrering på medarbejderens Arbejdsplan-fane. Bruges til SKAT-dokumentation og Dataløn-import.
                    </p>
                  </div>
                </div>

                {/* Bankoplysninger + CPR (flyttet fra Stamdata til Ansættelse). */}
                <div className="rounded-xl border border-border p-5">
                  <h3 className="text-[13px] font-bold text-muted-foreground uppercase tracking-wider mb-4">
                    Bank &amp; CPR
                  </h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <EditableField label="Reg.nummer" value={val("reg_nummer") || val("bank_reg_nr")} onChange={set("reg_nummer")} readOnly={!isSuperAdmin} />
                    <EditableField label="Kontonummer" value={val("konto_nummer") || val("bank_konto_nr") || val("kontonummer")} onChange={set("konto_nummer")} readOnly={!isSuperAdmin} />
                    <div className="sm:col-span-2">
                      <p className="text-[13px] text-muted-foreground mb-0.5">CPR-nummer</p>
                      <CprFelt cpr={val("cpr")} />
                    </div>
                  </div>
                </div>

                {/* Ansættelses-selskab */}
                {isSuperAdmin && (
                  <div className="rounded-xl border border-border p-5">
                    <h3 className="text-[13px] font-bold text-muted-foreground uppercase tracking-wider mb-4">Ansættelses-selskab</h3>
                    <div className="flex flex-col gap-1.5">
                      <label className="text-[13px] font-medium text-foreground">Selskab</label>
                      <select
                        value={stamdata?.ansaettelses_selskab_id || ""}
                        onChange={e => setStamdata(p => ({ ...p, ansaettelses_selskab_id: e.target.value }))}
                        className="h-9 rounded-xl border border-border bg-background px-3 text-[13px] text-foreground focus:outline-none focus:ring-1 focus:ring-[#FF2A6D]"
                      >
                        <option value="">— Intet selskab —</option>
                        {selskaber.map(s => (
                          <option key={s.selskab_id} value={s.selskab_id}>
                            {s.navn}{s.cvr ? ` (${s.cvr})` : ""}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                )}

                {/* Arbejdsrelationer: geolokation + ad-hoc-tilladelse. */}
                <div className="rounded-xl border border-border p-5">
                  <h3 className="text-[13px] font-bold text-muted-foreground uppercase tracking-wider mb-4">Arbejdsrelationer</h3>
                  <div className="flex flex-col gap-4">
                    <div>
                      <p className="text-[13px] text-muted-foreground mb-1 flex items-center gap-1.5">
                        Ad-hoc stempling
                        <span
                          title="Bruges af medarbejdere der arbejder hjemmefra og selv styrer arbejdstid. De kan stemple ind uden planlagt vagt."
                          className="inline-flex items-center justify-center w-4 h-4 rounded-full bg-secondary text-[13px] font-bold text-muted-foreground cursor-help">
                          ?
                        </span>
                      </p>
                      <label className="flex items-center gap-2 cursor-pointer select-none">
                        <input type="checkbox"
                          checked={!!val("tillad_adhoc_stempling")}
                          onChange={e => set("tillad_adhoc_stempling")(e.target.checked)}
                          className="w-4 h-4 rounded border-border bg-secondary accent-[#FF2A6D] cursor-pointer" />
                        <span className="text-sm">
                          {val("tillad_adhoc_stempling")
                            ? "Tilladt (kan stemple uden planlagt vagt)"
                            : "Tillad ad-hoc stempling (uden planlagt vagt)"}
                        </span>
                      </label>
                    </div>
                    <ExtraLokationerSektion
                      itemId={item_id}
                      token={token}
                      lokationer={profil.extra_lokationer || []}
                      onUpdated={load}
                    />
                  </div>
                </div>

                {/* Kontrakt-sektion med aktiv-badge. */}
                <div className="rounded-xl border border-border p-5">
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="text-[13px] font-bold text-muted-foreground uppercase tracking-wider">Kontrakt</h3>
                    {(() => {
                      const harAktivPodio = kontrakter.some(k => k.er_aktiv);
                      const harUploadet = dokumenter.some(d => d.doc_type === "kontrakt" && !d.periode_id);
                      const aktiv = harAktivPodio || harUploadet;
                      return (
                        <span className={`text-[13px] font-semibold px-2.5 py-0.5 rounded-full ${
                          aktiv ? "bg-green-500/10 text-green-500" : "bg-red-500/10 text-red-500"
                        }`}>
                          {aktiv ? "Aktiv" : "Ikke længere aktiv"}
                        </span>
                      );
                    })()}
                  </div>
                  {/* Aktiv Podio-kontrakt */}
                  {kontrakter.filter(k => k.er_aktiv).length > 0 && (
                    <KontrakterSektion
                      kontrakter={kontrakter.filter(k => k.er_aktiv)}
                      token={token}
                      onSynced={load}
                      onStatusChanged={load}
                      showAktive={true}
                      hideSyncButton={true}
                    />
                  )}
                  {/* Uploadet kontrakt */}
                  {(() => {
                    const nuKontrakt = dokumenter.find(d => d.doc_type === "kontrakt" && !d.periode_id);
                    if (nuKontrakt) {
                      return (
                        <div className="flex items-center gap-3 rounded-xl border border-green-500/30 bg-green-500/5 p-3 group mb-3 mt-2">
                          <span className="text-xl shrink-0">{fileIcon(nuKontrakt.content_type)}</span>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium truncate">{nuKontrakt.filename}</p>
                            <p className="text-[13px] text-muted-foreground">{fmtBytes(nuKontrakt.size)} · {fmtTs(nuKontrakt.uploaded_at)}</p>
                          </div>
                          {isViewable(nuKontrakt.content_type) && (
                            <button onClick={() => openDoc(nuKontrakt)}
                              className="p-1.5 rounded-full text-muted-foreground hover:text-foreground hover:bg-secondary transition-colors" title="Vis">
                              <Eye className="w-4 h-4" />
                            </button>
                          )}
                          <button onClick={() => downloadDoc(nuKontrakt.doc_id, nuKontrakt.filename)}
                            className="p-1.5 rounded-full text-muted-foreground hover:text-foreground hover:bg-secondary transition-colors" title="Download">
                            <Download className="w-4 h-4" />
                          </button>
                          <button onClick={() => handleDocDelete(nuKontrakt.doc_id, nuKontrakt.filename)}
                            className="p-1.5 rounded-full text-muted-foreground hover:text-red-500 hover:bg-red-500/10 opacity-0 group-hover:opacity-100 transition-all">
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      );
                    }
                    if (kontrakter.filter(k => k.er_aktiv).length === 0) {
                      return <p className="text-sm text-muted-foreground italic mb-3">Ingen nuværende kontrakt.</p>;
                    }
                    return null;
                  })()}
                  <input ref={kontraktInputRef} type="file" className="hidden" accept=".pdf" onChange={handleKontraktUpload} />
                  <button onClick={() => kontraktInputRef.current?.click()} disabled={uploadingKontrakt}
                    className="flex items-center gap-1.5 text-sm text-[#FF2A6D] hover:text-[#e02560] transition-colors disabled:opacity-50 mt-2">
                    {uploadingKontrakt ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
                    {uploadingKontrakt ? "Uploader..." : "Upload ny kontrakt (PDF)"}
                  </button>
                </div>

                {/* Historik-dropdown — tidligere subtab, nu udfoldelig sektion. */}
                <div className="rounded-xl border border-border overflow-hidden">
                  <button
                    type="button"
                    onClick={() => setVisHistorikDropdown(v => !v)}
                    className="w-full flex items-center justify-between p-5 text-left hover:bg-secondary/30 transition-colors">
                    <div className="flex items-center gap-2">
                      <History className="w-4 h-4 text-muted-foreground" />
                      <h3 className="text-[13px] font-bold text-muted-foreground uppercase tracking-wider">
                        Historik (løn, kontrakter & ansættelsesforhold)
                      </h3>
                    </div>
                    {visHistorikDropdown
                      ? <ChevronUp className="w-4 h-4 text-muted-foreground" />
                      : <ChevronDown className="w-4 h-4 text-muted-foreground" />}
                  </button>
                  {visHistorikDropdown && (
                    <div className="border-t border-border p-5 flex flex-col gap-5">
                      <div>
                        <h4 className="text-[13px] font-bold text-muted-foreground uppercase tracking-wider mb-3">Tidligere lønændringer og ansættelsesforhold</h4>
                        <AnsaettelsesHistorikSektion
                          itemId={item_id} token={token}
                          historik={ansaettelseshistorik} onUpdated={load}
                          dokumenter={dokumenter} onOpenDoc={openDoc}
                          ansaettelsestype={val("ansaettelsestype")} />
                      </div>
                      <div>
                        <h4 className="text-[13px] font-bold text-muted-foreground uppercase tracking-wider mb-3">Tidligere kontrakter (Podio)</h4>
                        <KontrakterSektion kontrakter={kontrakter} token={token} onSynced={load} onStatusChanged={load} showAktive={false} />
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {aktivSub === "loenberegner" && (
              <LoenBeregner
                stamdata={stamdata} profil={profil}
                itemId={item_id} token={token} onUpdated={load}
                onLoenChanged={visLoenToast} />
            )}

            {/* #40.1: Feriebank-fane fjernet — ferie justeres fra Arbejdsplan. */}

            {/* Dokumenter — flyttet fra top-fane til subfane under Ansættelse. */}
            {aktivSub === "dokumenter" && (
              <div className="flex flex-col gap-5">
                <div className="rounded-xl border border-border p-5 flex flex-col gap-3">
                  <h3 className="text-[13px] font-bold text-muted-foreground uppercase tracking-wider mb-1">Advarsler & noter</h3>
                  {advarsler.length === 0 && (
                    <p className="text-sm text-muted-foreground italic">Ingen advarsler registreret.</p>
                  )}
                  {advarsler.map((a, i) => (
                    <AdvarselsPost key={i} post={a}
                      onChange={v => setAdvarsler(advarsler.map((p, j) => j === i ? v : p))}
                      onDelete={() => setAdvarsler(advarsler.filter((_, j) => j !== i))} />
                  ))}
                  <EditableField label="Interne noter" value={stamdata.interne_noter} onChange={set("interne_noter")} multiline />
                  <button onClick={() => setAdvarsler([...advarsler, { dato: new Date().toISOString().slice(0, 10), type: "Mundtlig advarsel", tekst: "" }])}
                    className="flex items-center gap-1.5 text-sm text-red-500 hover:text-red-400 transition-colors mt-1">
                    <Plus className="w-4 h-4" /> Tilføj advarsel
                  </button>
                </div>

                <div className="rounded-xl border border-border p-5">
                  <h3 className="text-[13px] font-bold text-muted-foreground uppercase tracking-wider mb-3">Uploadede dokumenter</h3>
                  {dokumenter.filter(d => d.doc_type !== "kontrakt").length === 0 && (
                    <p className="text-sm text-muted-foreground italic mb-3">Ingen dokumenter uploadet endnu.</p>
                  )}
                  <div className="flex flex-col gap-2">
                    {dokumenter.filter(d => d.doc_type !== "kontrakt").map(doc => (
                      <div key={doc.doc_id} className="flex items-center gap-3 rounded-xl border border-border p-3 group bg-secondary/20">
                        <span className="text-xl shrink-0">{fileIcon(doc.content_type)}</span>
                        <div className="flex-1 min-w-0 cursor-pointer" onClick={() => openDoc(doc)}>
                          <p className="text-sm font-medium truncate group-hover:text-[#FF2A6D] transition-colors">{doc.filename}</p>
                          <p className="text-[13px] text-muted-foreground">{fmtBytes(doc.size)} · {fmtTs(doc.uploaded_at)}</p>
                        </div>
                        {isViewable(doc.content_type) && (
                          <button onClick={() => openDoc(doc)}
                            className="p-1.5 rounded-full text-muted-foreground hover:text-foreground hover:bg-secondary transition-colors" title="Vis">
                            <Eye className="w-4 h-4" />
                          </button>
                        )}
                        <button onClick={() => downloadDoc(doc.doc_id, doc.filename)}
                          className="p-1.5 rounded-full text-muted-foreground hover:text-foreground hover:bg-secondary transition-colors" title="Download">
                          <Download className="w-4 h-4" />
                        </button>
                        <button onClick={() => handleDocDelete(doc.doc_id, doc.filename)}
                          className="p-1.5 rounded-full text-muted-foreground hover:text-red-500 hover:bg-red-500/10 opacity-0 group-hover:opacity-100 transition-all">
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                  <input ref={fileInputRef} type="file" className="hidden"
                    accept=".pdf,.doc,.docx,.xls,.xlsx,.csv,.png,.jpg,.jpeg,.gif,.webp" onChange={handleDocUpload} />
                  <button onClick={() => fileInputRef.current?.click()} disabled={uploadingDoc}
                    className="flex items-center gap-1.5 text-sm text-[#FF2A6D] hover:opacity-80 transition-opacity mt-3 disabled:opacity-50">
                    {uploadingDoc ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Plus className="w-4 h-4" />}
                    {uploadingDoc ? "Uploader..." : "Upload dokument"}
                  </button>
                </div>
              </div>
            )}
          </div>
          );
        })()}

        {/* ── CV FANE (#43: Lidt om mig → Erhvervserfaring → Uddannelse → Kurser → Upload dokumenter) ── */}
        {aktivFaneSafe === "cv" && (
          <div className="flex flex-col gap-4">
            {/* Generer CV — kun superadmin (#43). Hook klar; rigtig dialog bygges separat. */}
            {isSuperAdmin && (
              <div className="flex justify-end">
                <button
                  onClick={() => { if (typeof onGenererCv === "function") onGenererCv(); else setShowGenererCv(true); }}
                  className="flex items-center gap-1.5 px-4 py-2 rounded-xl border border-[#FF2A6D] text-[#FF2A6D] text-sm font-semibold hover:bg-[#FF2A6D]/10 transition-colors">
                  <FileText className="w-4 h-4" /> Generer CV
                </button>
              </div>
            )}

            {/* 1) Lidt om mig (#43) — fritekst, gemmes i omkring_mig */}
            <div className="rounded-xl border border-border p-5">
              <CvUndersektion titel="Lidt om mig" color="#fbbf24">
                {omkringEditing ? (
                  <OmkringMigEditor
                    initial={omkringMig}
                    saving={cvSaving}
                    onCancel={() => setOmkringEditing(false)}
                    onSave={async (tekst) => {
                      const ok = await saveCvData({ omkring_mig: tekst });
                      if (ok) setOmkringEditing(false);
                    }} />
                ) : (omkringMig || "").trim() ? (
                  <div className="rounded-xl border border-border p-4 bg-secondary/20">
                    <p className="text-sm text-foreground/90 whitespace-pre-wrap">{omkringMig}</p>
                    <div className="flex items-center gap-3 mt-2">
                      <button type="button" onClick={() => setOmkringEditing(true)}
                        className="flex items-center gap-1 text-[13px] text-muted-foreground hover:text-[#FF2A6D] transition-colors">
                        <Edit3 className="w-3.5 h-3.5" /> Rediger
                      </button>
                    </div>
                  </div>
                ) : (
                  <button onClick={() => setOmkringEditing(true)}
                    className="flex items-center gap-1.5 text-sm text-[#fbbf24] hover:opacity-80 transition-opacity mt-1">
                    <Plus className="w-4 h-4" /> Tilføj
                  </button>
                )}
              </CvUndersektion>
            </div>

            {/* 2) Erhvervserfaring (#43: gem-pr-post, nyeste øverst efter Til-dato) */}
            <div className="rounded-xl border border-border p-5">
              <CvUndersektion titel="Erhvervserfaring" badge={cvPoster.length} color="#60a5fa">
                <div className="flex flex-col gap-3">
                  {cvPoster.length === 0 && cvEditing !== "new" && (
                    <p className="text-sm text-muted-foreground italic">Ingen poster endnu.</p>
                  )}
                  {sorterEfterTil(cvPoster).map(({ post, _origIndex }) => (
                    <CvPost key={_origIndex} post={post}
                      mode={cvEditing === _origIndex ? "edit" : "read"}
                      saving={cvSaving}
                      onEdit={() => setCvEditing(_origIndex)}
                      onCancel={() => setCvEditing(null)}
                      onSave={async (v) => {
                        const ny = cvPoster.map((p, j) => j === _origIndex ? v : p);
                        const ok = await saveCvData({ cv_poster: ny });
                        if (ok) setCvEditing(null);
                      }}
                      onDelete={async () => {
                        const ny = cvPoster.filter((_, j) => j !== _origIndex);
                        await saveCvData({ cv_poster: ny });
                        setCvEditing(null);
                      }} />
                  ))}
                  {cvEditing === "new" && (
                    <CvPost post={{ fra: "", til: "", stilling: "", firma: "", beskrivelse: "" }}
                      mode="edit" saving={cvSaving}
                      onCancel={() => setCvEditing(null)}
                      onSave={async (v) => {
                        const ok = await saveCvData({ cv_poster: [...cvPoster, v] });
                        if (ok) setCvEditing(null);
                      }} />
                  )}
                  {cvEditing !== "new" && (
                    <button onClick={() => setCvEditing("new")}
                      className="flex items-center gap-1.5 text-sm text-[#60a5fa] hover:text-blue-400 transition-colors mt-1">
                      <Plus className="w-4 h-4" /> Tilføj erhvervserfaring
                    </button>
                  )}
                </div>
              </CvUndersektion>
            </div>

            {/* 3) Uddannelse (#43: gem-pr-post, nyeste øverst efter Til-dato) */}
            <div className="rounded-xl border border-border p-5">
              <CvUndersektion titel="Uddannelse" badge={uddannelse.length} color="#34d399">
                <div className="flex flex-col gap-3">
                  {uddannelse.length === 0 && uddEditing !== "new" && (
                    <p className="text-sm text-muted-foreground italic">Ingen uddannelse endnu.</p>
                  )}
                  {sorterEfterTil(uddannelse).map(({ post, _origIndex }) => (
                    <UddannelsePost key={_origIndex} post={post}
                      mode={uddEditing === _origIndex ? "edit" : "read"}
                      saving={cvSaving}
                      onEdit={() => setUddEditing(_origIndex)}
                      onCancel={() => setUddEditing(null)}
                      onSave={async (v) => {
                        const ny = uddannelse.map((p, j) => j === _origIndex ? v : p);
                        const ok = await saveCvData({ uddannelse: ny });
                        if (ok) setUddEditing(null);
                      }}
                      onDelete={async () => {
                        const ny = uddannelse.filter((_, j) => j !== _origIndex);
                        await saveCvData({ uddannelse: ny });
                        setUddEditing(null);
                      }} />
                  ))}
                  {uddEditing === "new" && (
                    <UddannelsePost post={{ fra: "", til: "", titel: "", skole: "", beskrivelse: "" }}
                      mode="edit" saving={cvSaving}
                      onCancel={() => setUddEditing(null)}
                      onSave={async (v) => {
                        const ok = await saveCvData({ uddannelse: [...uddannelse, v] });
                        if (ok) setUddEditing(null);
                      }} />
                  )}
                  {uddEditing !== "new" && (
                    <button onClick={() => setUddEditing("new")}
                      className="flex items-center gap-1.5 text-sm text-[#34d399] hover:text-emerald-400 transition-colors mt-1">
                      <Plus className="w-4 h-4" /> Tilføj uddannelse
                    </button>
                  )}
                </div>
              </CvUndersektion>
            </div>

            {/* 4) Kurser (#43: gem-pr-post, nyeste øverst efter aar) */}
            <div className="rounded-xl border border-border p-5">
              <CvUndersektion titel="Kurser" badge={kurser.length} color="#a78bfa">
                <div className="flex flex-col gap-3">
                  {kurser.length === 0 && kurEditing !== "new" && (
                    <p className="text-sm text-muted-foreground italic">Ingen kurser endnu.</p>
                  )}
                  {sorterEfterAar(kurser).map(({ post, _origIndex }) => (
                    <KursusPost key={_origIndex} post={post}
                      mode={kurEditing === _origIndex ? "edit" : "read"}
                      saving={cvSaving}
                      onEdit={() => setKurEditing(_origIndex)}
                      onCancel={() => setKurEditing(null)}
                      onSave={async (v) => {
                        const ny = kurser.map((p, j) => j === _origIndex ? v : p);
                        const ok = await saveCvData({ kurser: ny });
                        if (ok) setKurEditing(null);
                      }}
                      onDelete={async () => {
                        const ny = kurser.filter((_, j) => j !== _origIndex);
                        await saveCvData({ kurser: ny });
                        setKurEditing(null);
                      }} />
                  ))}
                  {kurEditing === "new" && (
                    <KursusPost post={{ aar: "", navn: "", skole: "", beskrivelse: "" }}
                      mode="edit" saving={cvSaving}
                      onCancel={() => setKurEditing(null)}
                      onSave={async (v) => {
                        const ok = await saveCvData({ kurser: [...kurser, v] });
                        if (ok) setKurEditing(null);
                      }} />
                  )}
                  {kurEditing !== "new" && (
                    <button onClick={() => setKurEditing("new")}
                      className="flex items-center gap-1.5 text-sm text-[#a78bfa] hover:text-purple-400 transition-colors mt-1">
                      <Plus className="w-4 h-4" /> Tilføj kursus
                    </button>
                  )}
                </div>
              </CvUndersektion>
            </div>

            {/* 5) Upload dokumenter (#43) — kun for brugere med dokument-adgang (admin/super/fortrolig) */}
            {kanSeFortrolige && (
            <div className="rounded-xl border border-border p-5">
              <h3 className="text-[13px] font-bold text-muted-foreground uppercase tracking-wider mb-1">Upload dokumenter</h3>
              <p className="text-sm text-muted-foreground mb-3">Her kan du uploade udtalelser, beviser, certifikater og andet hvis du har lyst.</p>
              {dokumenter.filter(d => d.doc_type !== "kontrakt").length === 0 && (
                <p className="text-sm text-muted-foreground italic mb-3">Ingen dokumenter uploadet endnu.</p>
              )}
              {dokumenter.filter(d => d.doc_type !== "kontrakt").map(doc => (
                <div key={doc.doc_id} className="flex items-center gap-3 rounded-xl border border-border p-3 group bg-secondary/20 mb-2">
                  <span className="text-xl shrink-0">{fileIcon(doc.content_type)}</span>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium truncate">{doc.filename}</p>
                    <p className="text-[13px] text-muted-foreground">{fmtBytes(doc.size)} · {fmtTs(doc.uploaded_at)}</p>
                  </div>
                  <button onClick={() => downloadDoc(doc.doc_id, doc.filename)}
                    className="p-1.5 rounded-full text-muted-foreground hover:text-foreground hover:bg-secondary transition-colors">
                    <Download className="w-4 h-4" />
                  </button>
                  <button onClick={() => handleDocDelete(doc.doc_id, doc.filename)}
                    className="p-1.5 rounded-full text-muted-foreground hover:text-red-500 hover:bg-red-500/10 opacity-0 group-hover:opacity-100 transition-all">
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              ))}
              <input ref={cvDocInputRef} type="file" className="hidden"
                accept=".pdf,.doc,.docx,.xls,.xlsx,.csv,.png,.jpg,.jpeg,.gif,.webp" onChange={handleDocUpload} />
              <button onClick={() => cvDocInputRef.current?.click()} disabled={uploadingDoc}
                className="flex items-center gap-1.5 text-sm text-[#FF2A6D] hover:opacity-80 transition-opacity disabled:opacity-50 mt-1">
                {uploadingDoc ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Plus className="w-4 h-4" />}
                Upload dokument
              </button>
            </div>
            )}
          </div>
        )}

        {/* #43: Generer CV — dialog med LLM-forfinet preview + PDF/Word-download. */}
        {showGenererCv && (
          <GenererCvDialog itemId={item_id} token={token} navn={profil?.name || ""}
            onClose={() => setShowGenererCv(false)} />
        )}

        {/* #61 DEL B: Ændre kode-dialog — kun SuperAdmin. */}
        {showAendreKode && (
          <AendreKodeDialog itemId={item_id} token={token} navn={profil?.name || ""}
            onClose={() => setShowAendreKode(false)} />
        )}

        {/* ── LOG FANE ─────────────────────────────────────── */}
        {aktivFaneSafe === "log" && <LogFane log={log} />}

        {/* Gem-knap (stamdata/ansættelse). CV har egen Gem-knap; Arbejdsplan/Log ingen.
            #44.6: skjul for medarbejder på eget stamkort (Stamdata = kun se). */}
        {((aktivFaneSafe === "stamdata" && kanRedigereStamdata) ||
          (aktivFaneSafe === "ansaettelse" && ansaettelseSubTab !== "loenberegner")) && (
          <div className="flex justify-end mt-6">
            <button onClick={handleSave} disabled={saving}
              className="flex items-center gap-1.5 px-6 py-2.5 rounded-xl bg-[#FF2A6D] text-white text-sm font-semibold hover:bg-[#e02560] transition-colors disabled:opacity-60">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
              {saved ? "Gemt!" : "Gem ændringer"}
            </button>
          </div>
        )}

      </div>

      {/* Dokument-viewer modal */}
      {viewingDoc && (
        <div className="fixed inset-0 z-50 flex flex-col bg-black/80 backdrop-blur-sm" onClick={closeDoc}>
          <div className="flex items-center justify-between px-4 py-3 bg-background/90 border-b border-border shrink-0" onClick={e => e.stopPropagation()}>
            <span className="text-sm font-medium truncate max-w-[60vw]">{viewingDoc.filename}</span>
            <div className="flex items-center gap-2">
              <button onClick={() => downloadDoc(viewingDoc.doc_id, viewingDoc.filename)}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[13px] font-medium bg-secondary hover:bg-secondary/80 transition-colors">
                <Download className="w-3.5 h-3.5" /> Download
              </button>
              <button onClick={closeDoc} className="p-1.5 rounded-lg hover:bg-secondary transition-colors">
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>
          <div className="flex-1 overflow-auto p-4" onClick={e => e.stopPropagation()}>
            {viewingDoc.content_type.includes("image") ? (
              <img src={viewingDoc.blobUrl} alt={viewingDoc.filename}
                className="max-w-full max-h-full mx-auto rounded-xl shadow-2xl object-contain" />
            ) : (
              <iframe src={viewingDoc.blobUrl} title={viewingDoc.filename}
                className="w-full h-full rounded-xl border border-border bg-white min-h-[80vh]" />
            )}
          </div>
        </div>
      )}

      {/* Fortryd-toast for lønændring — vises i 30 sek efter ny løn er gemt. */}
      {loenToast && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 flex items-center gap-3 px-5 py-3 rounded-xl bg-foreground text-background shadow-2xl border border-border max-w-[90vw]">
          <Check className="w-4 h-4 shrink-0" />
          <span className="text-sm">
            Løn ændret fra <span className="font-semibold">{Number(loenToast.gammelLoen || 0).toLocaleString("da-DK")}</span>
            {" "}til <span className="font-semibold">{Number(loenToast.nyLoen || 0).toLocaleString("da-DK")} kr.</span>
          </span>
          <button
            onClick={fortrydLoenAendring}
            className="ml-2 px-3 py-1 rounded-lg text-[13px] font-semibold bg-background text-foreground hover:bg-background/80 transition-colors">
            Fortryd
          </button>
          <button onClick={() => setLoenToast(null)} className="p-1 rounded hover:bg-background/20 transition-colors" title="Luk">
            <X className="w-3.5 h-3.5" />
          </button>
        </div>
      )}
    </div>
  );
}
